import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent, CartDrawerPromotionsLoadingEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import '@theme/gift-with-purchase-options';
import {
  getApplicableDiscountCodes,
  getOtherApplicableDiscountCodes,
  getStrictlyApplicableDiscountCodes,
  isDiscountStrictlyApplicable,
  normalizeDiscountCode,
  updateCartDiscountCodes,
} from '@theme/cart-discount-utils';
import {
  getLineItemPidFromProperties,
  getPersonalizationEntryRawFromAttributesWithLegacy,
  parsePersonalizationEntryPayload,
  patchLineItemPersonalizationMapOnCart,
  pruneOrphanedLineItemPersonalizationFromCart,
  resolvePersonalizationTextFromEntryRaw,
  SKIP_PERSONALIZATION_PROPERTY_KEY,
  withSkipPersonalizationAttribute,
} from '@theme/line-item-personalization-cart';

const SYNC_SOURCE = 'gift-with-purchase';
const MANUAL_ADD_SOURCE = 'gwp-manual-add';
const DISMISSED_STORAGE_KEY = 'gwp-dismissed-ids';
const PENDING_COUPON_STORAGE_KEY = 'gwp-pending-coupon';
const GWP_LINE_KEYS_STORAGE_KEY = 'gwp-line-keys';
const GWP_SLOT_LINE_PROPERTY = '_gwp-slot';
const GWP_GIFT_WRAP_SLOT_PROPERTY = '_gift_wrap_gwp_slot';
const GIFT_WRAP_ADDON_PROPERTY = '_gift_wrap_addon';
const GWP_PERSONALIZATION_OPT_OUT_PROPERTY = '_gwp-personalization-opt-out';
const RUSH_ORDER_FEE_PROPERTY = '_rush_order_fee';
const ACCEPT_GWP_RUSH_VALUE = 'acceptGWPRushProduct';
const DIALOG_OPEN_EVENT = 'dialog:open';
const SECTION_HYDRATED_EVENT = 'theme:section-hydrated';
const GWP_GATE_CLAIM_EVENT = 'gwp:checkout-gate:claim';
const GWP_GATE_RELEASE_EVENT = 'gwp:checkout-gate:release';
const PROGRESS_MAX_GAP_CENTS = 2499;
const GWP_GATED_CHECKOUT_DEFAULT_ERROR =
  'Please complete or decline your free gift before checking out.';
const PERSONALIZATION_STATIC_PROPERTY_KEYS = new Set([
  'Design',
  'Thread Color',
  '_swatch-color',
  '_selected-id',
  '_selected-color',
]);
const GWP_DESIGN_INPUT_DATA_ATTRIBUTE_NAMES = [
  'data-design-id',
  'data-design-name',
  'data-design-font',
  'data-design-letter-type',
  'data-design-letter-type-raw',
  'data-design-thumbnail',
  'data-validation-regex',
  'data-validation-min',
  'data-validation-max',
  'data-validation-allow-numbers',
  'data-validation-allow-uppercase',
  'data-validation-allow-lowercase',
  'data-validation-special-characters',
];
const GWP_THREAD_COLOR_INPUT_ATTRIBUTE_NAMES = [
  'value',
  'data-thread-color-key',
  'data-thread-color-label',
  'data-thread-color-id',
  'data-thread-color-hex',
];

/**
 * @typedef {Object} GwpVariantOption
 * @property {number} variantId
 * @property {string} optionValue
 * @property {boolean} [available]
 */

/**
 * @typedef {Object} GwpProduct
 * @property {number} productId
 * @property {number} variantId
 * @property {string} [variantTitle]
 * @property {string} [productTitle]
 * @property {string} [productUrl]
 * @property {string} [imageUrl]
 * @property {boolean} isPersonalizable
 * @property {unknown} [mtoOptions]
 * @property {string} [optionName]
 * @property {string} [variantFieldsetHtml]
 * @property {number[]} [allowedVariantIds]
 * @property {GwpVariantOption[]} [variants]
 */

/**
 * @typedef {Object} GwpConfig
 * @property {string} id
 * @property {string | null} [appDiscountId]
 * @property {number | null} [maxDiscountQuantity]
 * @property {"free" | "percentage"} [discountType]
 * @property {Array<{ product?: GwpProduct } & Partial<GwpProduct>>} products - Gift products for this slot. Entries may wrap the product under a `product` key.
 * @property {number} progressVariant - Representative variant used for the progress prompt.
 * @property {string} progressHeading
 * @property {string} discountTitle
 * @property {'coupon' | 'subtotal'} trigger
 * @property {string} [couponCode]
 * @property {number} [subtotalThresholdCents]
 * @property {number} [couponThresholdCents]
 * @property {string} [couponThresholdErrorMessage]
 * @property {string} [offerHeading] - Per-gift offer heading (subtotal and coupon offer-box gifts)
 * @property {boolean} [showOfferBox] - Per-gift toggle for the re-add offer box (subtotal gifts)
 * @property {boolean} [offerBoxOnly] - Gift uses the offer box instead of auto-add (subtotal or coupon)
 */

/**
 * @typedef {Object} PendingCouponOffer
 * @property {string} code
 * @property {string[]} configIds
 * @property {string[]} [validatedRushConfigIds]
 * @property {string} cartToken
 * @property {number} createdAt
 */

/**
 * @typedef {Object} GwpFreeRushConfig
 * @property {number} variantId
 * @property {string} label
 * @property {string} [imageUrl]
 */

/**
 * @typedef {'gift' | 'rush'} GwpOfferChoice
 */

class GiftWithPurchaseController {
  /** @type {GwpConfig[]} */
  #config = [];

  /** @type {GwpFreeRushConfig | null} */
  #freeRushOrder = null;

  /**
   * Customer-facing label overrides for design letter types, keyed by the
   * lowercased source value (e.g. "monogram" -> "Initials").
   * @type {Map<string, string>}
   */
  #letterTypeOverrides = new Map();

  /** @type {boolean} */
  #syncing = false;

  /** @type {boolean} */
  #preparingDiscountCode = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {boolean | null} */
  #lastKnownGatedAcceleratedCheckoutBlocked = null;

  /** @type {Set<string>} */
  #lastKnownGwpIds = new Set();

  /** @type {Map<string, Set<string>>} */
  #lastKnownGwpLineKeys = new Map();

  /** @type {boolean} */
  #offersClickBound = false;

  /** @type {Set<string>} */
  #pendingCouponCodes = new Set();

  /** @type {Set<string>} */
  #pendingRejectConfigIds = new Set();

  /** @type {Map<string, string>} */
  #offerConflictMessages = new Map();

  /** Offer-box promos the shopper explicitly accepted (survives DOM re-renders). */
  /** @type {Set<string>} */
  #confirmedOfferAcceptIds = new Set();

  /** Unfinished gift edits must survive cart-section replacement. */
  /** @type {Map<string, { productId: number, variantId: number, text: string, optedOut: boolean }>} */
  #pendingOfferEdits = new Map();

  /** Rush selections waiting for an in-flight cart mutation to finish. */
  /** @type {Set<string>} */
  #pendingRushOfferIds = new Set();

  /** @type {string | null} */
  #lastDiscountConflictMessage = null;

  /** @type {Object | null} */
  #lastDiscountConflictCart = null;

  /** @type {Map<string, { priorDiscounts: string[], protectedAutoAddGwpIds: string[] }>} */
  #pendingDiscountApplySnapshots = new Map();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #offerInputDebounceTimer = null;

  /** @type {string} */
  #lastGwpProductSelectionKey = '';

  /** @type {number} */
  #lastGwpProductSelectionAt = 0;

  init() {
    if (this.#initialized) return;

    const configElement = document.getElementById('gift-with-purchase-config');
    if (!(configElement instanceof HTMLScriptElement)) {
      this.#releaseGatedCheckoutAfterInitializationFailure();
      return;
    }

    try {
      const parsed = JSON.parse(configElement.textContent || '[]');
      if (!Array.isArray(parsed) || parsed.length === 0) {
        this.#releaseGatedCheckoutAfterInitializationFailure();
        return;
      }
      this.#config = parsed.filter((entry) => this.#isValidConfig(entry));

      if (this.#config.length === 0) {
        this.#releaseGatedCheckoutAfterInitializationFailure();
        return;
      }

      this.#freeRushOrder = this.#loadFreeRushConfig();
      this.#letterTypeOverrides = this.#loadLetterTypeOverrides();
      this.currencySymbol = window.SDG?.Data?.currencySymbol || '';
      this.#bindOffersClick();
      document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
      document.addEventListener(ThemeEvents.discountUpdate, this.#onCartChange);
      document.addEventListener(DIALOG_OPEN_EVENT, this.#onCartDrawerOpen);
      document.addEventListener(SECTION_HYDRATED_EVENT, this.#onSectionHydrated);
      this.#initialized = true;

      if (this.#isGatedCheckoutEnabled()) {
        this.#setGatedCheckoutInitializing(true);
        this.#setGatedAcceleratedCheckoutBlocked(true);
      }

      document.dispatchEvent(new Event(GWP_GATE_CLAIM_EVENT));
      void this.#sync();
    } catch (error) {
      this.#releaseGatedCheckoutAfterInitializationFailure();
      console.error('Gift with purchase: failed to initialize', error);
    }
  }

  /**
   * Waits for any in-flight offer update, then verifies that every qualified
   * interactive GWP has been added, replaced with free rush, or declined.
   * @returns {Promise<boolean>}
   */
  async prepareForCheckout() {
    if (!this.#isGatedCheckoutEnabled()) return true;
    if (!this.#initialized) this.init();
    if (!this.#initialized) return true;

    try {
      this.#clearGatedCheckoutErrors();
      await this.#waitForPendingOfferUpdates();

      const cart = await this.#fetchCart();
      this.#updateGwpUI(cart);

      const unresolvedConfigs = this.#getUnresolvedGatedCheckoutConfigs(cart);
      this.#rememberGatedAcceleratedCheckoutBlocked(unresolvedConfigs.length > 0);

      if (unresolvedConfigs.length === 0) return true;

      this.#renderGatedCheckoutErrors(unresolvedConfigs);
      this.#focusFirstUnresolvedGwp(unresolvedConfigs[0]);
      return false;
    } catch (error) {
      // Preserve the last successfully evaluated gate state. Before the first
      // successful evaluation, the null state intentionally fails open.
      this.#restoreGatedCheckoutAfterFailure();
      throw error;
    }
  }

  #isGatedCheckoutEnabled() {
    return window.SDG?.Data?.gwpGatedCheckoutEnabled === true;
  }

  #getGatedCheckoutErrorMessage() {
    const configuredMessage = window.SDG?.Data?.gwpGatedCheckoutErrorMessage;
    return typeof configuredMessage === 'string' && configuredMessage.trim()
      ? configuredMessage.trim()
      : GWP_GATED_CHECKOUT_DEFAULT_ERROR;
  }

  /**
   * Lets personalization debounce and cart mutations settle before checkout is
   * evaluated. This prevents a fast checkout click from racing a valid gift add.
   * @returns {Promise<void>}
   */
  async #waitForPendingOfferUpdates() {
    const timeoutAt = Date.now() + 5000;

    while ((this.#syncing || this.#offerInputDebounceTimer) && Date.now() < timeoutAt) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  /**
   * @param {Object} cart
   * @returns {GwpConfig[]}
   */
  #getUnresolvedGatedCheckoutConfigs(cart) {
    if (!this.#isGatedCheckoutEnabled()) return [];

    return this.#config.filter((config) => {
      if (!this.#isInteractiveOffer(config)) return false;
      if (!this.#isGiftQualified(cart, config)) return false;
      if (!this.#shouldSurfaceGwpOffer(config, cart)) return false;

      if (this.#pendingRushOfferIds.has(config.id)) return true;

      const selection = this.#resolveOfferSelection(cart, config);
      const offerItems = Array.from(
        document.querySelectorAll(`[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`)
      );
      const hasPendingEdit = offerItems.some(
        (item) => item instanceof HTMLElement && this.#isOfferEditPending(item, config, selection)
      ) || (offerItems.length === 0 && this.#pendingOfferEdits.has(config.id));
      if (hasPendingEdit) return true;
      if (this.#isDismissed(config.id)) return false;
      if (!selection) return true;
      if (selection.choice === 'rush') return false;

      const product = this.#getConfigProduct(config, selection.variantId);
      if (!this.#resolveGwpPersonalizableDesign(product)) return false;

      return !selection.optedOut && !selection.text.trim();
    });
  }

  /**
   * An unfinished edit must not be satisfied by the previous gift still in cart.
   * Keep it pending until the current form is valid and matches the saved gift.
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {{ choice: GwpOfferChoice, variantId: number, text: string, optedOut: boolean } | null} selection
   * @returns {boolean}
   */
  #isOfferEditPending(item, config, selection) {
    // A removal or rejection is authoritative even when the existing offer DOM
    // still carries an edit marker from before the cart update.
    if (this.#isDismissed(config.id)) {
      delete item.dataset.gwpOfferEditing;
      return false;
    }

    this.#restorePendingOfferEdit(item, config);
    if (item.dataset.gwpOfferEditing !== 'true' || !this.#isOfferGiftAccepted(item)) return false;

    const context = this.#buildOfferContext(item, config);
    if (!context || !this.#isOfferVariantSelectionComplete(context)) return true;
    if (selection?.choice !== 'gift' || selection.variantId !== context.selectedVariantId) return true;

    const product = this.#getConfigProductById(config, context.selectedProductId);
    if (!this.#resolveGwpPersonalizableDesign(product)) return false;

    const optedOut = this.#isGwpOfferPersonalizationOptedOut(item);
    if (optedOut !== selection.optedOut) return true;
    if (optedOut) return false;

    const input = item.querySelector('[data-personalization-text-input]');
    const text = input instanceof HTMLInputElement ? input.value.trim() : '';
    return !text || text !== selection.text.trim() || !this.#isGwpOfferPersonalizationValid(context);
  }

  /** @param {HTMLElement} item */
  #markOfferEditing(item) {
    if (!this.#isOfferGiftAccepted(item)) return;
    item.dataset.gwpOfferEditing = 'true';
    const config = this.#config.find((entry) => entry.id === item.dataset.gwpId);
    if (config) {
      // A new gift interaction supersedes a prior decline before validation or sync.
      this.#clearDismissal(config.id);
      this.#pendingRejectConfigIds.delete(config.id);
      this.#pendingRushOfferIds.delete(config.id);
    }
    const context = config && this.#buildOfferContext(item, config);
    if (context) {
      const input = item.querySelector('[data-personalization-text-input]');
      this.#pendingOfferEdits.set(config.id, {
        productId: context.selectedProductId,
        variantId: context.selectedVariantId,
        text: input instanceof HTMLInputElement ? input.value : '',
        optedOut: this.#isGwpOfferPersonalizationOptedOut(item),
      });
    }
    if (this.#isGatedCheckoutEnabled()) {
      this.#rememberGatedAcceleratedCheckoutBlocked(true);
    }
  }

  /** Restore a draft when cart rendering has replaced the edited offer element. */
  #restorePendingOfferEdit(item, config) {
    const draft = this.#pendingOfferEdits.get(config.id);
    if (!draft || item.dataset.gwpOfferEditing === 'true') return;

    item.dataset.gwpOfferEditing = 'true';
    item.dataset.gwpOfferAccepted = 'true';
    for (const value of ['acceptGWPGProduct', 'rejectGWPProduct', ACCEPT_GWP_RUSH_VALUE]) {
      const choice = item.querySelector(`input[value="${value}"]`);
      if (choice instanceof HTMLInputElement) choice.checked = value === 'acceptGWPGProduct';
    }
    const productInput = item.querySelector(
      `input[name="gwpProduct-${CSS.escape(config.id)}"][value="${draft.productId}"]`
    );
    if (productInput instanceof HTMLInputElement) productInput.checked = true;
    const product = this.#getConfigProductById(config, draft.productId);
    if (product) this.#applyOfferVariantFieldsetState(item, product, draft, false);
    this.#syncOfferChoiceVisibility(item, 'gift');

    const input = item.querySelector('[data-personalization-text-input]');
    const optOut = item.querySelector('[data-personalize-opt-out-toggle]');
    if (input instanceof HTMLInputElement) input.value = draft.text;
    if (optOut instanceof HTMLInputElement) optOut.checked = draft.optedOut;
    this.#applyGwpOfferPersonalizationUiState(item, config, draft);

    const personalization = item.querySelector('personalization-component[data-gwp-personalization]');
    personalization?.validatePersonalizationInput?.();
    personalization?._queuePreviewRefresh?.();
  }

  /**
   * Waits until the current offer mutation releases its lock, unless the shopper
   * chooses something other than rush in the meantime.
   * @param {string} configId
   */
  async #waitForPendingRushUpdate(configId) {
    while (this.#syncing && this.#pendingRushOfferIds.has(configId)) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  #clearGatedCheckoutErrors() {
    document.querySelectorAll('[data-gwp-checkout-error]').forEach((error) => {
      if (!(error instanceof HTMLElement)) return;
      error.hidden = true;
      error.textContent = '';
    });
  }

  /**
   * @param {GwpConfig[]} configs
   */
  #renderGatedCheckoutErrors(configs) {
    const message = this.#getGatedCheckoutErrorMessage();

    for (const config of configs) {
      document
        .querySelectorAll(`[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`)
        .forEach((item) => {
          if (!(item instanceof HTMLElement)) return;

          let error = item.querySelector('[data-gwp-checkout-error]');
          if (!(error instanceof HTMLElement)) {
            error = document.createElement('p');
            error.className = 'gift-with-purchase-offers__checkout-error';
            error.dataset.gwpCheckoutError = '';
            error.setAttribute('role', 'alert');

            const fieldset = item.querySelector('.gift-with-purchase-offers__add-gwp');
            if (fieldset instanceof HTMLElement) {
              fieldset.insertAdjacentElement('afterend', error);
            } else {
              item.prepend(error);
            }
          }

          error.hidden = false;
          error.textContent = message;
        });
    }
  }

  /**
   * @param {GwpConfig} config
   */
  #focusFirstUnresolvedGwp(config) {
    const items = Array.from(
      document.querySelectorAll(`[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`)
    ).filter((item) => item instanceof HTMLElement);
    const item = items.find((candidate) => candidate.getClientRects().length > 0) || items[0];
    if (!(item instanceof HTMLElement)) return;

    const accept = item.querySelector('input[value="acceptGWPGProduct"]');
    const personalizationInput = item.querySelector(
      'input[data-personalization-text-input]:not(:disabled)'
    );
    const firstChoice = item.querySelector(
      '.gift-with-purchase-offers__add-gwp input:not(:disabled)'
    );
    const focusTarget =
      accept instanceof HTMLInputElement && accept.checked && personalizationInput instanceof HTMLElement
        ? personalizationInput
        : firstChoice;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  /**
   * Brings a newly surfaced coupon offer box into view. The discount field sits
   * below the cart items, so the offer box that renders above them is otherwise
   * easy to miss. The drawer jumps to its top; the cart page scrolls only when the
   * offer box is out of view. Focus moves to the offer's choice so the shopper can
   * act on it (and the mobile keyboard from the discount field closes).
   * @param {GwpConfig[]} configs
   */
  #revealCouponOffer(configs) {
    const selector = configs
      .map((config) => `[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`)
      .join(',');
    if (!selector) return;

    const item = Array.from(document.querySelectorAll(selector)).find(
      (candidate) => candidate instanceof HTMLElement && candidate.getClientRects().length > 0
    );
    if (!(item instanceof HTMLElement)) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const drawerContent = item.closest('.cart-drawer__content');

    if (drawerContent instanceof HTMLElement) {
      drawerContent.scrollTo({ top: 0, behavior });
    } else {
      const headerHeight =
        parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const { top } = item.getBoundingClientRect();

      if (top < headerHeight || top >= window.innerHeight) {
        window.scrollTo({ top: top + window.scrollY - headerHeight, behavior });
      }
    }

    const focusTarget =
      item.querySelector('.gift-with-purchase-offers__add-gwp input:checked:not(:disabled)') ||
      item.querySelector('.gift-with-purchase-offers__add-gwp input:not(:disabled)');
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  /**
   * Temporarily disables standard checkout only while the initial GWP cart
   * evaluation is in progress. Buttons that were already disabled are left
   * untouched so an empty cart cannot be accidentally enabled.
   * @param {boolean} initializing
   */
  #setGatedCheckoutInitializing(initializing) {
    document.querySelectorAll('.js-checkout-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (initializing) {
        if (button.disabled) return;
        button.disabled = true;
        button.dataset.gwpCheckoutInitializing = '';
        button.setAttribute('aria-busy', 'true');
        return;
      }

      if (!button.hasAttribute('data-gwp-checkout-initializing')) return;
      button.disabled = false;
      button.removeAttribute('data-gwp-checkout-initializing');
      button.removeAttribute('aria-busy');
    });
  }

  #restoreGatedCheckoutAfterFailure() {
    this.#setGatedCheckoutInitializing(false);
    this.#setGatedAcceleratedCheckoutBlocked(
      this.#lastKnownGatedAcceleratedCheckoutBlocked ?? false
    );
  }

  #releaseGatedCheckoutAfterInitializationFailure() {
    this.#initialized = false;
    this.#lastKnownGatedAcceleratedCheckoutBlocked = null;
    this.#setGatedCheckoutInitializing(false);
    this.#setGatedAcceleratedCheckoutBlocked(false);
    document.dispatchEvent(new Event(GWP_GATE_RELEASE_EVENT));
  }

  /**
   * @param {boolean} blocked
   */
  #setGatedAcceleratedCheckoutBlocked(blocked) {
    document.querySelectorAll('[data-gwp-gated-accelerated-checkout]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      container.hidden = false;
      container.removeAttribute('aria-hidden');
      container.inert = blocked;
      container.toggleAttribute('data-gwp-gated-checkout-blocked', blocked);

      if (blocked) {
        container.setAttribute('aria-disabled', 'true');
      } else {
        container.removeAttribute('aria-disabled');
      }
    });
  }

  /**
   * Records cart validation or an explicit pending edit. The provisional
   * initialization lock must not replace the last evaluated gate state.
   * @param {boolean} blocked
   */
  #rememberGatedAcceleratedCheckoutBlocked(blocked) {
    this.#lastKnownGatedAcceleratedCheckoutBlocked = blocked;
    this.#setGatedAcceleratedCheckoutBlocked(blocked);
  }

  /**
   * @param {Object} cart
   */
  #syncGatedAcceleratedCheckout(cart) {
    if (!this.#isGatedCheckoutEnabled()) {
      this.#rememberGatedAcceleratedCheckoutBlocked(false);
      return;
    }
    const unresolvedConfigIds = new Set(
      this.#getUnresolvedGatedCheckoutConfigs(cart).map((config) => config.id)
    );

    this.#rememberGatedAcceleratedCheckoutBlocked(unresolvedConfigIds.size > 0);

    document.querySelectorAll('[data-gwp-checkout-error]').forEach((error) => {
      if (!(error instanceof HTMLElement)) return;
      const item = error.closest('[data-gwp-offer-item]');
      if (!(item instanceof HTMLElement)) return;
      if (unresolvedConfigIds.has(item.dataset.gwpId || '')) return;
      error.hidden = true;
      error.textContent = '';
    });
  }

  /**
   * @param {unknown} entry
   * @returns {entry is GwpConfig}
   */
  #isValidConfig(entry) {
    if (!entry || typeof entry !== 'object') return false;
    const config = /** @type {GwpConfig} */ (entry);
    if (
      typeof config.id !== 'string' ||
      (config.maxDiscountQuantity != null &&
        (!Number.isInteger(config.maxDiscountQuantity) || config.maxDiscountQuantity < 1)) ||
      !config.products?.length ||
      typeof config.progressVariant !== 'number' ||
      !Number.isFinite(config.progressVariant) ||
      config.progressVariant <= 0 ||
      (config.trigger !== 'coupon' && config.trigger !== 'subtotal')
    ) {
      return false;
    }

    if (config.trigger === 'coupon') {
      return typeof config.couponCode === 'string' && config.couponCode.trim().length > 0;
    }

    return typeof config.discountTitle === 'string' && config.discountTitle.trim().length > 0;
  }

  /**
   * Normalizes a config's gift products into a flat array of product descriptors.
   * Supports the `products` array (entries may be wrapped under a `product` key).
   * @param {GwpConfig} config
   * @returns {GwpProduct[]}
   */
  #getConfigProducts(config) {
    if (!Array.isArray(config.products)) return [];

    return config.products
      .map((entry) => (entry && typeof entry === 'object' && 'product' in entry ? entry.product : entry))
      .filter(
        (/** @type {unknown} */ product) =>
          !!product &&
          typeof product === 'object' &&
          Number.isFinite(/** @type {GwpProduct} */ (product).productId) &&
          /** @type {GwpProduct} */ (product).productId > 0 &&
          Number.isFinite(/** @type {GwpProduct} */ (product).variantId) &&
          /** @type {GwpProduct} */ (product).variantId > 0
      );
  }

  /**
   * @param {GwpProduct} product
   * @returns {GwpVariantOption[]}
   */
  #getProductVariantOptions(product) {
    /** @type {GwpVariantOption[]} */
    let variants;

    if (!Array.isArray(product.variants) || product.variants.length === 0) {
      variants = [{ variantId: product.variantId, optionValue: product.variantTitle || '', available: true }];
    } else {
      variants = product.variants.filter(
        (variant) =>
          variant &&
          Number.isFinite(variant.variantId) &&
          variant.variantId > 0 &&
          variant.available !== false
      );
    }

    if (Array.isArray(product.allowedVariantIds) && product.allowedVariantIds.length > 0) {
      const allowedVariantIds = new Set(
        product.allowedVariantIds
          .map((variantId) => String(variantId).trim())
          .filter((variantId) => variantId.length > 0)
      );

      if (allowedVariantIds.size > 0) {
        variants = variants.filter((variant) => allowedVariantIds.has(String(variant.variantId)));
      }
    }

    return variants;
  }

  /**
   * @param {GwpProduct} product
   * @returns {boolean}
   */
  #productRequiresVariantSelection(product) {
    // Match Liquid: fieldset HTML is only rendered when a varying option name exists.
    return Boolean(product?.variantFieldsetHtml);
  }

  /**
   * @param {string} optionName
   * @returns {string}
   */
  #pluralizeGwpOptionName(optionName) {
    const normalized = optionName.trim().toLowerCase();
    if (!normalized) return '';
    if (normalized.endsWith('s')) return normalized;
    return `${normalized}s`;
  }

  /**
   * @param {GwpProduct} product
   * @param {number} variantId
   * @returns {boolean}
   */
  #productHasVariant(product, variantId) {
    const variantIdString = String(variantId);
    return this.#getProductVariantOptions(product).some(
      (variant) => String(variant.variantId) === variantIdString
    );
  }

  /**
   * @param {GwpConfig} config
   * @param {number} selectedProductId
   * @param {number} [selectedVariantId]
   * @returns {GwpProduct | null}
   */
  #resolveOfferProduct(config, selectedProductId, selectedVariantId = 0) {
    const productById = this.#getConfigProductById(config, selectedProductId);

    if (selectedVariantId <= 0) {
      return productById;
    }

    if (productById && this.#productHasVariant(productById, selectedVariantId)) {
      return productById;
    }

    return this.#getConfigProduct(config, selectedVariantId) || productById;
  }

  /**
   * @param {{ config: GwpConfig, selectedProductId: number, selectedVariantId: number }} context
   * @returns {boolean}
   */
  #isOfferVariantSelectionComplete(context) {
    const product = this.#getConfigProductById(context.config, context.selectedProductId);
    if (!product || !this.#productRequiresVariantSelection(product)) return true;

    return context.selectedVariantId > 0;
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {number} productId
   * @returns {number}
   */
  #getCheckedOfferVariantId(item, config, productId) {
    const gwpId = config.id;
    const productIdString = String(productId);
    const selectedVariantRadio = item.querySelector(
      `input[name="gwpVariant-${gwpId}-${productIdString}"]:checked`
    );

    if (!(selectedVariantRadio instanceof HTMLInputElement)) return 0;

    const selectedVariantId = Number(selectedVariantRadio.value);
    return Number.isFinite(selectedVariantId) && selectedVariantId > 0 ? selectedVariantId : 0;
  }

  /**
   * @param {GwpProduct} product
   * @returns {boolean}
   */
  #productHasVariantOptions(product) {
    return this.#productRequiresVariantSelection(product);
  }

  /**
   * Default variant per configured gift product (used for auto-add).
   * @param {GwpConfig} config
   * @returns {number[]}
   */
  #getConfigVariantIds(config) {
    return this.#getConfigProducts(config).map((product) => product.variantId);
  }

  /**
   * Default variants safe to auto-add without shopper confirmation.
   * Products with a variant option fieldset require explicit selection first.
   * @param {GwpConfig} config
   * @returns {number[]}
   */
  #getAutoAddVariantIds(config) {
    return this.#getConfigProducts(config)
      .filter((product) => !this.#productRequiresVariantSelection(product))
      .map((product) => product.variantId)
      .slice(0, config.maxDiscountQuantity ?? undefined);
  }

  /**
   * Default gift variant for offer-box auto-add. Uses the primary (progress) product
   * only when it needs no shopper input, so multi-gift promos do not add every
   * configured product or bypass personalization.
   * @param {GwpConfig} config
   * @returns {number[]}
   */
  #getOfferBoxAutoAddVariantIds(config) {
    const primary = this.#getPrimaryProduct(config);
    if (
      !primary ||
      primary.isPersonalizable ||
      this.#productRequiresVariantSelection(primary)
    ) {
      return [];
    }

    return [primary.variantId];
  }

  /**
   * Variant IDs eligible for automatic sync add for this config.
   * @param {GwpConfig} config
   * @returns {number[]}
   */
  #getAutoAddVariantIdsForConfig(config) {
    if (config.offerBoxOnly) {
      return this.#getOfferBoxAutoAddVariantIds(config);
    }

    return this.#getAutoAddVariantIds(config);
  }

  /**
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #configRequiresVariantSelection(config) {
    return this.#getConfigProducts(config).some((product) =>
      this.#productRequiresVariantSelection(product)
    );
  }

  /**
   * Subtotal offers that require shopper interaction instead of a fully automatic
   * add. Sized gifts remain interactive even when their fulfillment setting is
   * `auto_add`, because a variant must be selected before a cart line can exist.
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isInteractiveSubtotalOffer(config) {
    return (
      config.trigger === 'subtotal' &&
      (config.offerBoxOnly || this.#configRequiresVariantSelection(config))
    );
  }

  /**
   * Coupon offers configured to show the offer box instead of auto-adding.
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isInteractiveCouponOffer(config) {
    return config.trigger === 'coupon' && config.offerBoxOnly === true;
  }

  /**
   * Any offer that requires the shopper to confirm/select in the offer box
   * before the gift is committed to the cart, whether subtotal- or
   * coupon-triggered.
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isInteractiveOffer(config) {
    return this.#isInteractiveSubtotalOffer(config) || this.#isInteractiveCouponOffer(config);
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #hasPendingVariantSelectionGift(cart, config) {
    if (!this.#configRequiresVariantSelection(config)) {
      return false;
    }

    const existingVariantIds = new Set(
      this.#findManagedLines(cart, config).map((line) => line.variantId)
    );

    return this.#getConfigProducts(config).some((product) => {
      if (!this.#productRequiresVariantSelection(product)) return false;

      return !this.#getProductVariantOptions(product).some((variant) =>
        existingVariantIds.has(variant.variantId)
      );
    });
  }

  /**
   * Every variant ID that may appear in the cart for this gift slot.
   * @param {GwpConfig} config
   * @returns {number[]}
   */
  #getAllConfigVariantIds(config) {
    const variantIds = new Set();

    for (const product of this.#getConfigProducts(config)) {
      for (const variant of this.#getProductVariantOptions(product)) {
        variantIds.add(variant.variantId);
      }
    }

    return [...variantIds];
  }

  /**
   * Resolves the representative product used for single-product UI (progress prompt,
   * offer box). Prefers the product matching `progressVariant`, falling back to the first.
   * @param {GwpConfig} config
   * @returns {GwpProduct | null}
   */
  #getPrimaryProduct(config) {
    const products = this.#getConfigProducts(config);
    if (products.length === 0) return null;

    if (Number.isFinite(config.progressVariant)) {
      const match = products.find(
        (product) =>
          product.variantId === config.progressVariant ||
          this.#getProductVariantOptions(product).some(
            (variant) => variant.variantId === config.progressVariant
          )
      );
      if (match) return match;
    }

    return products[0];
  }

  /**
   * @param {GwpConfig} config
   * @param {number} variantId
   * @returns {GwpProduct | null}
   */
  #getConfigProduct(config, variantId) {
    const variantIdString = String(variantId);

    return (
      this.#getConfigProducts(config).find((product) =>
        this.#getProductVariantOptions(product).some(
          (variant) => String(variant.variantId) === variantIdString
        )
      ) || null
    );
  }

  /**
   * @param {GwpConfig} config
   * @param {number} productId
   * @returns {GwpProduct | null}
   */
  #getConfigProductById(config, productId) {
    const productIdString = String(productId);
    return (
      this.#getConfigProducts(config).find(
        (product) => String(product.productId) === productIdString
      ) || null
    );
  }

  /**
   * @param {GwpProduct | null | undefined} product
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpMtoOptions(product) {
    const mtoOptions = this.#parseGwpJsonValue(product?.mtoOptions);
    return mtoOptions && typeof mtoOptions === 'object'
      ? /** @type {Record<string, unknown>} */ (mtoOptions)
      : null;
  }

  /**
   * @param {Record<string, unknown> | null} mtoOptions
   * @param {string} key
   * @returns {unknown | null}
   */
  #getGwpMtoDefault(mtoOptions, key) {
    if (!mtoOptions?.defaults || typeof mtoOptions.defaults !== 'object') return null;

    return /** @type {Record<string, unknown>} */ (mtoOptions.defaults)[key] ?? null;
  }

  /**
   * Resolves the selected gift product's MTO design.
   * @param {GwpProduct | null | undefined} product
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpDesignForProduct(product) {
    const mtoOptions = this.#resolveGwpMtoOptions(product);
    const defaultDesign = this.#getGwpMtoDefault(mtoOptions, 'design');

    return (
      this.#resolveGwpDesign(defaultDesign)
      || this.#resolveGwpDesign(mtoOptions)
    );
  }

  /**
   * @param {GwpProduct | null | undefined} product
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpPersonalizableDesign(product) {
    if (!product?.isPersonalizable) return null;
    return this.#resolveGwpDesignForProduct(product);
  }

  /**
   * Resolves the selected gift product's MTO thread/color.
   * @param {GwpProduct | null | undefined} product
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpColorForProduct(product) {
    const mtoOptions = this.#resolveGwpMtoOptions(product);
    const defaultColor = this.#getGwpMtoDefault(mtoOptions, 'color');

    return (
      this.#resolveGwpColor(defaultColor)
      || this.#resolveGwpColor(mtoOptions)
    );
  }

  #bindOffersClick() {
    if (this.#offersClickBound) return;

    document.addEventListener('input', this.#onOffersInput);
    document.addEventListener('change', this.#onOffersChange);
    document.addEventListener('change', this.#togglePersonalization);
    // Product radios live inside swiper-container (shadow DOM); change may not
    // reach document and closest('[data-gwp-offer-item]') can fail. Click is composed.
    document.addEventListener('click', this.#onOffersClick, true);
    this.#offersClickBound = true;
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  #parseGwpScopeIdFromInputName(name) {
    if (typeof name !== 'string' || !name) return '';

    if (name.startsWith('gwpProduct-')) {
      return name.slice('gwpProduct-'.length);
    }

    if (name.startsWith('gwpVariant-')) {
      return name.slice('gwpVariant-'.length).split('-')[0] || '';
    }

    if (name.startsWith('addGWPProduct-')) {
      return name.slice('addGWPProduct-'.length);
    }

    return '';
  }

  /**
   * @param {string} scopeId
   * @returns {{ item: HTMLElement, config: GwpConfig } | null}
   */
  #resolveOfferItemByScopeId(scopeId) {
    if (!scopeId) return null;

    const item = document.querySelector(
      `[data-gwp-offer-item][data-gwp-id="${CSS.escape(scopeId)}"]`
    );
    if (!(item instanceof HTMLElement)) return null;

    const config = this.#config.find((entry) => entry.id === scopeId);
    if (!config) return null;

    return { item, config };
  }

  /**
   * Resolves the offer item for GWP-scoped inputs. Swiper may render product
   * radios outside the `[data-gwp-offer-item]` subtree, so fall back to the
   * slot id encoded in the input name.
   * @param {HTMLInputElement | null} input
   * @returns {{ item: HTMLElement, config: GwpConfig } | null}
   */
  #resolveOfferItemFromGwpScopedInput(input) {
    if (input instanceof HTMLInputElement) {
      const fromClosest = this.#resolveOfferItemFromTarget(input);
      if (fromClosest) return fromClosest;

      const scopeId = this.#parseGwpScopeIdFromInputName(input.name);
      if (scopeId) {
        return this.#resolveOfferItemByScopeId(scopeId);
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @returns {string}
   */
  #resolveSelectedProductIdString(item, config) {
    const gwpId = config.id;
    const inputName = `gwpProduct-${gwpId}`;
    let selectedRadio = item.querySelector(`input[name="${CSS.escape(inputName)}"]:checked`);

    if (!(selectedRadio instanceof HTMLInputElement)) {
      selectedRadio = document.querySelector(`input[name="${CSS.escape(inputName)}"]:checked`);
    }

    if (selectedRadio instanceof HTMLInputElement) {
      const productId = selectedRadio.value.trim();
      if (productId && productId !== '0') return productId;
    }

    const primary = this.#getPrimaryProduct(config);
    return primary ? String(primary.productId) : '';
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @returns {number}
   */
  #resolveSelectedProductId(item, config) {
    const productIdString = this.#resolveSelectedProductIdString(item, config);
    const productId = Number(productIdString);
    return Number.isFinite(productId) && productId > 0 ? productId : 0;
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {number} [productId]
   * @returns {number}
   */
  #resolveSelectedVariantId(item, config, productId = this.#resolveSelectedProductId(item, config)) {
    const product = this.#getConfigProductById(config, productId);
    if (!product) return 0;

    const variantOptions = this.#getProductVariantOptions(product);
    if (!this.#productRequiresVariantSelection(product)) {
      return variantOptions[0]?.variantId ?? product.variantId;
    }

    return this.#getCheckedOfferVariantId(item, config, productId);
  }

  /**
   * Coupon free rush must validate the entered code against a real configured
   * gift variant before the rush line is granted. When the shopper chooses Rush
   * first, a sized gift may not have a checked variant yet, so use that product's
   * first configured variant solely for the temporary validation step.
   * @param {{ config: GwpConfig, selectedVariantId: number, selectedProductId: number }} context
   * @returns {number}
   */
  #resolveFreeRushValidationVariantId(context) {
    if (Number.isFinite(context.selectedVariantId) && context.selectedVariantId > 0) {
      return context.selectedVariantId;
    }

    const product = this.#getConfigProductById(context.config, context.selectedProductId);
    const variantId = product
      ? this.#getProductVariantOptions(product)[0]?.variantId ?? product.variantId
      : 0;

    return Number.isFinite(variantId) && variantId > 0 ? variantId : 0;
  }

  /**
   * @param {Element} target
   * @returns {{ item: HTMLElement, config: GwpConfig } | null}
   */
  #resolveOfferItemFromTarget(target) {
    if (!(target instanceof Element)) return null;

    const item = target.closest('[data-gwp-offer-item]');
    if (!(item instanceof HTMLElement)) return null;

    const gwpId = item.dataset.gwpId;
    if (!gwpId) return null;

    const config = this.#config.find((entry) => entry.id === gwpId);
    if (!config) return null;

    return { item, config };
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isOfferGiftAccepted(item) {
    const accept = item.querySelector('input[value="acceptGWPGProduct"]');
    return accept instanceof HTMLInputElement && accept.checked;
  }

  /**
   * True when the shopper explicitly accepted the gift (or rush) offer, as opposed
   * to the offer box merely showing gift options in its default preview state.
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #hasUserConfirmedGiftAccept(item) {
    const gwpId = item instanceof HTMLElement ? item.dataset.gwpId : '';
    return Boolean(gwpId && this.#confirmedOfferAcceptIds.has(gwpId));
  }

  /**
   * @param {HTMLElement} item
   */
  #markUserConfirmedGiftAccept(item) {
    if (!(item instanceof HTMLElement)) return;

    const gwpId = item.dataset.gwpId;
    if (!gwpId) return;

    this.#confirmedOfferAcceptIds.add(gwpId);
    item.dataset.gwpOfferAccepted = 'true';
  }

  /**
   * Treats interaction with the visible gift form as confirmation of the already
   * selected FREE gift choice. The initial preview remains unconfirmed until the
   * shopper interacts with either that choice or one of its form controls.
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #confirmGiftAcceptFromInteraction(item) {
    if (!this.#isOfferGiftAccepted(item)) return false;

    this.#markUserConfirmedGiftAccept(item);
    return true;
  }

  /**
   * @param {HTMLElement} item
   */
  #clearUserConfirmedGiftAccept(item) {
    if (!(item instanceof HTMLElement)) return;

    const gwpId = item.dataset.gwpId;
    if (gwpId) this.#confirmedOfferAcceptIds.delete(gwpId);
    delete item.dataset.gwpOfferAccepted;
  }

  /**
   * @param {HTMLElement} item
   */
  #syncUserConfirmedGiftAcceptToItem(item) {
    if (!(item instanceof HTMLElement)) return;

    const gwpId = item.dataset.gwpId;
    if (!gwpId) return;

    if (this.#confirmedOfferAcceptIds.has(gwpId)) {
      item.dataset.gwpOfferAccepted = 'true';
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {number} [selectedProductIdOverride]
   * @returns {{ item: HTMLElement, config: GwpConfig, selectedVariantId: number, selectedProductId: number } | null}
   */
  #buildOfferContext(item, config, selectedProductIdOverride) {
    const selectedProductId =
      selectedProductIdOverride != null &&
      Number.isFinite(selectedProductIdOverride) &&
      selectedProductIdOverride > 0
        ? selectedProductIdOverride
        : this.#resolveSelectedProductId(item, config);

    if (!selectedProductId) return null;

    const selectedVariantId = this.#resolveSelectedVariantId(item, config, selectedProductId);

    return { item, config, selectedVariantId, selectedProductId };
  }

  /**
   * @param {Element} target
   * @returns {{ item: HTMLElement, config: GwpConfig, selectedVariantId: number, selectedProductId: number } | null}
   */
  #resolveOfferContext(target) {
    if (!(target instanceof Element)) return null;

    const offerItem =
      (target instanceof HTMLInputElement ? this.#resolveOfferItemFromGwpScopedInput(target) : null)
      || this.#resolveOfferItemFromTarget(target);

    if (!offerItem) return null;

    const { item, config } = offerItem;
    return this.#buildOfferContext(item, config);
  }

  /**
   * @returns {GwpFreeRushConfig | null}
   */
  #loadFreeRushConfig() {
    const configElement = document.getElementById('gift-with-purchase-free-rush-config');
    if (!(configElement instanceof HTMLScriptElement)) return null;

    try {
      const parsed = JSON.parse(configElement.textContent || '{}');
      const variantId = Number(parsed.variantId);

      if (!Number.isFinite(variantId) || variantId <= 0) return null;

      const fallbackLabel =
        Theme.translations?.gift_with_purchase_free_rush_option || 'RUSH my order for FREE';

      return {
        variantId,
        label:
          typeof parsed.label === 'string' && parsed.label.trim()
            ? parsed.label.trim()
            : `${fallbackLabel}:`,
        imageUrl: typeof parsed.imageUrl === 'string' ? parsed.imageUrl : '',
      };
    } catch (error) {
      console.error('Gift with purchase: invalid free rush config', error);
      return null;
    }
  }

  /**
   * Parses the `letter_type_label_overrides` theme setting (one FROM=TO mapping
   * per line, FROM matched case-insensitively) into a lookup map.
   * @returns {Map<string, string>}
   */
  #loadLetterTypeOverrides() {
    /** @type {Map<string, string>} */
    const overrides = new Map();
    const configElement = document.getElementById('gift-with-purchase-letter-type-overrides');
    if (!(configElement instanceof HTMLScriptElement)) return overrides;

    try {
      const raw = JSON.parse(configElement.textContent || '""');
      if (typeof raw !== 'string') return overrides;

      for (const line of raw.split('\n')) {
        const separatorIndex = line.indexOf('=');
        if (separatorIndex === -1) continue;
        const from = line.slice(0, separatorIndex).trim().toLowerCase();
        const to = line.slice(separatorIndex + 1).trim();
        if (from && to) overrides.set(from, to);
      }
    } catch (error) {
      console.error('Gift with purchase: invalid letter type overrides', error);
    }

    return overrides;
  }

  /**
   * @param {Object} cart
   * @returns {boolean}
   */
  #hasPaidRushFeeInCart(cart) {
    const rush = this.#freeRushOrder;
    if (!rush) return false;

    return (cart.items || []).some((item) => {
      return (
        Number(item?.variant_id) === rush.variantId &&
        this.#getLineItemProperty(item?.properties, RUSH_ORDER_FEE_PROPERTY) === 'false'
      );
    });
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} [cart]
   * @returns {boolean}
   */
  #canOfferFreeRush(config, cart) {
    if (
      !this.#isInteractiveOffer(config) ||
      !this.#freeRushOrder?.variantId
    ) {
      return false;
    }
    if (cart && this.#hasPaidRushFeeInCart(cart)) return false;

    return true;
  }

  /**
   * @param {Object} item
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isFreeRushLineItem(item, config) {
    const rush = this.#freeRushOrder;
    if (!rush || Number(item?.variant_id) !== rush.variantId) return false;

    const properties = item?.properties || {};

    return (
      this.#getLineItemProperty(properties, GWP_SLOT_LINE_PROPERTY) === config.id &&
      this.#getLineItemProperty(properties, RUSH_ORDER_FEE_PROPERTY) === 'true'
    );
  }

  /**
   * @param {HTMLElement} item
   * @returns {'gift' | 'reject' | 'rush'}
   */
  #resolveOfferChoiceFromDom(item) {
    const reject = item.querySelector('input[value="rejectGWPProduct"]');
    if (reject instanceof HTMLInputElement && reject.checked) return 'reject';

    const rush = item.querySelector(`input[value="${ACCEPT_GWP_RUSH_VALUE}"]`);
    if (rush instanceof HTMLInputElement && rush.checked) return 'rush';

    const accept = item.querySelector('input[value="acceptGWPGProduct"]');
    if (accept instanceof HTMLInputElement && accept.checked) return 'gift';

    return 'gift';
  }

  /**
   * @param {HTMLElement} item
   * @param {'reject' | GwpOfferChoice} choice
   */
  #syncOfferChoiceVisibility(item, choice) {
    const giftOptions = item.querySelector('.gift-with-purchase-offers__add-gwp-options');
    const variantOptions = item.querySelector('[data-gwp-variant-options]');
    const showGiftOptions = choice === 'gift';

    item.dataset.gwpShowGiftDetails = showGiftOptions ? 'true' : 'false';
    this.#setOfferGiftDetailsDisabled(item, !showGiftOptions);

    if (giftOptions instanceof HTMLElement) {
      giftOptions.hidden = !showGiftOptions;
    }

    const gwpId = item.dataset.gwpId;
    const config = gwpId ? this.#config.find((entry) => entry.id === gwpId) : null;

    if (showGiftOptions && config) {
      this.#syncOfferVariantOptionsVisibility(
        item,
        config,
        this.#resolveSelectedProductIdString(item, config)
      );
      this.#syncOfferPersonalizationForSelection(
        item,
        config,
        this.#resolveSelectedVariantId(item, config),
        this.#resolveSelectedProductIdString(item, config)
      );
      return;
    }

    if (variantOptions instanceof HTMLElement) {
      variantOptions.hidden = true;
    }

    item.querySelectorAll('[data-gwp-variant-options-item]').forEach((wrapper) => {
      if (wrapper instanceof HTMLElement) {
        wrapper.hidden = true;
      }
    });
  }

  /**
   * Removes a paid rush fee line when the shopper selects the free rush promo.
   * @returns {Promise<void>}
   */
  async #removePaidRushFeeIfPresent() {
    const rush = this.#freeRushOrder;
    if (!rush) return;

    const cart = await this.#fetchCart();
    const paidRushLine = (cart.items || []).find((item) => {
      return (
        Number(item?.variant_id) === rush.variantId &&
        this.#getLineItemProperty(item?.properties, RUSH_ORDER_FEE_PROPERTY) === 'false'
      );
    });

    if (!paidRushLine?.key) return;

    await this.#changeLineQuantity(paidRushLine.key, 0, cart);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} disabled
   */
  #setOfferGiftDetailsDisabled(item, disabled) {
    const gwpId = item.dataset.gwpId || '';

    item.querySelectorAll(
      `input[name="gwpProduct-${gwpId}"], input[data-gwp-variant-input], input[data-personalization-text-input], input[data-personalize-opt-out-toggle]`
    ).forEach((input) => {
      if (input instanceof HTMLInputElement) input.disabled = disabled;
    });
  }

  /**
   * Accept / reject / rush choice radios for an offer item.
   * @param {HTMLElement} item
   * @param {boolean} disabled
   */
  #setOfferChoiceDisabled(item, disabled) {
    const gwpId = item.dataset.gwpId || '';

    item.querySelectorAll(`input[name="addGWPProduct-${gwpId}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement) input.disabled = disabled;
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} disabled
   */
  #setOfferControlsDisabled(item, disabled) {
    this.#setOfferGiftDetailsDisabled(item, disabled);
    this.#setOfferChoiceDisabled(item, disabled);
  }

  /**
   * Re-applies rush/reject gift-detail disabled state after loading ends.
   */
  #restoreAllOfferControlsEnabledState() {
    document.querySelectorAll('[data-gwp-offer-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const choice = this.#resolveOfferChoiceFromDom(item);
      this.#setOfferChoiceDisabled(item, false);
      this.#setOfferGiftDetailsDisabled(item, choice === 'reject' || choice === 'rush');
    });
  }

  /**
   * Clears the promotions overlay when no cart mutation is in flight.
   */
  #clearPromotionsLoadingIfIdle() {
    if (!this.#syncing) {
      this.#setPromotionsLoading(false);
    }
  }

  /**
   * Fallback in case cart section morph listeners are not ready yet.
   */
  #schedulePromotionsLoadingFallbackClear() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#clearPromotionsLoadingIfIdle();
      });
    });
  }

  /**
   * @returns {boolean}
   */
  #lockOfferUpdates() {
    if (this.#syncing || this.#preparingDiscountCode) return false;

    this.#syncing = true;
    return true;
  }

  /**
   * @param {boolean} [clearLoading]
   */
  #unlockOfferUpdates(clearLoading = true) {
    if (clearLoading) {
      this.#setPromotionsLoading(false);
    }
    this.#syncing = false;
    void this.#flushPendingRejectRemovals();
  }

  /**
   * @param {unknown} colorPayload
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpColor(colorPayload) {
    if (!colorPayload || typeof colorPayload !== 'object') return null;

    if (Array.isArray(colorPayload)) {
      return this.#findGwpOption(colorPayload);
    }

    const payload = /** @type {Record<string, unknown>} */ (colorPayload);

    if (Array.isArray(payload.colors)) {
      return this.#findGwpOption(payload.colors);
    }

    if (this.#hasGwpOptionValue(payload, 'id') || this.#hasGwpOptionValue(payload, 'name')) {
      return payload;
    }

    return null;
  }

  /**
   * @param {GwpProduct | null | undefined} product
   * @returns {string}
   */
  #getGwpProductCode(product) {
    const mtoOptions = this.#resolveGwpMtoOptions(product);
    return this.#toGwpString(mtoOptions?.sku).trim();
  }

  /**
   * @param {HTMLElement} item
   * @returns {Promise<string>}
   */
  async #resolveGwpPreviewCartImageUrl(item) {
    const personalization = item.querySelector('personalization-component[data-gwp-personalization]');
    if (!(personalization instanceof HTMLElement)) return '';

    if (typeof personalization.ensurePreviewCartImageUrl === 'function') {
      return personalization.ensurePreviewCartImageUrl();
    }

    return personalization.dataset.previewCartImageUrl?.trim() || '';
  }

  /**
   * @param {GwpProduct | null | undefined} product
   * @param {string} text
   * @param {{ previewCartImageUrl?: string }} [options]
   * @returns {Record<string, string>}
   */
  #buildGwpPersonalizationProperties(product, text, options = {}) {
    const trimmed = (text || '').trim();
    if (!trimmed) return {};

    const design = this.#resolveGwpPersonalizableDesign(product);
    if (!design) return {};

    const color = this.#resolveGwpColorForProduct(product);
    // Store the raw letter type as the property key for order fulfillment
    const letterType = this.#getGwpDesignLetterTypeRaw(design);
    const previewCartImageUrl = (options.previewCartImageUrl || '').trim();
    const designImage =
      previewCartImageUrl || this.#toGwpString(design.svg_url || design.thumbnail).trim();
    const designId = this.#toGwpString(design.id).trim();

    /** @type {Record<string, string>} */
    const properties = {};
    properties[letterType] = trimmed;

    if (designImage) properties.Design = designImage;
    if (designId) properties['_selected-id'] = designId;

    if (color) {
      const colorName = this.#toGwpString(color.name).trim();
      const colorHex = this.#toGwpString(color.hex).trim();
      const colorId = this.#toGwpString(color.id).trim();

      if (colorName) properties['Thread Color'] = colorName;
      if (colorHex) properties['_swatch-color'] = colorHex;
      if (colorId) properties['_selected-color'] = colorId;
    }

    return properties;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} design
   * @returns {string}
   */
  #getGwpDesignLetterType(design) {
    const rawLetterType = this.#getGwpDesignLetterTypeRaw(design);
    return this.#letterTypeOverrides.get(rawLetterType.toLowerCase()) || rawLetterType;
  }

  /**
   * The original (un-overridden) letter type. Used for stored data
   * @param {Record<string, unknown> | null | undefined} design
   * @returns {string}
   */
  #getGwpDesignLetterTypeRaw(design) {
    return this.#toGwpString(design?.letter_type).trim() || 'Monogram';
  }

  /**
   * Hidden line-item property persisted when the shopper explicitly opts out of
   * personalization. Empty personalization text alone does not set this flag.
   * @returns {Record<string, string>}
   */
  #buildGwpOptOutProperties() {
    return { [GWP_PERSONALIZATION_OPT_OUT_PROPERTY]: 'Yes' };
  }

  /**
   * @param {Record<string, unknown> | null | undefined} properties
   * @returns {boolean}
   */
  #isGwpPersonalizationOptedOut(properties) {
    if (this.#hasLineItemProperty(properties, GWP_PERSONALIZATION_OPT_OUT_PROPERTY)) {
      return this.#getLineItemProperty(properties, GWP_PERSONALIZATION_OPT_OUT_PROPERTY) === 'Yes';
    }
    return this.#getLineItemProperty(properties, SKIP_PERSONALIZATION_PROPERTY_KEY) === 'true';
  }

  /**
   * True only when the cart line (or linked personalization record) stores the
   * explicit opt-out property. Empty personalization text alone is not opt-out.
   * @param {Object} cart
   * @param {Object} line
   * @param {GwpProduct | null | undefined} product
   * @returns {boolean}
   */
  #resolveGwpOfferOptedOut(cart, line, product) {
    const properties = line?.properties || {};
    if (this.#isGwpPersonalizationOptedOut(properties)) return true;

    const design = this.#resolveGwpPersonalizableDesign(product);
    if (!design) return false;

    const letterType = this.#getGwpDesignLetterTypeRaw(design);
    const personalizationRecord = this.#getGwpPersonalizationRecordFromCartLine(cart, line, letterType);
    if (this.#isGwpPersonalizationOptedOut(personalizationRecord)) return true;

    return false;
  }

  /**
   * @param {Record<string, string>} [properties]
   * @param {string} configId
   * @returns {Record<string, string>}
   */
  #withGwpSlotProperty(properties = {}, configId) {
    return {
      ...properties,
      [GWP_SLOT_LINE_PROPERTY]: configId,
      [SKIP_PERSONALIZATION_PROPERTY_KEY]: this.#isGwpPersonalizationOptedOut(properties) ? 'true' : 'false',
    };
  }

  /**
   * @returns {boolean}
   */
  #isCartAttributePersonalizationEnabled() {
    return window.SDG?.Data?.enableCartAttributePersonalization === true;
  }

  /**
   * @param {Record<string, string>} properties
   * @returns {{ attributes: Array<{ key: string, value: string | { label: string, value: string } }> }}
   */
  #buildCartAttributePersonalizationPayload(properties) {
    /** @type {Array<{ key: string, value: string | { label: string, value: string } }>} */
    const attributes = [];

    for (const key of PERSONALIZATION_STATIC_PROPERTY_KEYS) {
      const value = properties[key];
      if (typeof value !== 'string' || !value.trim()) continue;
      attributes.push({ key, value: value.trim() });
    }

    for (const [key, value] of Object.entries(properties)) {
      if (!this.#isGwpDynamicPersonalizationProperty(key, value)) continue;

      attributes.push({
        key: '_selected-letter-type',
        value: { label: key, value: value.trim() },
      });
      break;
    }

    return withSkipPersonalizationAttribute(
      { attributes },
      this.#isGwpPersonalizationOptedOut(properties)
    );
  }

  /**
   * @param {string} key
   * @param {unknown} value
   * @returns {value is string}
   */
  #isGwpDynamicPersonalizationProperty(key, value) {
    if (typeof value !== 'string' || !value.trim()) return false;
    if (PERSONALIZATION_STATIC_PROPERTY_KEYS.has(key)) return false;
    if (key.startsWith('_') || key === 'Gift Message') return false;
    return true;
  }

  /**
   * @param {unknown} properties
   * @param {string} key
   * @returns {string}
   */
  #getLineItemProperty(properties, key) {
    if (!properties || !key) return '';

    if (Array.isArray(properties)) {
      for (const entry of properties) {
        if (Array.isArray(entry) && entry[0] === key) {
          return entry[1] != null ? String(entry[1]).trim() : '';
        }

        if (entry && typeof entry === 'object') {
          const record = /** @type {Record<string, unknown>} */ (entry);
          if (record.name === key) {
            return record.value != null ? String(record.value).trim() : '';
          }
        }
      }

      return '';
    }

    if (typeof properties === 'object') {
      const value = /** @type {Record<string, unknown>} */ (properties)[key];
      if (typeof value === 'string') return value.trim();
      return value != null ? String(value).trim() : '';
    }

    return '';
  }

  /**
   * @param {unknown} properties
   * @param {string} key
   * @returns {boolean}
   */
  #hasLineItemProperty(properties, key) {
    if (!properties || !key) return false;
    if (Array.isArray(properties)) {
      return properties.some((entry) =>
        (Array.isArray(entry) && entry[0] === key) ||
        (entry && typeof entry === 'object' && /** @type {Record<string, unknown>} */ (entry).name === key)
      );
    }
    return typeof properties === 'object' && Object.prototype.hasOwnProperty.call(properties, key);
  }

  /**
   * @param {Object} cart
   * @param {Object} line
   * @param {string} preferredLetterType
   * @returns {Record<string, string>}
   */
  #getGwpPersonalizationRecordFromCartLine(cart, line, preferredLetterType) {
    const properties = line?.properties || {};
    /** @type {Record<string, string>} */
    const record = {};

    const legacyText = this.#resolveGwpPersonalizationText(properties, preferredLetterType);
    if (legacyText) {
      if (preferredLetterType) record[preferredLetterType] = legacyText;
      return record;
    }

    const pid = this.#getLineItemProperty(properties, '_pid');
    if (!pid || !cart?.attributes) return record;

    const rawPayload = getPersonalizationEntryRawFromAttributesWithLegacy(cart.attributes, pid);
    if (!rawPayload) return record;

    return parsePersonalizationEntryPayload(rawPayload);
  }

  /**
   * @param {Record<string, unknown> | null | undefined} properties
   * @param {string} preferredLetterType
   * @returns {string}
   */
  #resolveGwpPersonalizationText(properties, preferredLetterType) {
    if (!properties || typeof properties !== 'object') return '';

    const preferredText = preferredLetterType ? properties[preferredLetterType] : undefined;
    if (typeof preferredText === 'string' && preferredText.trim()) return preferredText;

    for (const [key, value] of Object.entries(properties)) {
      if (this.#isGwpDynamicPersonalizationProperty(key, value)) return value;
    }

    return '';
  }

  /**
   * @param {Record<string, string>} properties
   * @returns {boolean}
   */
  #hasGwpPersonalizationPayload(properties) {
    return (
      this.#isGwpPersonalizationOptedOut(properties) ||
      this.#buildCartAttributePersonalizationPayload(properties).attributes.some(
        (attribute) => attribute.key !== SKIP_PERSONALIZATION_PROPERTY_KEY
      )
    );
  }

  /**
   * Stable personalization identity for change detection. Excludes `Design` because
   * preview cart image URLs are regenerated and must not force cart/_pid churn.
   * @param {Record<string, string>} properties
   * @returns {string}
   */
  #getGwpPersonalizationIdentity(properties) {
    if (!properties || typeof properties !== 'object') return '';

    if (this.#isGwpPersonalizationOptedOut(properties)) {
      return 'opt-out';
    }

    let letterType = '';
    let text = '';
    for (const [key, value] of Object.entries(properties)) {
      if (!this.#isGwpDynamicPersonalizationProperty(key, value)) continue;
      letterType = key.trim();
      text = value.trim();
      break;
    }

    return [
      (properties['_selected-id'] || '').trim(),
      (properties['_selected-color'] || '').trim(),
      (properties['Thread Color'] || '').trim(),
      (properties['_swatch-color'] || '').trim(),
      letterType,
      text,
    ].join('\0');
  }

  /**
   * @param {Object} cart
   * @param {Object} line
   * @returns {string}
   */
  #getGwpPersonalizationIdentityFromCartLine(cart, line) {
    const properties = line?.properties || {};
    if (this.#isGwpPersonalizationOptedOut(properties)) return 'opt-out';

    const pid = this.#getLineItemProperty(properties, '_pid');
    if (pid && cart?.attributes) {
      const rawPayload = getPersonalizationEntryRawFromAttributesWithLegacy(cart.attributes, pid);
      if (rawPayload) {
        return this.#getGwpPersonalizationIdentity(parsePersonalizationEntryPayload(rawPayload));
      }
    }

    return this.#getGwpPersonalizationIdentity(
      /** @type {Record<string, string>} */ (properties)
    );
  }

  /**
   * True when the cart already holds this gift variant with the same personalization
   * content (ignoring volatile preview Design URLs).
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {number} variantId
   * @param {Record<string, string>} properties
   * @returns {boolean}
   */
  #isGwpGiftSelectionUnchanged(cart, config, variantId, properties) {
    const existingLine = this.#findManagedLines(cart, config).find(
      (line) => line.variantId === variantId
    );
    if (!existingLine) return false;

    const lineItem = (cart.items || []).find((item) => item.key === existingLine.key);
    if (!lineItem) return false;

    const proposedRush = properties[RUSH_ORDER_FEE_PROPERTY] === 'true';
    const existingRush = this.#isFreeRushLineItem(lineItem, config);
    if (proposedRush || existingRush) {
      return proposedRush === existingRush;
    }

    const proposedOptOut = this.#isGwpPersonalizationOptedOut(properties);
    const existingOptOut = this.#resolveGwpOfferOptedOut(
      cart,
      lineItem,
      this.#getConfigProduct(config, variantId)
    );
    if (proposedOptOut || existingOptOut) {
      return proposedOptOut === existingOptOut;
    }

    return (
      this.#getGwpPersonalizationIdentity(properties) ===
      this.#getGwpPersonalizationIdentityFromCartLine(cart, lineItem)
    );
  }

  /**
   * Keeps only non-personalization line properties and assigns a `_pid` reference.
   * @param {Record<string, string>} properties
   * @param {string} pid
   * @returns {Record<string, string>}
   */
  #toCartAttributeLineProperties(properties, pid) {
    /** @type {Record<string, string>} */
    const result = { _pid: pid };

    for (const [key, value] of Object.entries(properties)) {
      if (typeof value !== 'string' || !value.trim()) continue;
      if (PERSONALIZATION_STATIC_PROPERTY_KEYS.has(key)) continue;
      if (key.startsWith('_') && key !== GWP_PERSONALIZATION_OPT_OUT_PROPERTY && key !== GWP_SLOT_LINE_PROPERTY) continue;
      if (key === 'Gift Message') {
        result[key] = value.trim();
        continue;
      }
      // Dynamic letter-type values are stored on the cart attribute payload.
    }

    if (properties[GWP_PERSONALIZATION_OPT_OUT_PROPERTY]) {
      result[GWP_PERSONALIZATION_OPT_OUT_PROPERTY] = properties[GWP_PERSONALIZATION_OPT_OUT_PROPERTY];
    }

    if (properties[GWP_SLOT_LINE_PROPERTY]) {
      result[GWP_SLOT_LINE_PROPERTY] = properties[GWP_SLOT_LINE_PROPERTY];
    }

    result[SKIP_PERSONALIZATION_PROPERTY_KEY] =
      properties[SKIP_PERSONALIZATION_PROPERTY_KEY] === 'true' ? 'true' : 'false';

    return result;
  }

  /**
   * @param {Object} cart
   * @param {Array<{ key: string }>} lines
   * @returns {string[]}
   */
  #collectPersonalizationPidsFromCartLines(cart, lines) {
    /** @type {string[]} */
    const pids = [];

    for (const line of lines) {
      const pid = this.#resolvePersonalizationPidForLineKey(cart, line.key);
      if (pid) pids.push(pid);
    }

    return pids;
  }

  /**
   * Resolves a line item `_pid` from cart data, falling back to the cart row DOM
   * when Ajax cart responses omit line properties.
   * @param {Object} cart
   * @param {string} lineKey
   * @returns {string}
   */
  #resolvePersonalizationPidForLineKey(cart, lineKey) {
    if (!lineKey) return '';

    const item = (cart.items || []).find((entry) => entry.key === lineKey);
    const pidFromCart = getLineItemPidFromProperties(item?.properties);
    if (pidFromCart) return pidFromCart;

    const row = document.querySelector(`.cart-items__table-row[data-key="${CSS.escape(lineKey)}"]`);
    if (row instanceof HTMLElement) {
      return row.dataset.personalizationPid?.trim() || '';
    }

    return this.#getLineItemProperty(item?.properties, '_pid');
  }

  /**
   * @param {Object} cartResponse
   * @returns {Promise<Object>}
   */
  async #refreshCartAfterLineRemovals(cartResponse) {
    const sections = cartResponse.sections;
    const refreshedCart = await this.#fetchCart();

    if (sections) {
      refreshedCart.sections = sections;
    }

    return refreshedCart;
  }

  /**
   * Re-runs cart discount calculation with the current applicable codes so
   * automatic GWP discounts can settle before verify.
   * @param {Object} cart
   * @returns {Promise<Object>}
   */
  async #recalculateCartDiscounts(cart) {
    const sections = this.#getCartSectionIds();
    const codes = getApplicableDiscountCodes(
      /** @type {import('@theme/cart-discount-utils').CartDiscountCode[] | undefined} */ (
        cart.discount_codes
      )
    );
    const response = await updateCartDiscountCodes(Theme.routes.cart_update_url, codes, {
      sections: sections.length > 0 ? sections : undefined,
    });

    const refreshedCart = await this.#fetchCart();
    if (response.sections) {
      refreshedCart.sections = response.sections;
    } else if (cart.sections) {
      refreshedCart.sections = cart.sections;
    }

    return refreshedCart;
  }

  /**
   * Nudges Shopify to re-evaluate automatic discounts by posting a no-op cart update.
   * @param {Object} cart
   * @returns {Promise<Object>}
   */
  async #triggerCartRecalculation(cart) {
    const sections = this.#getCartSectionIds();
    const body = JSON.stringify({
      note: typeof cart.note === 'string' ? cart.note : '',
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to recalculate cart: ${response.status}`);
    }

    const data = await response.json();
    const refreshedCart = await this.#fetchCart();

    if (data.sections) {
      refreshedCart.sections = data.sections;
    } else if (cart.sections) {
      refreshedCart.sections = cart.sections;
    }

    return refreshedCart;
  }

  /**
   * Re-applies quantity on a slot-stamped gift line so automatic discounts can attach.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {Promise<Object>}
   */
  async #nudgeGiftLineForDiscount(cart, config) {
    const giftLine = this.#getConfigGiftLineItems(cart, config).find(
      (item) => this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY) === config.id
    );

    if (!giftLine?.key) return cart;

    const quantity = Number(giftLine.quantity) > 0 ? Number(giftLine.quantity) : 1;
    const changeResponse = await this.#changeLineQuantity(giftLine.key, quantity, cart);
    const refreshedCart = await this.#fetchCart();

    if (changeResponse.sections) {
      refreshedCart.sections = changeResponse.sections;
    } else if (cart.sections) {
      refreshedCart.sections = cart.sections;
    }

    return refreshedCart;
  }

  /**
   * Polls the cart after a gift add so Shopify has time to apply automatic discounts.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {number} [maxAttempts]
   * @param {number | null} [expectedVariantId]
   * @returns {Promise<Object>}
   */
  async #awaitGwpNativeApplication(cart, config, maxAttempts = 8, expectedVariantId = null) {
    const sections = cart.sections;
    let cartSnapshot = cart;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const applied = expectedVariantId == null
        ? this.#isGwpNativelyApplied(cartSnapshot, config)
        : this.#isGwpSelectionApplied(cartSnapshot, config, expectedVariantId);
      if (applied) {
        if (sections && !cartSnapshot.sections) {
          cartSnapshot.sections = sections;
        }
        return cartSnapshot;
      }

      if (attempt >= maxAttempts - 1) break;

      await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 200 : 300));

      try {
        if (attempt % 3 === 0) {
          cartSnapshot = await this.#nudgeGiftLineForDiscount(cartSnapshot, config);
        } else if (attempt % 3 === 1) {
          cartSnapshot = await this.#recalculateCartDiscounts(cartSnapshot);
        } else {
          cartSnapshot = await this.#triggerCartRecalculation(cartSnapshot);
        }
      } catch {
        cartSnapshot = await this.#fetchCart();
      }

      if (sections) {
        cartSnapshot.sections = sections;
      }
    }

    return cartSnapshot;
  }

  /**
   * Retries automatic discount application for gifts already in the cart.
   * @param {Object} cart
   * @returns {Promise<Object>}
   */
  async #applyPendingGiftDiscounts(cart) {
    let cartSnapshot = cart;

    for (const config of this.#config) {
      if (!this.#isGiftQualified(cartSnapshot, config)) continue;
      if (this.#isDismissed(config.id)) continue;
      if (!this.#hasSlotStampedGiftLine(cartSnapshot, config)) continue;
      if (this.#isGwpSelectionApplied(cartSnapshot, config)) continue;
      if (this.#hasDiscountConflictForGwp(cartSnapshot, config)) continue;

      cartSnapshot = await this.#awaitGwpNativeApplication(cartSnapshot, config);
    }

    return cartSnapshot;
  }

  /**
   * @param {Object} cartResponse
   * @param {string[]} [sectionIds]
   * @returns {Promise<Object>}
   */
  async #pruneOrphanedPersonalizationCartAttributes(cartResponse, sectionIds = this.#getCartSectionIds()) {
    if (!this.#isCartAttributePersonalizationEnabled()) return cartResponse;

    const attributeResponse = await pruneOrphanedLineItemPersonalizationFromCart({
      cart: cartResponse,
      existingAttributes: cartResponse.attributes,
      sectionIds,
      sectionsUrl: window.location.pathname,
    });

    return this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
  }

  /**
   * @param {Object} cart
   * @param {string[]} lineKeys
   * @returns {string[]}
   */
  #collectPersonalizationPidsFromLineKeys(cart, lineKeys) {
    return this.#collectPersonalizationPidsFromCartLines(
      cart,
      lineKeys.map((key) => ({ key }))
    );
  }

  /**
   * @param {Object} cartResponse
   * @param {any | null} attributeResponse
   * @returns {Object}
   */
  #mergePersonalizationAttributeResponse(cartResponse, attributeResponse) {
    if (!attributeResponse) return cartResponse;

    if (attributeResponse.sections) {
      cartResponse.sections = attributeResponse.sections;
    }

    if (attributeResponse.attributes) {
      cartResponse.attributes = attributeResponse.attributes;
    }

    return cartResponse;
  }

  /**
   * @param {string[]} removePids
   * @param {Record<string, { attributes: Array<{ key: string, value: string | { label: string, value: string } }> }>} [setEntries]
   * @param {Record<string, string>} [existingAttributes]
   * @param {string[]} [sectionIds]
   * @returns {Promise<Object | null>}
   */
  async #patchPersonalizationCartAttribute(removePids, setEntries = {}, existingAttributes, sectionIds = []) {
    if (!this.#isCartAttributePersonalizationEnabled()) return null;

    const response = await patchLineItemPersonalizationMapOnCart({
      removePids,
      setEntries,
      existingAttributes,
      sectionIds,
      sectionsUrl: window.location.pathname,
    });
    const expectsPatch = removePids.some((pid) => typeof pid === 'string' && pid.trim()) ||
      Object.keys(setEntries).some((pid) => pid.trim());
    if (expectsPatch && !response) {
      throw new Error('Failed to save gift personalization cart data');
    }
    return response;
  }

  /**
   * @param {string | undefined} raw
   * @returns {Record<string, string>}
   */
  #parseCartAttributePersonalizationPayload(raw) {
    return parsePersonalizationEntryPayload(raw);
  }

  /**
   * Records offer rejection and removes managed cart lines, deferring removal
   * while another cart sync is in flight to avoid conflicting updates.
   * @param {GwpConfig} config
   * @returns {Promise<void>}
   */
  async #rejectOffer(config) {
    if (this.#syncing || this.#preparingDiscountCode) {
      this.#pendingRejectConfigIds.add(config.id);
      return;
    }

    if (this.#isInteractiveCouponOffer(config)) {
      this.#clearPendingCouponOffer(config.couponCode || '');
    }
    this.#dismiss(config.id);
    this.#confirmedOfferAcceptIds.delete(config.id);

    await this.#removeManagedLinesForConfig(config);
  }

  /**
   * When an offer-box promo loses its auto-added gift while still qualified,
   * mirror that as an explicit rejection in the offer UI. When a coupon offer-box
   * promo loses its gift because the code was removed/invalidated (no longer
   * qualified), there's no need to mark it dismissed — losing qualification
   * already hides it — but the stale "accepted" flag must still be cleared so a
   * reapplied code presents a fresh selection experience instead of silently
   * re-adding the prior choice.
   * @param {GwpConfig} config
   * @param {Object} cart
   */
  #reflectOfferBoxGiftRemoval(config, cart) {
    if (!config.offerBoxOnly) return;

    if (!this.#isGiftQualified(cart, config)) {
      if (config.trigger === 'coupon') {
        this.#confirmedOfferAcceptIds.delete(config.id);
      }
      return;
    }

    this.#dismiss(config.id);
    this.#confirmedOfferAcceptIds.delete(config.id);
  }

  /**
   * @param {Object} cart
   * @param {Set<string>} previouslyTrackedGwpIds
   */
  #reflectRemovedOfferBoxAutoAddGifts(cart, previouslyTrackedGwpIds) {
    for (const config of this.#config) {
      if (!config.offerBoxOnly) continue;
      if (!previouslyTrackedGwpIds.has(config.id)) continue;
      if (this.#findManagedLines(cart, config).length > 0) continue;

      this.#reflectOfferBoxGiftRemoval(config, cart);
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async #flushPendingRejectRemovals() {
    if (this.#syncing || this.#preparingDiscountCode || this.#pendingRejectConfigIds.size === 0) return;

    while (this.#pendingRejectConfigIds.size > 0) {
      if (this.#syncing || this.#preparingDiscountCode) return;

      const configId = this.#pendingRejectConfigIds.values().next().value;
      if (!configId) break;

      this.#pendingRejectConfigIds.delete(configId);

      const config = this.#config.find((entry) => entry.id === configId);
      if (!config) continue;

      if (this.#isInteractiveCouponOffer(config)) {
        this.#clearPendingCouponOffer(config.couponCode || '');
      }
      this.#dismiss(config.id);
      this.#confirmedOfferAcceptIds.delete(config.id);

      await this.#removeManagedLinesForConfig(config);
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async #waitUntilNotSyncing() {
    while (this.#syncing) {
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
  }

  /**
   * @param {boolean} wasSyncing
   */
  #releaseSyncing(wasSyncing) {
    this.#syncing = wasSyncing;
    if (!this.#syncing) {
      void this.#flushPendingRejectRemovals();
    }
  }

  /**
   * @param {GwpConfig} config
   * @returns {Promise<Object | null>}
   */
  async #removeManagedLinesForConfig(config) {
    // #rejectOffer already recorded dismissal. Do not reapply it after awaiting
    // removal: the shopper may have accepted a new gift in the meantime.
    const wasSyncing = this.#syncing;
    this.#syncing = true;
    let dispatchedCartUpdate = false;

    try {
      const cart = await this.#fetchCart();
      const lines = this.#findManagedLines(cart, config);
      const giftWrapLines = this.#findGwpGiftWrapLines(cart, config);
      const pidsToRemove = this.#collectPersonalizationPidsFromCartLines(cart, lines);

      if (lines.length === 0 && giftWrapLines.length === 0) {
        this.#snapshotGwpState(cart);
        this.#updateGwpUI(cart);
        return cart;
      }

      this.#setPromotionsLoading(true);

      let cartResponse = cart;

      // Removing a parent line also removes its nested children. Remove the
      // managed gift lines first, then refresh before cleaning up any wraps
      // that remain (for example, legacy top-level GWP wrap lines). This keeps
      // us from submitting stale nested-line keys to /cart/change.js.
      if (lines.length > 0) {
        cartResponse = await this.#removeCartLines(
          lines.map((line) => line.key),
          cartResponse
        );
        cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);
      }

      const remainingGiftWrapLines = this.#findGwpGiftWrapLines(cartResponse, config);
      if (remainingGiftWrapLines.length > 0) {
        cartResponse = await this.#removeCartLines(
          remainingGiftWrapLines.map((line) => line.key),
          cartResponse
        );
        cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);
      }

      if (pidsToRemove.length > 0) {
        const attributeResponse = await this.#patchPersonalizationCartAttribute(
          pidsToRemove,
          {},
          cartResponse.attributes,
          this.#getCartSectionIds()
        );
        cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
      }

      cartResponse = await this.#pruneOrphanedPersonalizationCartAttributes(cartResponse);

      this.#snapshotGwpState(cartResponse);

      document.dispatchEvent(
        new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
          source: MANUAL_ADD_SOURCE,
          itemCount: cartResponse.item_count,
          sections: cartResponse.sections,
        })
      );
      dispatchedCartUpdate = true;

      this.#updateGwpUI(cartResponse);
      this.#scheduleOffersUIRefresh(cartResponse);
      return cartResponse;
    } catch (error) {
      console.error('Gift with purchase: failed to remove managed lines', error);
      return null;
    } finally {
      if (!dispatchedCartUpdate) {
        this.#setPromotionsLoading(false);
      }
      this.#releaseSyncing(wasSyncing);
    }
  }

  /**
   * Applies a deferred offer-box coupon after its selected gift line exists,
   * then verifies Shopify made the code applicable and the gift free.
   * @param {Object} cartWithGift
   * @param {GwpConfig} config
   * @param {Object} cartBeforeGiftChange
   * @param {number} expectedVariantId
   * @param {PendingCouponOffer} expectedPending
   * @param {{ preserveForFreeRush?: boolean }} [options]
   * @returns {Promise<{ cart: Object, success: boolean, message: string }>}
   */
  async #commitPendingCouponOffer(
    cartWithGift,
    config,
    cartBeforeGiftChange,
    expectedVariantId,
    expectedPending,
    options = {}
  ) {
    const pending = this.#getPendingCouponOffer();
    if (
      !pending ||
      pending.createdAt !== expectedPending.createdAt ||
      pending.cartToken !== expectedPending.cartToken ||
      cartWithGift?.token !== expectedPending.cartToken ||
      !pending.configIds.includes(config.id) ||
      normalizeDiscountCode(pending.code) !== normalizeDiscountCode(config.couponCode || '')
    ) {
      let cartResponse = await this.#revertGiftAdd(cartWithGift, config, cartBeforeGiftChange);
      cartResponse = await this.#restoreGiftSelectionSnapshot(
        cartResponse,
        config,
        cartBeforeGiftChange
      );
      return {
        cart: cartResponse,
        success: false,
        message: this.#getDiscountCodeRejectedMessage(),
      };
    }

    const normalized = normalizeDiscountCode(pending.code);
    // The shopper can change their cart after entering a deferred coupon and
    // before choosing a gift. Use the selection-time cart as the conflict
    // baseline so intentionally removed discounts are not restored and newly
    // qualified automatic gifts remain protected.
    const priorDiscounts = getStrictlyApplicableDiscountCodes(
      cartBeforeGiftChange.discount_codes
    ).filter((code) => normalizeDiscountCode(code) !== normalized);
    const protectedAutoAddGwpIds = this.#getProtectedAutoAddGwpConfigIds(
      cartBeforeGiftChange
    );
    const currentDiscounts = getStrictlyApplicableDiscountCodes(cartWithGift.discount_codes).filter(
      (code) => normalizeDiscountCode(code) !== normalized
    );
    const discountsToApply = [...new Set([...currentDiscounts, pending.code])];
    let cartResponse = cartWithGift;

    try {
      const sections = this.#getCartSectionIds();
      const applyResponse = await updateCartDiscountCodes(
        Theme.routes.cart_update_url,
        discountsToApply,
        { sections: sections.length > 0 ? sections : undefined }
      );
      cartResponse = await this.#fetchCart();
      if (applyResponse.sections) {
        cartResponse.sections = applyResponse.sections;
      } else if (cartWithGift.sections) {
        cartResponse.sections = cartWithGift.sections;
      }

      cartResponse = await this.#awaitGwpNativeApplication(
        cartResponse,
        config,
        8,
        expectedVariantId
      );

      const displacedPriorDiscounts = priorDiscounts.filter(
        (code) => !isDiscountStrictlyApplicable(cartResponse, code)
      );
      const displacedAutoAddGwps = this.#getDisplacedProtectedAutoAddGwps(
        cartResponse,
        protectedAutoAddGwpIds
      );
      const currentPending = this.#getPendingCouponOffer();
      const pendingUnchanged =
        currentPending?.createdAt === expectedPending.createdAt &&
        currentPending?.cartToken === expectedPending.cartToken &&
        cartResponse?.token === expectedPending.cartToken &&
        normalizeDiscountCode(currentPending.code) === normalized;
      const giftOutcome = this.#resolveGwpGiftAddOutcome(
        cartResponse,
        config,
        expectedVariantId
      );
      const codeApplicable = isDiscountStrictlyApplicable(cartResponse, pending.code);

      if (
        codeApplicable &&
        pendingUnchanged &&
        giftOutcome.success &&
        displacedPriorDiscounts.length === 0 &&
        displacedAutoAddGwps.length === 0
      ) {
        if (options.preserveForFreeRush) {
          this.#markPendingCouponRushValidated(pending, config);
        } else {
          this.#clearPendingCouponOffer(pending.code);
        }
        return { cart: cartResponse, success: true, message: '' };
      }

      const hasCombinationConflict =
        displacedPriorDiscounts.length > 0 || displacedAutoAddGwps.length > 0;
      const message = hasCombinationConflict
        ? this.#getDiscountCombinationErrorMessage()
        : !pendingUnchanged
          ? this.#getDiscountCodeRejectedMessage()
          : codeApplicable
            ? giftOutcome.message || this.#getGiftDiscountNotAppliedMessage()
            : this.#getDiscountCodeRejectedMessage();

      cartResponse = await this.#revertGiftAdd(cartResponse, config, cartBeforeGiftChange);
      cartResponse = await this.#restorePriorDiscountCodes(priorDiscounts);
      if (protectedAutoAddGwpIds.length > 0) {
        cartResponse = await this.#restoreProtectedAutoAddGwps(cartResponse, protectedAutoAddGwpIds);
      }
      cartResponse = await this.#restoreGiftSelectionSnapshot(
        cartResponse,
        config,
        cartBeforeGiftChange
      );
      this.#clearPendingCouponOffer(pending.code);
      this.#setOfferConflictError(config.id, message);
      return { cart: cartResponse, success: false, message };
    } catch (error) {
      console.error('Gift with purchase: failed to apply pending discount code', error);

      try {
        cartResponse = await this.#revertGiftAdd(cartResponse, config, cartBeforeGiftChange);
        cartResponse = await this.#restorePriorDiscountCodes(priorDiscounts);
        if (protectedAutoAddGwpIds.length > 0) {
          cartResponse = await this.#restoreProtectedAutoAddGwps(cartResponse, protectedAutoAddGwpIds);
        }
        cartResponse = await this.#restoreGiftSelectionSnapshot(
          cartResponse,
          config,
          cartBeforeGiftChange
        );
      } catch (rollbackError) {
        console.error('Gift with purchase: failed to roll back pending discount code', rollbackError);
      }

      const message = this.#getDiscountCodeRejectedMessage();
      this.#clearPendingCouponOffer(pending.code);
      this.#setOfferConflictError(config.id, message);
      return { cart: cartResponse, success: false, message };
    }
  }

  /**
   * Ensures this config's offer slot holds exactly the selected variant with the
   * supplied personalization properties. Any previously-selected managed lines
   * for the config are removed first, so changing product / personalization /
   * opt-out updates the existing gift in place rather than stacking lines.
   * @param {GwpConfig} config
   * @param {number} variantId
   * @param {Record<string, string>} properties
   * @param {{ onlyIfPresent?: boolean, ownsLock?: boolean, validateForFreeRush?: boolean }} [options] - When `onlyIfPresent`, skips the
   *   update if the config has no managed line in the cart yet (used for changes
   *   that should only edit an already-added gift, e.g. switching product image).
   *   When `ownsLock` is false, caller already acquired `#lockOfferUpdates()`.
   * @returns {Promise<Object | null>}
   */
  async #updateGiftSelection(config, variantId, properties, options = {}) {
    const ownsLock = options.ownsLock !== false;
    if (ownsLock && !this.#lockOfferUpdates()) return null;

    let dispatchedCartUpdate = false;
    let cartToSyncAfterUnlock = null;

    try {
      const cart = await this.#fetchCart();
      const pendingCouponOffer = this.#isPendingCouponOfferQualified(cart, config)
        ? this.#getPendingCouponOffer()
        : null;
      const isValidatedCouponRushSelection =
        config.trigger === 'coupon' &&
        properties[RUSH_ORDER_FEE_PROPERTY] === 'true' &&
        Boolean(pendingCouponOffer?.validatedRushConfigIds?.includes(config.id));
      const protectedCouponGwpIds = this.#getProtectedCouponGwpConfigIds(cart);
      const cartBeforeGiftChange = cart;
      const existingLines = this.#findManagedLines(cart, config);
      const existingMatchingLine = existingLines.find((line) => line.variantId === variantId);
      const existingPid = existingMatchingLine
        ? this.#resolvePersonalizationPidForLineKey(cart, existingMatchingLine.key)
        : '';
      const pidsToRemove = this.#collectPersonalizationPidsFromCartLines(cart, existingLines);

      if (options.onlyIfPresent && existingLines.length === 0) {
        return null;
      }

      // Preview Design URLs change often; skip cart/_pid churn when content is unchanged.
      if (
        this.#isGwpGiftSelectionUnchanged(cart, config, variantId, properties) &&
        !pendingCouponOffer
      ) {
        this.#clearOfferConflictError(config.id);
        this.#clearDismissal(config.id);
        if (this.#isInteractiveOffer(config)) {
          this.#confirmedOfferAcceptIds.add(config.id);
        }
        this.#snapshotGwpState(cart);
        this.#updateGwpUI(cart);
        return cart;
      }

      const sections = this.#getCartSectionIds();
      const canPatchPersonalizationInPlace =
        Boolean(existingMatchingLine) &&
        Boolean(existingPid) &&
        this.#isCartAttributePersonalizationEnabled() &&
        this.#hasGwpPersonalizationPayload(properties) &&
        this.#resolveGwpOfferOptedOut(
          cart,
          (cart.items || []).find((item) => item.key === existingMatchingLine?.key),
          this.#getConfigProduct(config, variantId)
        ) === this.#isGwpPersonalizationOptedOut(properties) &&
        properties[RUSH_ORDER_FEE_PROPERTY] !== 'true';

      // Same gift line: update the cart-attribute payload in place and keep `_pid`.
      if (canPatchPersonalizationInPlace) {
        this.#setPromotionsLoading(true);

        const personalizationPayload = this.#buildCartAttributePersonalizationPayload(properties);
        const attributeResponse = await this.#patchPersonalizationCartAttribute(
          [],
          { [existingPid]: personalizationPayload },
          cart.attributes,
          sections
        );
        let cartResponse = this.#mergePersonalizationAttributeResponse(cart, attributeResponse);
        let discountCodeChanged = false;

        if (pendingCouponOffer) {
          const pendingOutcome = await this.#commitPendingCouponOffer(
            cartResponse,
            config,
            cartBeforeGiftChange,
            variantId,
            pendingCouponOffer,
            { preserveForFreeRush: options.validateForFreeRush === true }
          );
          cartResponse = pendingOutcome.cart;
          discountCodeChanged = true;
          if (pendingOutcome.success && !options.validateForFreeRush) {
            cartToSyncAfterUnlock = cartResponse;
          }

          if (!pendingOutcome.success) {
            this.#snapshotGwpState(cartResponse);
            document.dispatchEvent(
              new DiscountUpdateEvent(cartResponse, SYNC_SOURCE, {
                discountCodeError: pendingOutcome.message,
              })
            );
            dispatchedCartUpdate = true;
            this.#updateGwpUI(cartResponse);
            this.#scheduleOffersUIRefresh(cartResponse);
            return null;
          }
        }

        this.#clearOfferConflictError(config.id);
        this.#clearDismissal(config.id);
        if (this.#isInteractiveOffer(config)) {
          this.#confirmedOfferAcceptIds.add(config.id);
        }
        this.#snapshotGwpState(cartResponse);

        if (discountCodeChanged) {
          document.dispatchEvent(new DiscountUpdateEvent(cartResponse, SYNC_SOURCE));
        } else {
          document.dispatchEvent(
            new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
              source: MANUAL_ADD_SOURCE,
              itemCount: cartResponse.item_count,
              sections: cartResponse.sections,
            })
          );
        }
        dispatchedCartUpdate = true;

        this.#updateGwpUI(cartResponse);
        this.#scheduleOffersUIRefresh(cartResponse);
        return cartResponse;
      }

      this.#setPromotionsLoading(true);

      let cartResponse = cart;

      for (const line of existingLines) {
        cartResponse = await this.#changeLineQuantity(line.key, 0, cartResponse);
      }

      let lineProperties = this.#withGwpSlotProperty(properties, config.id);
      /** @type {{ attributes: Array<{ key: string, value: string | { label: string, value: string } }> } | null} */
      let personalizationPayload = null;
      /** @type {string | null} */
      let personalizationPid = null;
      /** @type {string[]} */
      let attributeRemovePids = pidsToRemove;

      if (this.#isCartAttributePersonalizationEnabled() && this.#hasGwpPersonalizationPayload(properties)) {
        personalizationPayload = this.#buildCartAttributePersonalizationPayload(properties);
        // Reuse the existing pid when replacing the same gift so attributes don't churn.
        personalizationPid = existingPid || crypto.randomUUID();
        attributeRemovePids = pidsToRemove.filter((pid) => pid !== personalizationPid);
        lineProperties = this.#toCartAttributeLineProperties(lineProperties, personalizationPid);
      }

      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: lineProperties }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      if (!response.ok) {
        throw new Error(`Failed to add gift line: ${response.status}`);
      }

      const data = await response.json();
      if (data.status) {
        throw new Error(data.message || 'Failed to add gift with purchase');
      }

      cartResponse = await this.#fetchCart();
      cartResponse = this.#mergeAddedItemsIntoCart(cartResponse, data.items);
      if (data.sections) {
        cartResponse.sections = data.sections;
      }

      if (personalizationPid && personalizationPayload) {
        /** @type {Record<string, { attributes: Array<{ key: string, value: string | { label: string, value: string } }> }>} */
        const setEntries = { [personalizationPid]: personalizationPayload };
        const attributeResponse = await this.#patchPersonalizationCartAttribute(
          attributeRemovePids,
          setEntries,
          cartResponse.attributes,
          sections
        );
        cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
      } else if (attributeRemovePids.length > 0) {
        const attributeResponse = await this.#patchPersonalizationCartAttribute(
          attributeRemovePids,
          {},
          cartResponse.attributes,
          sections
        );
        cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
      }

      if (existingLines.length > 0) {
        cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);
        cartResponse = await this.#pruneOrphanedPersonalizationCartAttributes(cartResponse, sections);
      }

      let discountCodeChanged = false;
      let outcome;

      if (isValidatedCouponRushSelection && pendingCouponOffer) {
        cartResponse = await this.#removeCouponCodeForValidatedRush(
          cartResponse,
          pendingCouponOffer
        );
        discountCodeChanged = true;
        cartResponse = await this.#awaitGwpNativeApplication(
          cartResponse,
          config,
          8,
          variantId
        );
        outcome = {
          success: this.#isGwpSelectionApplied(cartResponse, config, variantId),
          message: this.#getGiftDiscountNotAppliedMessage(),
        };
      } else if (pendingCouponOffer) {
        const pendingOutcome = await this.#commitPendingCouponOffer(
          cartResponse,
          config,
          cartBeforeGiftChange,
          variantId,
          pendingCouponOffer,
          { preserveForFreeRush: options.validateForFreeRush === true }
        );
        cartResponse = pendingOutcome.cart;
        discountCodeChanged = true;
        if (pendingOutcome.success && !options.validateForFreeRush) {
          cartToSyncAfterUnlock = cartResponse;
        }
        outcome = { success: pendingOutcome.success, message: pendingOutcome.message };
      } else {
        cartResponse = await this.#awaitGwpNativeApplication(cartResponse, config, 8, variantId);
        const verifyOptions = { protectedCouponGwpIds };
        outcome = this.#resolveGwpGiftAddOutcome(
          cartResponse,
          config,
          variantId,
          verifyOptions
        );
      }
      if (!outcome.success) {
        if (isValidatedCouponRushSelection) {
          cartResponse = await this.#revertGiftAdd(
            cartResponse,
            config,
            cartBeforeGiftChange
          );
          cartResponse = await this.#restorePriorDiscountCodes(
            getStrictlyApplicableDiscountCodes(cartBeforeGiftChange.discount_codes)
          );
          this.#clearPendingCouponOffer(config.couponCode || '');
          this.#setOfferConflictError(config.id, outcome.message);
        } else if (!discountCodeChanged) {
          cartResponse = await this.#revertSubtotalAddForProtectedCoupons(
            cartResponse,
            config,
            cartBeforeGiftChange,
            protectedCouponGwpIds,
            outcome.message,
            { reflectOfferBoxRejection: false }
          );
        }
        this.#snapshotGwpState(cartResponse);

        if (discountCodeChanged) {
          document.dispatchEvent(
            new DiscountUpdateEvent(cartResponse, SYNC_SOURCE, {
              discountCodeError: outcome.message,
            })
          );
        } else {
          document.dispatchEvent(
            new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
              source: MANUAL_ADD_SOURCE,
              itemCount: cartResponse.item_count,
              sections: cartResponse.sections,
            })
          );
        }
        dispatchedCartUpdate = true;

        this.#updateGwpUI(cartResponse);
        this.#scheduleOffersUIRefresh(cartResponse);
        return null;
      }

      this.#clearOfferConflictError(config.id);
      this.#clearDismissal(config.id);
      if (this.#isInteractiveOffer(config)) {
        this.#confirmedOfferAcceptIds.add(config.id);
      }
      this.#snapshotGwpState(cartResponse);

      if (discountCodeChanged) {
        document.dispatchEvent(new DiscountUpdateEvent(cartResponse, SYNC_SOURCE));
      } else {
        document.dispatchEvent(
          new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
            source: MANUAL_ADD_SOURCE,
            itemCount: cartResponse.item_count,
            sections: cartResponse.sections,
          })
        );
      }
      dispatchedCartUpdate = true;

      this.#updateGwpUI(cartResponse);
      this.#scheduleOffersUIRefresh(cartResponse);
      return cartResponse;
    } catch (error) {
      console.error('Gift with purchase: failed to update gift selection', error);
      return null;
    } finally {
      if (!dispatchedCartUpdate) {
        this.#unlockOfferUpdates(true);
      } else {
        this.#syncing = false;
        void this.#flushPendingRejectRemovals().then(() => {
          if (cartToSyncAfterUnlock) void this.#sync(cartToSyncAfterUnlock);
        });
        this.#schedulePromotionsLoadingFallbackClear();
      }
    }
  }

  /**
   * @param {HTMLInputElement} addGwpInput
   */
  #handleAddGwpProductChoice = async (addGwpInput) => {
    if (!addGwpInput.checked) return;

    const offerItem = this.#resolveOfferItemFromTarget(addGwpInput);
    if (!offerItem) return;

    const { item, config } = offerItem;

    if (addGwpInput.value === 'rejectGWPProduct') {
      this.#pendingRushOfferIds.delete(config.id);
      this.#pendingOfferEdits.delete(config.id);
      delete item.dataset.gwpOfferEditing;
      this.#clearOfferConflictError(config.id);
      this.#clearUserConfirmedGiftAccept(item);
      this.#syncOfferChoiceVisibility(item, 'reject');
      await this.#rejectOffer(config);
      return;
    }

    // Record the new choice even if cart synchronization delays its save. The
    // previously saved rush/gift must no longer satisfy checkout in the meantime.
    if (addGwpInput.value === 'acceptGWPGProduct') {
      this.#markUserConfirmedGiftAccept(item);
      this.#markOfferEditing(item);
    } else if (addGwpInput.value === ACCEPT_GWP_RUSH_VALUE) {
      this.#pendingOfferEdits.delete(config.id);
      this.#pendingRushOfferIds.add(config.id);
      delete item.dataset.gwpOfferEditing;
      this.#markUserConfirmedGiftAccept(item);
      this.#clearOfferConflictError(config.id);
      this.#syncOfferChoiceVisibility(item, 'rush');
      this.#clearDismissal(config.id);
      if (this.#isGatedCheckoutEnabled()) {
        this.#rememberGatedAcceleratedCheckoutBlocked(true);
      }

      await this.#waitForPendingRushUpdate(config.id);
      if (!this.#pendingRushOfferIds.has(config.id)) return;
    }

    if (this.#syncing) return;

    const currentItem = document.querySelector(
      `[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`
    );
    const context = currentItem instanceof HTMLElement
      ? this.#buildOfferContext(currentItem, config)
      : this.#resolveOfferContext(addGwpInput);
    if (!context) return;

    if (addGwpInput.value === ACCEPT_GWP_RUSH_VALUE) {
      delete context.item.dataset.gwpOfferEditing;
      this.#markUserConfirmedGiftAccept(context.item);
      this.#clearOfferConflictError(context.config.id);
      this.#syncOfferChoiceVisibility(context.item, 'rush');

      if (this.#syncing) return;

      this.#clearDismissal(context.config.id);

      await this.#removePaidRushFeeIfPresent();

      const rush = this.#freeRushOrder;
      if (!rush) return;

      if (this.#isInteractiveCouponOffer(context.config)) {
        const validationVariantId = this.#resolveFreeRushValidationVariantId(context);
        if (!validationVariantId) return;

        const validationCart = await this.#updateGiftSelection(
          context.config,
          validationVariantId,
          {},
          { validateForFreeRush: true }
        );
        if (!validationCart) return;

        if (!this.#isValidatedCouponRushOffer(validationCart, context.config)) {
          const sourcePending = this.#getPendingCouponOffer();
          const resolvedSourceConfigs = sourcePending?.configIds
            .map((configId) => this.#config.find((entry) => entry.id === configId))
            .filter(Boolean) || [];
          const sourceConfigs = resolvedSourceConfigs.length > 0
            ? resolvedSourceConfigs
            : [context.config];

          if (
            !this.#isCouponActive(validationCart, context.config.couponCode || '') ||
            !this.#isGwpAutomaticDiscountActive(validationCart, context.config) ||
            !this.#setPendingCouponOffer(
              context.config.couponCode || '',
              sourceConfigs,
              validationCart,
              { validatedRushConfigIds: [context.config.id] }
            )
          ) {
            return;
          }
        }
      }

      const updatedCart = await this.#updateGiftSelection(context.config, rush.variantId, {
        [RUSH_ORDER_FEE_PROPERTY]: 'true',
      });
      if (updatedCart && this.#resolveOfferSelection(updatedCart, context.config)?.choice === 'rush') {
        this.#pendingRushOfferIds.delete(context.config.id);
      }
      return;
    }

    if (addGwpInput.value === 'acceptGWPGProduct') {
      this.#markUserConfirmedGiftAccept(context.item);
      this.#clearOfferConflictError(context.config.id);
      this.#syncOfferChoiceVisibility(context.item, 'gift');

      if (this.#syncing) return;

      await this.#commitAcceptedOfferGift(context, { reportValidity: true });
    }
  };

  /**
   * Adds the selected offer-box gift when accept is confirmed and selection is complete.
   * @param {{ item: HTMLElement, config: GwpConfig, selectedVariantId: number, selectedProductId: number }} context
   * @param {{ reportValidity?: boolean }} [options]
   * @returns {Promise<Object | null>}
   */
  async #commitAcceptedOfferGift(context, options = {}) {
    this.#syncOfferPersonalizationForSelection(
      context.item,
      context.config,
      context.selectedVariantId,
      context.selectedProductId
    );

    if (!this.#isOfferVariantSelectionComplete(context)) {
      return null;
    }

    if (!this.#isGwpOfferPersonalizationValid(context, { reportValidity: options.reportValidity === true })) {
      return null;
    }

    this.#clearDismissal(context.config.id);

    const properties = await this.#buildOfferProperties(context);
    return this.#updateGiftSelection(context.config, context.selectedVariantId, properties);
  }

  /**
   * @param {Event} event
   */
  #onOffersInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('.js-personalization-text-input, [data-personalization-text-input]')) return;
    const item = target.closest('[data-gwp-offer-item]');
    if (!(item instanceof HTMLElement)) return;

    this.#markOfferEditing(item);

    if (this.#offerInputDebounceTimer) {
      clearTimeout(this.#offerInputDebounceTimer);
    }

    const personalization = target.closest('personalization-component');
    if (personalization && typeof personalization.validatePersonalizationInput === 'function') {
      personalization.validatePersonalizationInput({ enforceMinimum: true });
    }

    this.#offerInputDebounceTimer = setTimeout(async () => {
      this.#offerInputDebounceTimer = null;

      const context = this.#resolveOfferContext(target);
      if (!context) return;

      if (!this.#isGwpOfferPersonalizationValid(context)) return;
      if (!this.#isOfferVariantSelectionComplete(context)) return;
      if (!this.#confirmGiftAcceptFromInteraction(context.item)) return;
      if (this.#syncing) return;

      const acceptInput = context.item.querySelector('input[value="acceptGWPGProduct"]');
      if (acceptInput instanceof HTMLInputElement && acceptInput.checked) {
        this.#clearDismissal(context.config.id);
      }

      const properties = await this.#buildOfferProperties(context);
      if (Object.keys(properties).length === 0) return;

      await this.#updateGiftSelection(context.config, context.selectedVariantId, properties);
    }, 400);
  };

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  #resolveGwpProductInputFromEvent(event) {
    const path = event.composedPath();

    const directInput = path.find(
      (element) =>
        element instanceof HTMLInputElement &&
        element.type === 'radio' &&
        element.name.startsWith('gwpProduct-')
    );
    if (directInput instanceof HTMLInputElement) return directInput;

    for (const element of path) {
      if (!(element instanceof HTMLLabelElement)) continue;

      const control = element.control;
      if (
        control instanceof HTMLInputElement &&
        control.type === 'radio' &&
        control.name.startsWith('gwpProduct-')
      ) {
        return control;
      }
    }

    return null;
  }

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  #resolveGwpAcceptInputFromEvent(event) {
    const path = event.composedPath();

    const directInput = path.find(
      (element) =>
        element instanceof HTMLInputElement &&
        element.type === 'radio' &&
        element.name.startsWith('addGWPProduct-') &&
        element.value === 'acceptGWPGProduct'
    );
    if (directInput instanceof HTMLInputElement) return directInput;

    for (const element of path) {
      if (!(element instanceof HTMLLabelElement)) continue;

      const control = element.control;
      if (
        control instanceof HTMLInputElement &&
        control.type === 'radio' &&
        control.name.startsWith('addGWPProduct-') &&
        control.value === 'acceptGWPGProduct'
      ) {
        return control;
      }
    }

    return null;
  }

  /**
   * @param {HTMLInputElement} productInput
   */
  #syncOfferVariantOptionsForProductInput(productInput) {
    const offerItem = this.#resolveOfferItemFromGwpScopedInput(productInput);
    if (!offerItem) return;

    const selectedProductIdString = productInput.value.trim();
    if (!selectedProductIdString) return;

    this.#syncOfferVariantOptionsVisibility(
      offerItem.item,
      offerItem.config,
      selectedProductIdString
    );
  }

  /**
   * @param {HTMLInputElement} productInput
   */
  #handleGwpProductSelection = async (productInput) => {
    if (!(productInput instanceof HTMLInputElement) || !productInput.checked) return;

    const offerItem = this.#resolveOfferItemFromGwpScopedInput(productInput);
    if (!offerItem) return;

    const { item, config } = offerItem;
    const selectedProductIdString = productInput.value.trim();
    if (!selectedProductIdString) return;

    const selectedProductId = Number(selectedProductIdString);
    if (!Number.isFinite(selectedProductId) || selectedProductId <= 0) return;

    // Update variant fieldsets immediately so UI tracks the selected product
    // even when cart fetch/syncing is in flight.
    this.#syncOfferVariantOptionsVisibility(item, config, selectedProductIdString);

    const selectionKey = `${config.id}:${selectedProductIdString}`;
    const now = Date.now();
    if (
      this.#lastGwpProductSelectionKey === selectionKey &&
      now - this.#lastGwpProductSelectionAt < 150
    ) {
      return;
    }
    this.#lastGwpProductSelectionKey = selectionKey;
    this.#lastGwpProductSelectionAt = now;

    this.#markOfferEditing(item);
    if (!this.#confirmGiftAcceptFromInteraction(item) || this.#syncing) return;

    const context = this.#buildOfferContext(item, config, selectedProductId);
    if (!context) return;

    if (!this.#isOfferVariantSelectionComplete(context)) {
      return;
    }

    if (!this.#lockOfferUpdates()) return;

    try {
      const cart = await this.#fetchCart();
      const cartSelection = this.#resolveOfferSelection(cart, config);

      this.#syncOfferPersonalizationForSelection(
        item,
        config,
        context.selectedVariantId,
        selectedProductIdString
      );

      if (!this.#isGwpOfferPersonalizationValid(context, { reportValidity: true })) {
        this.#restoreOfferUiFromCartSelection(item, config, cartSelection);
        return;
      }

      const properties = await this.#buildOfferProperties(context);
      await this.#updateGiftSelection(config, context.selectedVariantId, properties, {
        onlyIfPresent: !this.#hasUserConfirmedGiftAccept(item),
        ownsLock: false,
      });
    } finally {
      if (this.#syncing) {
        this.#unlockOfferUpdates(true);
      }
    }
  };

  /**
   * @param {Event} event
   */
  #onOffersClick = (event) => {
    const productInput = this.#resolveGwpProductInputFromEvent(event);
    if (productInput) {
      // Sync variant fieldsets on pointer down so UI updates even if change never
      // reaches document (e.g. label clicks before the radio is checked).
      this.#syncOfferVariantOptionsForProductInput(productInput);

      queueMicrotask(() => {
        if (productInput.checked) {
          void this.#handleGwpProductSelection(productInput);
        }
      });
      return;
    }

    const acceptInput = this.#resolveGwpAcceptInputFromEvent(event);
    if (!acceptInput) return;

    // FREE GIFT is checked by default for preview; `change` does not fire when the
    // shopper clicks the already-selected accept radio, so confirm on click instead.
    queueMicrotask(() => {
      if (!acceptInput.checked) return;
      void this.#handleAddGwpProductChoice(acceptInput);
    });
  };

  /**
   * @param {Event} event
   */
  #onOffersChange = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.type === 'radio' && target.name.startsWith('gwpProduct-')) {
      if (!target.checked) return;
      await this.#handleGwpProductSelection(target);
      return;
    }

    if (!target.closest('[data-gwp-offer-item]') && !this.#resolveOfferItemFromGwpScopedInput(target)) {
      return;
    }

    if (target.type === 'radio' && target.name.startsWith('addGWPProduct')) {
      if (!target.checked) return;

      const offerItem = this.#resolveOfferItemFromTarget(target);
      if (offerItem) {
        if (target.value === 'rejectGWPProduct') {
          this.#clearOfferConflictError(offerItem.config.id);
          this.#syncOfferChoiceVisibility(offerItem.item, 'reject');
        } else if (target.value === ACCEPT_GWP_RUSH_VALUE) {
          this.#syncOfferChoiceVisibility(offerItem.item, 'rush');
        } else if (target.value === 'acceptGWPGProduct') {
          this.#syncOfferChoiceVisibility(offerItem.item, 'gift');
        }
      }

      await this.#handleAddGwpProductChoice(target);
      return;
    }

    // Switching the selected variant option within the chosen gift product.
    if (target.type === 'radio' && target.name.startsWith('gwpVariant-')) {
      if (!target.checked) return;

      const context = this.#resolveOfferContext(target);
      if (!context) return;

      const selectedVariantId = Number(target.value);
      if (!Number.isFinite(selectedVariantId) || selectedVariantId <= 0) return;

      this.#markOfferEditing(context.item);

      context.selectedVariantId = selectedVariantId;

      const product = this.#getConfigProductById(context.config, context.selectedProductId);
      if (product) {
        this.#updateVariantOptionLegend(context.item, product, selectedVariantId);
      }

      if (!this.#isOfferVariantSelectionComplete(context)) {
        return;
      }

      if (!this.#confirmGiftAcceptFromInteraction(context.item)) return;
      if (this.#syncing) return;

      if (!this.#lockOfferUpdates()) return;

      try {
        const cart = await this.#fetchCart();
        const cartSelection = this.#resolveOfferSelection(cart, context.config);

        this.#syncOfferPersonalizationForSelection(
          context.item,
          context.config,
          selectedVariantId,
          context.selectedProductId
        );

        if (!this.#isGwpOfferPersonalizationValid(context, { reportValidity: true })) {
          this.#restoreOfferUiFromCartSelection(context.item, context.config, cartSelection);
          return;
        }

        const properties = await this.#buildOfferProperties(context);
        await this.#updateGiftSelection(context.config, selectedVariantId, properties, {
          onlyIfPresent: !this.#hasUserConfirmedGiftAccept(context.item),
          ownsLock: false,
        });
      } finally {
        if (this.#syncing) {
          this.#unlockOfferUpdates(true);
        }
      }
    }

    // Explicit opt-out persists a hidden line-item flag (not inferred from empty text).
    if (target.matches('[data-personalize-opt-out-toggle]')) {
      const context = this.#resolveOfferContext(target);
      if (!context) return;
      this.#markOfferEditing(context.item);
      if (!this.#confirmGiftAcceptFromInteraction(context.item)) return;
      if (!this.#isOfferVariantSelectionComplete(context)) return;
      if (this.#syncing) return;

      if (!target.checked) {
        await this.#commitAcceptedOfferGift(context, { reportValidity: true });
        return;
      }

      const acceptInput = context.item.querySelector('input[value="acceptGWPGProduct"]');
      if (acceptInput instanceof HTMLInputElement && acceptInput.checked) {
        this.#clearDismissal(context.config.id);
      }

      await this.#updateGiftSelection(
        context.config,
        context.selectedVariantId,
        this.#buildGwpOptOutProperties()
      );
    }
  };

  /**
   * Validates the offer-box monogram field using the embedded personalization component.
   * Skips validation when the gift is not personalizable or the shopper opted out.
   * @param {{ item: HTMLElement, config: GwpConfig, selectedVariantId: number }} context
   * @param {{ reportValidity?: boolean }} [options]
   * @returns {boolean}
   */
  #isGwpOfferPersonalizationValid(context, options = {}) {
    const product = this.#resolveOfferProduct(
      context.config,
      context.selectedProductId,
      context.selectedVariantId
    );
    if (!this.#resolveGwpPersonalizableDesign(product)) return true;

    if (this.#isGwpOfferPersonalizationOptedOut(context.item)) return true;

    const personalization = context.item.querySelector('personalization-component[data-gwp-personalization]');
    if (
      !(personalization instanceof HTMLElement) ||
      typeof personalization.validatePersonalizationInput !== 'function'
    ) {
      return true;
    }

    return personalization.validatePersonalizationInput({
      enforceMinimum: true,
      reportValidity: options.reportValidity === true,
    });
  }

  /**
   * Builds personalization properties for the currently-selected offer product,
   * reading the live text input. Returns no properties when the selected product
   * does not have a usable personalization design.
   * @param {{ item: HTMLElement, config: GwpConfig, selectedVariantId: number }} context
   * @returns {Promise<Record<string, string>>}
   */
  async #buildOfferProperties(context) {
    if (this.#isGwpOfferPersonalizationOptedOut(context.item)) {
      return this.#buildGwpOptOutProperties();
    }

    const product = this.#resolveOfferProduct(
      context.config,
      context.selectedProductId,
      context.selectedVariantId
    );
    const textInput = context.item.querySelector(
      '.js-personalization-text-input, [data-personalization-text-input]'
    );
    const text = textInput instanceof HTMLInputElement ? textInput.value : '';
    const previewCartImageUrl = await this.#resolveGwpPreviewCartImageUrl(context.item);
    return this.#buildGwpPersonalizationProperties(product, text, { previewCartImageUrl });
  }

  /**
   * Serializes discount preparation so the most recent submitted code cannot
   * be overwritten by an older fetch completing out of order.
   * @param {string} discountCode
   * @returns {Promise<boolean | { deferred: true, code: string } | { rejected: true, message: string }>}
   */
  prepareForDiscountCode = async (discountCode) => {
    while (this.#preparingDiscountCode) {
      await new Promise((resolve) => setTimeout(resolve, 16));
    }

    this.#preparingDiscountCode = true;

    try {
      await this.#waitUntilNotSyncing();
      return await this.#prepareDiscountCode(discountCode);
    } finally {
      this.#preparingDiscountCode = false;
      void this.#flushPendingRejectRemovals();
    }
  };

  /**
   * Adds coupon-triggered gifts before a discount code is applied so eligibility
   * checks that require the gift variant in cart can succeed. Native combination
   * is verified after the code apply in `afterDiscountCodeApplied`.
   * @param {string} discountCode
   * @returns {Promise<boolean | { deferred: true, code: string } | { rejected: true, message: string }>} False when gift lines could not be added.
   */
  async #prepareDiscountCode(discountCode) {
    const normalized = normalizeDiscountCode(discountCode);
    if (!normalized) return true;

    this.#lastDiscountConflictMessage = null;

    /** @type {Object} */
    let cart;

    try {
      cart = await this.#fetchCart();

      const existingPending = this.#getPendingCouponOffer();
      if (existingPending && normalizeDiscountCode(existingPending.code) !== normalized) {
        const itemCountBeforePendingChange = cart.item_count;
        cart = await this.#removeValidatedCouponRushLines(cart, existingPending);
        this.#clearPendingCouponOffer(existingPending.code);

        if (cart.item_count !== itemCountBeforePendingChange) {
          this.#snapshotGwpState(cart);
          document.dispatchEvent(
            new CartUpdateEvent(cart, SYNC_SOURCE, {
              source: SYNC_SOURCE,
              itemCount: cart.item_count,
              sections: cart.sections,
            })
          );
        }
      }

      this.#pendingDiscountApplySnapshots.set(normalized, {
        priorDiscounts: getStrictlyApplicableDiscountCodes(cart.discount_codes).filter(
          (code) => normalizeDiscountCode(code) !== normalized
        ),
        protectedAutoAddGwpIds: this.#getProtectedAutoAddGwpConfigIds(cart),
      });
    } catch (error) {
      console.error('Gift with purchase: failed to snapshot discounts before apply', error);
      return false;
    }

    if (this.#config.length === 0) return true;

    const matchingConfigs = this.#config.filter(
      (config) =>
        config.trigger === 'coupon' &&
        normalizeDiscountCode(config.couponCode || '') === normalized
    );

    if (matchingConfigs.length === 0) return true;

    const pendingOfferConfigs = matchingConfigs.filter(
      (config) =>
        this.#isInteractiveCouponOffer(config) &&
        this.#getCouponOfferMerchandiseSubtotalCents(cart) >= (config.couponThresholdCents ?? 0)
    );

    if (pendingOfferConfigs.length > 0) {
      if (!this.#setPendingCouponOffer(discountCode, pendingOfferConfigs, cart)) {
        this.#pendingDiscountApplySnapshots.delete(normalized);
        return false;
      }
      pendingOfferConfigs.forEach((config) => {
        this.#clearDismissal(config.id);
        this.#clearOfferConflictError(config.id);
      });
      this.#updateGwpUI(cart);
      this.#scheduleDeferredOffersUIReapply(cart);
      this.#revealCouponOffer(pendingOfferConfigs);
      return { deferred: true, code: discountCode.trim() };
    }

    const interactiveConfigs = matchingConfigs.filter((config) =>
      this.#isInteractiveCouponOffer(config)
    );
    if (
      interactiveConfigs.length > 0 &&
      interactiveConfigs.length === matchingConfigs.length
    ) {
      const merchandiseSubtotalCents = this.#getCouponOfferMerchandiseSubtotalCents(cart);
      const nextConfig = interactiveConfigs
        .filter((config) => merchandiseSubtotalCents < (config.couponThresholdCents ?? 0))
        .sort(
          (configA, configB) =>
            (configA.couponThresholdCents ?? 0) - (configB.couponThresholdCents ?? 0)
        )[0];

      if (nextConfig) {
        this.#pendingDiscountApplySnapshots.delete(normalized);
        return {
          rejected: true,
          message: this.#resolveCouponThresholdErrorMessage(
            nextConfig,
            cart,
            merchandiseSubtotalCents
          ),
        };
      }
    }

    this.#pendingCouponCodes.add(normalized);
    matchingConfigs.forEach((config) => {
      this.#clearDismissal(config.id);
      this.#clearOfferConflictError(config.id);
    });

    const wasSyncing = this.#syncing;
    this.#syncing = true;

    try {
      /** @type {Array<{ variantId: number, configId: string }>} */
      const linesToAdd = [];

      for (const config of matchingConfigs) {
        if (config.offerBoxOnly) continue;

        const existingVariantIds = new Set(
          this.#findManagedLines(cart, config).map((line) => line.variantId)
        );
        for (const variantId of this.#getConfigVariantIds(config)) {
          if (!existingVariantIds.has(variantId)) {
            linesToAdd.push({ variantId, configId: config.id });
          }
        }
      }

      if (linesToAdd.length === 0) return true;

      const cartResponse = await this.#addGiftLines(linesToAdd);
      this.#snapshotGwpState(cartResponse);

      document.dispatchEvent(
        new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
          source: SYNC_SOURCE,
          itemCount: cartResponse.item_count,
          sections: cartResponse.sections,
        })
      );

      this.#updateGwpUI(cartResponse);
      this.#scheduleOffersUIRefresh(cartResponse);
      return true;
    } catch (error) {
      console.error('Gift with purchase: failed to prepare gifts for discount code', error);
      this.#lastDiscountConflictMessage = this.#getConflictErrorMessage();
      return false;
    } finally {
      this.#releaseSyncing(wasSyncing);
    }
  }

  /**
   * Clears pending coupon state and syncs coupon-triggered gifts after apply.
   * When Shopify keeps the code applicable, verifies the gift; otherwise sync
   * removes unqualified gift lines. On native conflict, reverts the gift and
   * exposes an error via `consumeDiscountConflictMessage`.
   * @param {string} discountCode
   * @returns {Promise<void>}
   */
  afterDiscountCodeApplied = async (discountCode) => {
    const normalized = normalizeDiscountCode(discountCode);
    if (!normalized) return;

    await this.#waitUntilNotSyncing();

    this.#pendingCouponCodes.delete(normalized);

    const snapshot = this.#pendingDiscountApplySnapshots.get(normalized) ?? {
      priorDiscounts: [],
      protectedAutoAddGwpIds: [],
    };
    this.#pendingDiscountApplySnapshots.delete(normalized);
    const { priorDiscounts, protectedAutoAddGwpIds } = snapshot;

    const matchingConfigs = this.#config.filter(
      (config) =>
        config.trigger === 'coupon' &&
        normalizeDiscountCode(config.couponCode || '') === normalized
    );

    let revertedCart = null;

    try {
      let cart = await this.#fetchCart();
      const codeApplicable = isDiscountStrictlyApplicable(cart, discountCode);
      const displacedPriorDiscounts = priorDiscounts.filter(
        (code) => !isDiscountStrictlyApplicable(cart, code)
      );

      if (matchingConfigs.length === 0) {
        if (protectedAutoAddGwpIds.length > 0) {
          cart = await this.#triggerCartRecalculation(cart);
        }

        const displacedGwpCoupons = this.#getDisplacedCouponGwpCodes(priorDiscounts, displacedPriorDiscounts);
        const displacedAutoAddGwps = this.#getDisplacedProtectedAutoAddGwps(cart, protectedAutoAddGwpIds);
        const shouldRevertDiscountForGwp =
          displacedGwpCoupons.length > 0 || displacedAutoAddGwps.length > 0;

        if (shouldRevertDiscountForGwp) {
          cart = await this.#restorePriorDiscountCodes(priorDiscounts);
          cart = await this.#restoreProtectedAutoAddGwps(cart, protectedAutoAddGwpIds);

          this.#snapshotGwpState(cart);

          if (codeApplicable || displacedAutoAddGwps.length > 0) {
            this.#lastDiscountConflictMessage = this.#getDiscountCombinationErrorMessage();
          }

          this.#lastDiscountConflictCart = cart;
          revertedCart = cart;
          this.#updateGwpUI(cart);
          this.#scheduleOffersUIRefresh(cart);
          return;
        }

        await this.#sync(cart);
        return;
      }

      if (protectedAutoAddGwpIds.length > 0) {
        cart = await this.#triggerCartRecalculation(cart);
      }

      const displacedProtectedSubtotal = this.#getDisplacedProtectedAutoAddGwps(
        cart,
        protectedAutoAddGwpIds
      );
      const couponConfigsWithGift = matchingConfigs.filter(
        (config) => this.#hasManagedGiftLineInCart(cart, config)
      );
      const failedCouponGifts = couponConfigsWithGift.filter(
        (config) => !this.#isGwpAutomaticDiscountActive(cart, config)
      );
      const shouldRevertCouponForProtectedSubtotal =
        protectedAutoAddGwpIds.length > 0 &&
        (displacedProtectedSubtotal.length > 0 ||
          (codeApplicable && failedCouponGifts.length > 0));

      if (shouldRevertCouponForProtectedSubtotal) {
        cart = await this.#revertCouponForProtectedSubtotalGwps(
          cart,
          matchingConfigs,
          priorDiscounts,
          protectedAutoAddGwpIds
        );

        this.#snapshotGwpState(cart);
        this.#lastDiscountConflictMessage = this.#getDiscountCombinationErrorMessage();
        this.#lastDiscountConflictCart = cart;
        revertedCart = cart;
        this.#updateGwpUI(cart);
        this.#scheduleOffersUIRefresh(cart);
        return;
      }

      const configsWithGift = matchingConfigs.filter(
        (config) => this.#findManagedLines(cart, config).length > 0
      );
      const nativeGiftFailures = configsWithGift.filter(
        (config) => !this.#isGwpAutomaticDiscountActive(cart, config)
      );

      const hasConflict =
        displacedPriorDiscounts.length > 0 ||
        nativeGiftFailures.length > 0 ||
        (!codeApplicable && configsWithGift.length > 0);

      if (hasConflict) {
        cart = await this.#revertCouponGwpAttempt(cart, matchingConfigs, priorDiscounts);

        if (protectedAutoAddGwpIds.length > 0) {
          cart = await this.#restoreProtectedAutoAddGwps(cart, protectedAutoAddGwpIds);
          this.#lastDiscountConflictMessage = this.#getDiscountCombinationErrorMessage();
        } else {
          for (const config of matchingConfigs) {
            this.#setOfferConflictError(config.id, this.#getConflictErrorMessage());
          }

          this.#lastDiscountConflictMessage = this.#getConflictErrorMessage();
        }

        this.#lastDiscountConflictCart = cart;
        revertedCart = cart;
        this.#snapshotGwpState(cart);

        this.#updateGwpUI(cart);
        this.#scheduleOffersUIRefresh(cart);
        return;
      }

      matchingConfigs.forEach((config) => this.#clearOfferConflictError(config.id));
      await this.#sync(cart);
    } catch (error) {
      console.error('Gift with purchase: failed after discount code applied', error);
    } finally {
      if (revertedCart) {
        document.dispatchEvent(
          new CartUpdateEvent(revertedCart, SYNC_SOURCE, {
            source: SYNC_SOURCE,
            itemCount: revertedCart.item_count,
          })
        );
      }
    }
  };

  /**
   * @returns {string | null}
   */
  consumeDiscountConflictMessage = () => {
    const message = this.#lastDiscountConflictMessage;
    this.#lastDiscountConflictMessage = null;
    return message;
  };

  /**
   * Cart payload after a coupon GWP conflict was reverted (for section morph).
   * @returns {Object | null}
   */
  consumeDiscountConflictCart = () => {
    const cart = this.#lastDiscountConflictCart;
    this.#lastDiscountConflictCart = null;
    return cart;
  };

  /**
   * @param {string[]} priorDiscounts
   * @param {string[]} displacedPriorDiscounts
   * @returns {string[]}
   */
  #getDisplacedCouponGwpCodes(priorDiscounts, displacedPriorDiscounts) {
    return displacedPriorDiscounts.filter((code) =>
      this.#config.some(
        (config) =>
          config.trigger === 'coupon' &&
          normalizeDiscountCode(config.couponCode || '') === normalizeDiscountCode(code) &&
          priorDiscounts.some(
            (priorCode) => normalizeDiscountCode(priorCode) === normalizeDiscountCode(code)
          )
      )
    );
  }

  /**
   * Subtotal auto-add gifts that were natively free before a discount code apply.
   * @param {Object} cart
   * @returns {string[]}
   */
  #getProtectedAutoAddGwpConfigIds(cart) {
    return this.#config
      .filter(
        (config) =>
          config.trigger === 'subtotal' &&
          this.#getAutoAddVariantIdsForConfig(config).length > 0 &&
          this.#isGwpAutomaticDiscountActive(cart, config)
      )
      .map((config) => config.id);
  }

  /**
   * Protected auto-add gifts that lost their native discount after a code apply.
   * @param {Object} cart
   * @param {string[]} protectedAutoAddGwpIds
   * @returns {string[]}
   */
  #getDisplacedProtectedAutoAddGwps(cart, protectedAutoAddGwpIds) {
    return protectedAutoAddGwpIds.filter((configId) => {
      const config = this.#config.find((entry) => entry.id === configId);
      if (!config) return false;

      return (
        this.#hasManagedGiftLineInCart(cart, config) &&
        !this.#isGwpAutomaticDiscountActive(cart, config)
      );
    });
  }

  /**
   * Coupon-triggered gifts that were natively free before another GWP action.
   * @param {Object} cart
   * @returns {string[]}
   */
  #getProtectedCouponGwpConfigIds(cart) {
    return this.#config
      .filter(
        (config) =>
          config.trigger === 'coupon' &&
          this.#hasManagedGiftLineInCart(cart, config) &&
          this.#isGwpAutomaticDiscountActive(cart, config)
      )
      .map((config) => config.id);
  }

  /**
   * Protected coupon gifts that lost their native discount after a subtotal auto-add.
   * @param {Object} cart
   * @param {string[]} protectedCouponGwpIds
   * @returns {string[]}
   */
  #getDisplacedProtectedCouponGwps(cart, protectedCouponGwpIds) {
    return protectedCouponGwpIds.filter((configId) => {
      const config = this.#config.find((entry) => entry.id === configId);
      if (!config) return false;

      return (
        this.#hasManagedGiftLineInCart(cart, config) &&
        !this.#isGwpAutomaticDiscountActive(cart, config)
      );
    });
  }

  /**
   * Re-runs discount calculation so protected coupon gifts regain their native discount.
   * @param {Object} cart
   * @param {string[]} protectedCouponGwpIds
   * @returns {Promise<Object>}
   */
  async #restoreProtectedCouponGwps(cart, protectedCouponGwpIds) {
    let cartResponse = await this.#recalculateCartDiscounts(cart);

    for (const configId of protectedCouponGwpIds) {
      const config = this.#config.find((entry) => entry.id === configId);
      if (!config || !this.#hasManagedGiftLineInCart(cartResponse, config)) continue;

      cartResponse = await this.#awaitGwpNativeApplication(cartResponse, config);
    }

    return cartResponse;
  }

  /**
   * Re-runs discount calculation so protected subtotal auto-add gifts regain their discount.
   * @param {Object} cart
   * @param {string[]} protectedAutoAddGwpIds
   * @returns {Promise<Object>}
   */
  async #restoreProtectedAutoAddGwps(cart, protectedAutoAddGwpIds) {
    let cartResponse = await this.#recalculateCartDiscounts(cart);

    for (const configId of protectedAutoAddGwpIds) {
      const config = this.#config.find((entry) => entry.id === configId);
      if (!config || !this.#hasManagedGiftLineInCart(cartResponse, config)) continue;

      cartResponse = await this.#awaitGwpNativeApplication(cartResponse, config);
    }

    return cartResponse;
  }

  /**
   * Reverts a subtotal auto-add that conflicts with coupon GWPs already in the cart.
   * @param {Object} cartResponse
   * @param {GwpConfig} config
   * @param {Object} cartBeforeAdd
   * @param {string[]} protectedCouponGwpIds
   * @param {string} [message]
   * @param {{ reflectOfferBoxRejection?: boolean }} [options]
   * @returns {Promise<Object>}
   */
  async #revertSubtotalAddForProtectedCoupons(
    cartResponse,
    config,
    cartBeforeAdd,
    protectedCouponGwpIds,
    message = this.#getConflictErrorMessage(),
    options = {}
  ) {
    let cart = await this.#revertGiftAdd(cartResponse, config, cartBeforeAdd);
    this.#setOfferConflictError(config.id, message);

    if (config.offerBoxOnly && options.reflectOfferBoxRejection !== false) {
      this.#reflectOfferBoxGiftRemoval(config, cart);
    }

    if (protectedCouponGwpIds.length > 0) {
      cart = await this.#restoreProtectedCouponGwps(cart, protectedCouponGwpIds);
    }

    return cart;
  }

  /**
   * Removes a GWP coupon attempt while keeping earlier auto-add subtotal gifts free.
   * @param {Object} cart
   * @param {GwpConfig[]} matchingConfigs
   * @param {string[]} priorDiscounts
   * @param {string[]} protectedAutoAddGwpIds
   * @returns {Promise<Object>}
   */
  async #revertCouponForProtectedSubtotalGwps(cart, matchingConfigs, priorDiscounts, protectedAutoAddGwpIds) {
    let cartResponse = cart;

    for (const config of matchingConfigs) {
      cartResponse = await this.#revertGiftAdd(cartResponse, config);
    }

    cartResponse = await this.#restorePriorDiscountCodes(priorDiscounts);
    cartResponse = await this.#restoreProtectedAutoAddGwps(cartResponse, protectedAutoAddGwpIds);

    return cartResponse;
  }

  /**
   * True when a subtotal gift line is fully free (not merely partially discounted).
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isGwpGiftLineFree(cart, config) {
    return this.#getConfigGiftLineItems(cart, config).some((item) => {
      const finalLinePrice = item.final_line_price ?? item.line_price;
      const finalPrice = item.final_price;
      return finalLinePrice === 0 || finalPrice === 0;
    });
  }

  /**
   * True when the configured automatic GWP discount is active on the gift line.
   * Ignores unrelated partial discounts from other codes.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isGwpAutomaticDiscountActive(cart, config) {
    const lines = this.#getConfigGiftLineItems(cart, config);
    if (lines.length === 0) return false;

    if (config.trigger === 'coupon') {
      if (!this.#isCouponActive(cart, config.couponCode || '')) return false;

      const discountTitle = (config.discountTitle || '').trim();
      if (discountTitle && lines.some((item) => this.#lineHasDiscountTitle(item, discountTitle))) {
        return true;
      }

      if (
        lines.some((item) => this.#lineHasCouponCode(item, config.couponCode || ''))
      ) {
        return true;
      }

      if (this.#isGwpGiftLineFree(cart, config)) return true;

      return this.#sectionsIndicateFreeSubtotalGift(cart, config);
    }

    const discountTitle = (config.discountTitle || '').trim();
    if (discountTitle && lines.some((item) => this.#lineHasDiscountTitle(item, discountTitle))) {
      return true;
    }

    if (this.#isGwpGiftLineFree(cart, config)) return true;

    return this.#sectionsIndicateFreeSubtotalGift(cart, config);
  }

  /**
   * True when the current selection meets final GWP acceptance requirements.
   * Physical gifts require the configured discount; free Rush is identified by
   * its dedicated variant and stamped line properties.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {number | null} [variantId]
   * @returns {boolean}
   */
  #isGwpSelectionApplied(cart, config, variantId = null) {
    const freeRushVariantId = this.#freeRushOrder?.variantId ?? null;
    const expectsFreeRush =
      variantId != null && freeRushVariantId != null && variantId === freeRushVariantId;

    if (variantId != null && !expectsFreeRush) {
      const selectedItems = this.#getConfigGiftLineItems(cart, config).filter(
        (item) => Number(item.variant_id) === Number(variantId)
      );
      if (selectedItems.length === 0) return false;
      if (config.trigger === 'coupon' && !this.#isCouponActive(cart, config.couponCode || '')) {
        return false;
      }

      const discountTitle = (config.discountTitle || '').trim();
      if (
        discountTitle &&
        selectedItems.some((item) => this.#lineHasDiscountTitle(item, discountTitle))
      ) {
        return true;
      }

      if (
        config.trigger === 'coupon' &&
        selectedItems.some((item) => this.#lineHasCouponCode(item, config.couponCode || ''))
      ) {
        return true;
      }

      const selectionIsFree = selectedItems.some((item) => {
        const finalLinePrice = item.final_line_price ?? item.line_price;
        const finalPrice = item.final_price;
        return finalLinePrice === 0 || finalPrice === 0;
      });
      if (selectionIsFree) return true;

      return this.#sectionsIndicateFreeSubtotalGift(cart, config);
    }

    if (!expectsFreeRush && this.#isGwpAutomaticDiscountActive(cart, config)) return true;
    if (this.#hasPaidRushFeeInCart(cart)) return false;

    const hasFreeRushLine = (cart.items || []).some((item) =>
      this.#isFreeRushLineItem(item, config)
    );
    if (!hasFreeRushLine) return false;
    if (config.trigger === 'subtotal') return true;

    return this.#isValidatedCouponRushOffer(cart, config);
  }

  /**
   * Section HTML indicates a fully free subtotal gift (not a partial markdown).
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #sectionsIndicateFreeSubtotalGift(cart, config) {
    const sections = cart?.sections;
    if (!sections || typeof sections !== 'object') return false;

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string' || !html) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const row = doc.querySelector(
        `.cart-items__table-row[data-gwp-slot="${CSS.escape(config.id)}"]`
      );
      if (!(row instanceof HTMLElement)) continue;

      const finalLinePrice = Number(row.dataset.gwpFinalLinePrice);
      if (Number.isFinite(finalLinePrice) && finalLinePrice === 0) return true;
    }

    return false;
  }

  /**
   * @returns {string}
   */
  #getDiscountCombinationErrorMessage() {
    return (
      Theme.translations?.discount_code_combination_error ||
      Theme.translations?.discount_code_error ||
      'This discount code cannot be combined with a discount already in your cart.'
    );
  }

  /**
   * @returns {string}
   */
  #getDiscountCodeRejectedMessage() {
    return Theme.translations?.discount_code_error || 'Discount code cannot be applied to your cart';
  }

  /**
   * @param {string[]} priorDiscounts
   * @returns {Promise<Object>}
   */
  async #restorePriorDiscountCodes(priorDiscounts) {
    const sections = this.#getCartSectionIds();
    return updateCartDiscountCodes(Theme.routes.cart_update_url, priorDiscounts, {
      sections: sections.length > 0 ? sections : undefined,
    });
  }

  /**
   * Removes a validated GWP code after its physical validation gift has been
   * replaced by free rush. The pending offer remains as the shopper-visible
   * activation record while the rush cart transform supplies the $0 price.
   * @param {Object} cart
   * @param {PendingCouponOffer} pending
   * @returns {Promise<Object>}
   */
  async #removeCouponCodeForValidatedRush(cart, pending) {
    const normalized = normalizeDiscountCode(pending.code);
    const remainingDiscounts = getStrictlyApplicableDiscountCodes(cart.discount_codes).filter(
      (code) => normalizeDiscountCode(code) !== normalized
    );
    const sections = this.#getCartSectionIds();
    const response = await updateCartDiscountCodes(
      Theme.routes.cart_update_url,
      remainingDiscounts,
      { sections: sections.length > 0 ? sections : undefined }
    );
    const cartResponse = await this.#fetchCart();

    if (response.sections) {
      cartResponse.sections = response.sections;
    } else if (cart.sections) {
      cartResponse.sections = cart.sections;
    }

    return cartResponse;
  }

  /**
   * Removes coupon GWP gift lines and restores discount codes from before the attempt.
   * @param {Object} cart
   * @param {GwpConfig[]} matchingConfigs
   * @param {string[]} priorDiscounts
   * @returns {Promise<Object>}
   */
  async #revertCouponGwpAttempt(cart, matchingConfigs, priorDiscounts) {
    let cartResponse = cart;

    for (const config of matchingConfigs) {
      cartResponse = await this.#revertGiftAdd(cartResponse, config);
    }

    const sections = this.#getCartSectionIds();
    return updateCartDiscountCodes(Theme.routes.cart_update_url, priorDiscounts, {
      sections: sections.length > 0 ? sections : undefined,
    });
  }

  /**
   * @param {Object} item
   * @param {GwpConfig} config
   * @param {Object} [cart]
   * @returns {boolean}
   */
  #lineHasSubtotalGiftDiscount(item, config, cart = null) {
    if (this.#lineHasDiscountTitle(item, config.discountTitle || '')) return true;

    if (cart && this.#cartHasDiscountTitle(cart, config.discountTitle || '')) return true;
    if (cart && this.#sectionsIndicateSubtotalGiftDiscount(cart, config)) return true;

    const finalLinePrice = item.final_line_price ?? item.line_price ?? null;
    if (finalLinePrice === 0) return true;

    const finalPrice = item.final_price ?? null;
    if (finalPrice === 0) return true;

    if ((item.total_discount ?? 0) > 0) return true;

    const originalLinePrice = item.original_line_price ?? item.line_price ?? 0;
    if (
      finalLinePrice != null &&
      originalLinePrice > 0 &&
      finalLinePrice < originalLinePrice
    ) {
      return true;
    }

    const originalPrice = item.original_price ?? item.price ?? 0;
    const discountedPrice = item.discounted_price ?? item.final_price ?? originalPrice;
    if (originalPrice > 0 && discountedPrice < originalPrice) {
      return true;
    }

    const allocations = item.line_level_discount_allocations || item.discounts || [];
    return allocations.some(
      (/** @type {{ amount?: number, total_allocated_amount?: number }} */ allocation) => {
        const amount = allocation.amount ?? allocation.total_allocated_amount ?? 0;
        return amount > 0;
      }
    );
  }

  /**
   * @param {Object} cart
   * @param {string} discountTitle
   * @returns {boolean}
   */
  #cartHasDiscountTitle(cart, discountTitle) {
    const normalized = this.#normalizeDiscountTitle(discountTitle);
    if (!normalized) return false;

    const applications = cart?.cart_level_discount_applications || [];
    return applications.some((/** @type {{ title?: string }} */ application) => {
      const title = this.#normalizeDiscountTitle(application?.title);
      return (
        title === normalized ||
        (title.length > 0 && normalized.length > 0 && (title.includes(normalized) || normalized.includes(title)))
      );
    });
  }

  /**
   * Server-rendered cart sections often include automatic gift discounts before
   * `/cart.js` line allocations catch up. Reads the stamped GWP row from section HTML.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #sectionsIndicateSubtotalGiftDiscount(cart, config) {
    const sections = cart?.sections;
    if (!sections || typeof sections !== 'object') return false;

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string' || !html) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const row = doc.querySelector(
        `.cart-items__table-row[data-gwp-slot="${CSS.escape(config.id)}"]`
      );
      if (!(row instanceof HTMLElement)) continue;

      const finalLinePrice = Number(row.dataset.gwpFinalLinePrice);
      const originalLinePrice = Number(row.dataset.gwpOriginalLinePrice);

      if (Number.isFinite(finalLinePrice) && finalLinePrice === 0) return true;
      if (
        Number.isFinite(finalLinePrice) &&
        Number.isFinite(originalLinePrice) &&
        originalLinePrice > finalLinePrice
      ) {
        return true;
      }

      if (row.querySelector('.cart-items__price-compare')) return true;
    }

    return false;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {Object[]}
   */
  #getConfigGiftLineItems(cart, config) {
    const variantIds = new Set(this.#getAllConfigVariantIds(config));

    return (cart.items || []).filter((item) => {
      if (!variantIds.has(item.variant_id)) return false;

      return (
        this.#lineMatchesGwpIdentity(item, config) ||
        this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY) === config.id
      );
    });
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #configGiftHasDiscountSignals(cart, config) {
    return this.#getConfigGiftLineItems(cart, config).some((item) =>
      this.#lineHasSubtotalGiftDiscount(item, config, cart)
    );
  }

  /**
   * True when other discount codes are applicable but the gift line shows no discount.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #hasDiscountConflictForGwp(cart, config, options = {}) {
    if (this.#isGwpAutomaticDiscountActive(cart, config)) return false;
    return getOtherApplicableDiscountCodes(cart).length > 0;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {number | null} [variantId]
   * @returns {boolean}
   */
  #hasSlotStampedGiftLine(cart, config, variantId = null) {
    return (cart.items || []).some((item) => {
      if (variantId != null && Number(item.variant_id) !== variantId) return false;
      return this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY) === config.id;
    });
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #hasManagedGiftLineInCart(cart, config) {
    if (this.#findManagedLines(cart, config).length > 0) return true;
    return this.#hasSlotStampedGiftLine(cart, config);
  }

  /**
   * Resolves whether a gift add should be kept after native verify polling.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {number | null} [variantId]
   * @returns {{ success: boolean, message: string }}
   */
  #resolveGwpGiftAddOutcome(cart, config, variantId = null, options = {}) {
    const protectedCouponGwpIds = options.protectedCouponGwpIds ?? [];

    if (config.trigger === 'subtotal' && protectedCouponGwpIds.length > 0) {
      if (
        !this.#hasSlotStampedGiftLine(cart, config, variantId) &&
        !this.#hasManagedGiftLineInCart(cart, config)
      ) {
        return { success: false, message: this.#getGiftDiscountNotAppliedMessage() };
      }

      if (this.#getDisplacedProtectedCouponGwps(cart, protectedCouponGwpIds).length > 0) {
        return { success: false, message: this.#getConflictErrorMessage() };
      }

      if (!this.#isGwpSelectionApplied(cart, config, variantId)) {
        return { success: false, message: this.#getConflictErrorMessage() };
      }

      return { success: true, message: '' };
    }

    if (this.#isGwpSelectionApplied(cart, config, variantId)) {
      return { success: true, message: '' };
    }

    if (this.#hasDiscountConflictForGwp(cart, config, options)) {
      return { success: false, message: this.#getConflictErrorMessage() };
    }

    if (!this.#hasSlotStampedGiftLine(cart, config, variantId)) {
      return { success: false, message: this.#getGiftDiscountNotAppliedMessage() };
    }

    return { success: false, message: this.#getGiftDiscountNotAppliedMessage() };
  }

  /**
   * Polling signal that Shopify has processed the current GWP selection. Section
   * HTML is intentionally accepted while `/cart.js` allocations catch up; final
   * acceptance must use #isGwpSelectionApplied instead.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isGwpNativelyApplied(cart, config) {
    if (config.trigger === 'subtotal' && this.#sectionsIndicateSubtotalGiftDiscount(cart, config)) {
      return true;
    }

    let lines = this.#findManagedLines(cart, config);

    if (lines.length === 0 && config.trigger === 'subtotal') {
      const slotLines = this.#getConfigGiftLineItems(cart, config).filter(
        (item) => this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY) === config.id
      );

      if (slotLines.length > 0) {
        return slotLines.some((item) => this.#lineHasSubtotalGiftDiscount(item, config, cart));
      }

      return false;
    }

    if (lines.length === 0) return false;

    const rush = this.#freeRushOrder;
    const allRush =
      Boolean(rush) &&
      lines.every((line) => {
        const item = (cart.items || []).find((entry) => entry.key === line.key);
        return item ? this.#isFreeRushLineItem(item, config) : false;
      });
    if (allRush) return true;

    if (config.trigger === 'coupon') {
      if (!this.#isCouponActive(cart, config.couponCode || '')) return false;

      const discountTitle = (config.discountTitle || '').trim();
      if (!discountTitle) return true;

      return lines.some((line) => {
        const item = (cart.items || []).find((entry) => entry.key === line.key);
        if (!item) return false;
        if (this.#isFreeRushLineItem(item, config)) return true;
        return (
          this.#lineHasCouponCode(item, config.couponCode || '') ||
          this.#lineHasDiscountTitle(item, discountTitle)
        );
      });
    }

    return lines.some((line) => {
      const item = (cart.items || []).find((entry) => entry.key === line.key);
      if (!item) return false;
      if (this.#isFreeRushLineItem(item, config)) return true;
      return this.#lineHasSubtotalGiftDiscount(item, config, cart);
    });
  }

  /**
   * True when a managed gift is in cart but Shopify is no longer applying its discount.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #hasNativeGwpConflict(cart, config) {
    if (!this.#hasManagedGiftLineInCart(cart, config)) return false;
    if (this.#isGwpSelectionApplied(cart, config)) return false;
    return this.#hasDiscountConflictForGwp(cart, config);
  }

  /**
   * @returns {string}
   */
  #getConflictErrorMessage() {
    return (
      Theme.translations?.gift_with_purchase_discount_conflict_error ||
      'This gift cannot be combined with a discount already in your cart.'
    );
  }

  /**
   * @returns {string}
   */
  #getGiftDiscountNotAppliedMessage() {
    return (
      Theme.translations?.gift_with_purchase_discount_not_applied_error ||
      'This gift could not be applied. Please try again or contact support.'
    );
  }

  /**
   * @param {string} gwpId
   * @param {string} message
   */
  #setOfferConflictError(gwpId, message) {
    this.#offerConflictMessages.set(gwpId, message);
    this.#paintOfferConflictError(gwpId, message);
  }

  /**
   * @param {string} gwpId
   */
  #clearOfferConflictError(gwpId) {
    this.#offerConflictMessages.delete(gwpId);
    this.#paintOfferConflictError(gwpId, '');
  }

  /**
   * @param {string} gwpId
   * @param {string} message
   */
  #paintOfferConflictError(gwpId, message) {
    document.querySelectorAll(`[data-gwp-offer-item][data-gwp-id="${CSS.escape(gwpId)}"]`).forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      let error = item.querySelector('[data-gwp-conflict-error]');
      if (!(error instanceof HTMLElement)) {
        if (!message) return;
        error = document.createElement('p');
        error.className = 'gift-with-purchase-offers__conflict-error';
        error.dataset.gwpConflictError = '';
        error.setAttribute('role', 'alert');
        const fieldset = item.querySelector('.gift-with-purchase-offers__add-gwp');
        if (fieldset instanceof HTMLElement) {
          fieldset.insertAdjacentElement('afterend', error);
        } else {
          item.prepend(error);
        }
      }

      if (!message) {
        error.hidden = true;
        error.textContent = '';
        return;
      }

      error.hidden = false;
      error.textContent = message;
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   */
  #restoreOfferConflictError(item, config) {
    const message = this.#offerConflictMessages.get(config.id) || '';
    this.#paintOfferConflictError(config.id, message);
    if (!message) return;

    const error = item.querySelector('[data-gwp-conflict-error]');
    if (error instanceof HTMLElement && !item.contains(error)) {
      const fieldset = item.querySelector('.gift-with-purchase-offers__add-gwp');
      if (fieldset instanceof HTMLElement) {
        fieldset.insertAdjacentElement('afterend', error);
      }
    }
  }

  /**
   * Reverts a gift add that Shopify declined to discount natively.
   * @param {Object} cartAfter
   * @param {GwpConfig} config
   * @param {Object} [cartBefore]
   * @returns {Promise<Object>}
   */
  async #revertGiftAdd(cartAfter, config, cartBefore) {
    const beforeKeys = new Set(
      (cartBefore ? this.#findManagedLines(cartBefore, config) : []).map((line) => line.key)
    );
    const keysToRemove = this.#findManagedLines(cartAfter, config)
      .map((line) => line.key)
      .filter((key) => !beforeKeys.has(key));

    if (keysToRemove.length === 0) {
      return cartAfter;
    }

    const pidsToRemove = this.#collectPersonalizationPidsFromLineKeys(cartAfter, keysToRemove);
    let cartResponse = await this.#removeCartLines(keysToRemove, cartAfter);
    cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);

    if (pidsToRemove.length > 0) {
      const attributeResponse = await this.#patchPersonalizationCartAttribute(
        pidsToRemove,
        {},
        cartResponse.attributes,
        this.#getCartSectionIds()
      );
      cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
    }

    return this.#pruneOrphanedPersonalizationCartAttributes(cartResponse);
  }

  /**
   * Restores the exact managed gift lines and personalization payload that
   * existed before a pending coupon selection replaced them.
   * @param {Object} cartAfter
   * @param {GwpConfig} config
   * @param {Object} cartBefore
   * @returns {Promise<Object>}
   */
  async #restoreGiftSelectionSnapshot(cartAfter, config, cartBefore) {
    const beforeKeys = new Set(
      this.#findManagedLines(cartBefore, config, { forRemoval: true }).map((line) => line.key)
    );
    const beforeItems = (cartBefore.items || []).filter((item) => beforeKeys.has(item.key));
    if (beforeItems.length === 0) return cartAfter;

    let cartResponse = cartAfter;
    const currentKeys = this.#findManagedLines(cartResponse, config, { forRemoval: true }).map(
      (line) => line.key
    );
    if (currentKeys.length > 0) {
      cartResponse = await this.#removeCartLines(currentKeys, cartResponse);
      cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);
    }

    const sections = this.#getCartSectionIds();
    const body = JSON.stringify({
      items: beforeItems.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity || 1,
        properties: item.properties || {},
      })),
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to restore previous gift selection: ${response.status}`);
    }

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || 'Failed to restore previous gift selection');
    }

    cartResponse = await this.#fetchCart();
    cartResponse = this.#mergeAddedItemsIntoCart(cartResponse, data.items);
    if (data.sections) cartResponse.sections = data.sections;

    /** @type {Record<string, { attributes: Array<{ key: string, value: string | { label: string, value: string } }> }>} */
    const personalizationEntries = {};
    for (const item of beforeItems) {
      const pid = getLineItemPidFromProperties(item.properties);
      if (!pid) continue;
      const raw = getPersonalizationEntryRawFromAttributesWithLegacy(cartBefore.attributes, pid);
      const properties = parsePersonalizationEntryPayload(raw);
      if (Object.keys(properties).length === 0) continue;
      personalizationEntries[pid] = this.#buildCartAttributePersonalizationPayload(properties);
    }

    if (Object.keys(personalizationEntries).length > 0) {
      const attributeResponse = await this.#patchPersonalizationCartAttribute(
        [],
        personalizationEntries,
        cartResponse.attributes,
        sections
      );
      cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
    }

    return cartResponse;
  }

  /**
   * @param {CartUpdateEvent | DiscountUpdateEvent | Event} event
   */
  #onCartChange = async (event) => {
    if (this.#syncing) return;

    try {
      let source;
      let cart = null;
      const isDiscountUpdate = event instanceof DiscountUpdateEvent;

      if (event instanceof CartUpdateEvent) {
        source = event.detail?.data?.source;
        cart = event.detail?.resource ?? null;
      } else if (isDiscountUpdate) {
        cart = event.detail?.resource ?? null;
      }

      if (source === SYNC_SOURCE || source === MANUAL_ADD_SOURCE) {
        return;
      }

      if (!cart?.items) {
        cart = await this.#fetchCart();
      }

      const previouslyTrackedGwpIds = this.#lastKnownGwpIds;
      const previouslyTrackedGwpLineKeys = this.#cloneTrackedLineKeysMap(this.#lastKnownGwpLineKeys);
      this.#detectUserDismissals(cart, source);
      await this.#sync(cart, previouslyTrackedGwpIds, previouslyTrackedGwpLineKeys);

      if (isDiscountUpdate) {
        // cart-discount / cart-items section morph runs after this event and resets
        // empty GWP mount points; re-apply once the DOM has settled.
        const refreshedCart = await this.#fetchCart();
        this.#scheduleDeferredOffersUIReapply(refreshedCart);
      }
    } catch (error) {
      this.#restoreGatedCheckoutAfterFailure();
      console.error('Gift with purchase: failed to process cart update', error);
    }
  };

  /**
   * Re-fetches the cart and repaints dismissed-gift offer boxes.
   * @returns {Promise<Object | null>}
   */
  refreshOffersUI = async () => {
    if (!this.#initialized) return null;

    try {
      const cart = await this.#fetchCart();
      this.#updateGwpUI(cart);
      this.#scheduleDeferredOffersUIReapply(cart);
      return cart;
    } catch (error) {
      this.#restoreGatedCheckoutAfterFailure();
      console.error('Gift with purchase: failed to refresh offers UI', error);
      return null;
    }
  };

  /**
   * @param {Object} [cartFromEvent]
   * @param {Set<string>} [previouslyTrackedGwpIds]
   * @param {Map<string, Set<string>>} [previouslyTrackedGwpLineKeys]
   */
  async #sync(cartFromEvent, previouslyTrackedGwpIds = this.#lastKnownGwpIds, previouslyTrackedGwpLineKeys = this.#lastKnownGwpLineKeys) {
    if (this.#syncing || this.#preparingDiscountCode || this.#config.length === 0) return;

    this.#syncing = true;
    let dispatchedCartUpdate = false;

    try {
      let cart = cartFromEvent?.items ? cartFromEvent : await this.#fetchCart();

      if (cartFromEvent?.items) {
        const fullCart = await this.#fetchCart();
        cart = {
          ...fullCart,
          sections: cartFromEvent.sections ?? fullCart.sections,
        };
      } else if (cartFromEvent?.sections) {
        cart = { ...cart, sections: cartFromEvent.sections };
      }

      this.#reconcilePendingCouponOffer(cart);

      for (const config of this.#config) {
        if (!this.#isGiftQualified(cart, config)) {
          this.#clearDismissal(config.id);
          this.#clearOfferConflictError(config.id);
          continue;
        }

        if (this.#hasNativeGwpConflict(cart, config)) {
          this.#setOfferConflictError(config.id, this.#getConflictErrorMessage());
        } else if (
          this.#offerConflictMessages.has(config.id) &&
          this.#findManagedLines(cart, config).length === 0 &&
          getOtherApplicableDiscountCodes(cart).length === 0
        ) {
          // Discount conflict cleared — allow auto-add to retry on this sync.
          this.#clearOfferConflictError(config.id);
        }
      }

      cart = await this.#applyPendingGiftDiscounts(cart);

      const protectedCouponGwpIds = this.#getProtectedCouponGwpConfigIds(cart);

      const { toRemove, toAdd } = this.#planCartChanges(cart, previouslyTrackedGwpIds, previouslyTrackedGwpLineKeys);
      const pidsToRemove = this.#collectPersonalizationPidsFromLineKeys(cart, toRemove);

      let cartResponse = cart;

      if (toRemove.length > 0) {
        cartResponse = await this.#removeCartLines(toRemove, cartResponse);
        cartResponse = await this.#refreshCartAfterLineRemovals(cartResponse);
      }

      if (pidsToRemove.length > 0) {
        const attributeResponse = await this.#patchPersonalizationCartAttribute(
          pidsToRemove,
          {},
          cartResponse.attributes,
          this.#getCartSectionIds()
        );
        cartResponse = this.#mergePersonalizationAttributeResponse(cartResponse, attributeResponse);
      }

      if (toRemove.length > 0) {
        cartResponse = await this.#pruneOrphanedPersonalizationCartAttributes(cartResponse);
        this.#reflectRemovedOfferBoxAutoAddGifts(cartResponse, previouslyTrackedGwpIds);
      }

      if (toAdd.length > 0) {
        const cartBeforeAdd = cartResponse;
        cartResponse = await this.#addGiftLines(toAdd);

        /** @type {Set<string>} */
        const failedConfigIds = new Set();
        for (const line of toAdd) {
          const config = this.#config.find((entry) => entry.id === line.configId);
          if (!config) continue;
          if (failedConfigIds.has(config.id)) continue;

          cartResponse = await this.#awaitGwpNativeApplication(cartResponse, config);

          const verifyOptions = { protectedCouponGwpIds };
          const outcome = this.#resolveGwpGiftAddOutcome(
            cartResponse,
            config,
            line.variantId,
            verifyOptions
          );

          if (!outcome.success) {
            cartResponse = await this.#revertSubtotalAddForProtectedCoupons(
              cartResponse,
              config,
              cartBeforeAdd,
              protectedCouponGwpIds,
              outcome.message
            );
            failedConfigIds.add(config.id);
            continue;
          }

          this.#clearOfferConflictError(config.id);
          this.#clearDismissal(config.id);
          if (this.#isInteractiveOffer(config)) {
            this.#confirmedOfferAcceptIds.add(config.id);
          }
        }
      }

      if (toRemove.length > 0 || toAdd.length > 0) {
        document.dispatchEvent(
          new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
            source: SYNC_SOURCE,
            itemCount: cartResponse.item_count,
            sections: cartResponse.sections,
          })
        );
        dispatchedCartUpdate = true;
      }

      const cartBeforeDiscountRetry = cartResponse;
      cartResponse = await this.#applyPendingGiftDiscounts(cartResponse);

      if (
        !dispatchedCartUpdate &&
        cartResponse.sections &&
        cartResponse.sections !== cartBeforeDiscountRetry.sections &&
        this.#config.some((config) => this.#isGwpNativelyApplied(cartResponse, config))
      ) {
        document.dispatchEvent(
          new CartUpdateEvent(cartResponse, SYNC_SOURCE, {
            source: SYNC_SOURCE,
            itemCount: cartResponse.item_count,
            sections: cartResponse.sections,
          })
        );
        dispatchedCartUpdate = true;
      }

      this.#snapshotGwpState(cartResponse);
      this.#updateGwpUI(cartResponse);
      this.#scheduleOffersUIRefresh(cartResponse);
    } catch (error) {
      this.#restoreGatedCheckoutAfterFailure();
      console.error('Gift with purchase: sync failed', error);
    } finally {
      this.#releaseSyncing(false);
    }
  }

  /**
   * @param {Event} event
   */
  #onCartDrawerOpen = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('cart-drawer-component')) return;

    void this.#refreshOffersUIAfterDrawerOpen();
  };

  /**
   * @param {Event} event
   */
  #onSectionHydrated = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    const drawerCart = document.querySelector(
      `cart-items-component[data-drawer][data-section-id="${sectionId}"]`
    );
    if (!drawerCart) return;

    if (this.#lastKnownGatedAcceleratedCheckoutBlocked !== null) {
      this.#setGatedAcceleratedCheckoutBlocked(
        this.#lastKnownGatedAcceleratedCheckoutBlocked
      );
    }

    void this.#refreshOffersUI();
  };

  /**
   * Header hydration and drawer open can morph cart markup after offers are painted.
   */
  async #refreshOffersUIAfterDrawerOpen() {
    const cart = await this.#refreshOffersUI();
    if (!cart) return;

    this.#scheduleDeferredOffersUIReapply(cart);
  }

  /**
   * @returns {Promise<Object | null>}
   */
  async #refreshOffersUI() {
    if (!this.#initialized) return null;

    try {
      let cart = await this.#fetchCart();
      this.#reconcilePendingCouponOffer(cart);
      const cartBeforeDiscountRetry = cart;
      cart = await this.#applyPendingGiftDiscounts(cart);

      if (
        cart.sections &&
        cart.sections !== cartBeforeDiscountRetry.sections &&
        this.#config.some((config) => this.#isGwpNativelyApplied(cart, config))
      ) {
        document.dispatchEvent(
          new CartUpdateEvent(cart, SYNC_SOURCE, {
            source: SYNC_SOURCE,
            itemCount: cart.item_count,
            sections: cart.sections,
          })
        );
      }

      this.#updateGwpUI(cart);
      return cart;
    } catch (error) {
      this.#restoreGatedCheckoutAfterFailure();
      console.error('Gift with purchase: failed to refresh offers UI', error);
      return null;
    }
  }

  /**
   * Re-applies add-back offers after cart section morphs (drawer uses hydration morph after cart:update).
   * @param {Object} cart
   */
  #scheduleOffersUIRefresh(cart) {
    this.#scheduleDeferredOffersUIReapply(cart, { microtaskOnly: true });
  }

  /**
   * Re-applies offers UI at later timing points without re-fetching cart.
   * @param {Object} cart
   * @param {{ microtaskOnly?: boolean }} [options]
   */
  #scheduleDeferredOffersUIReapply(cart, options = {}) {
    const reapply = () => {
      if (!this.#initialized) return;
      this.#updateGwpUI(cart);
    };

    queueMicrotask(reapply);

    if (options.microtaskOnly) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(reapply);
    });

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(reapply);
    } else {
      setTimeout(reapply, 50);
    }
  }

  /**
   * @param {Object} cart
   * @param {string | undefined} source
   */
  #detectUserDismissals(cart, source) {
    if (source === SYNC_SOURCE || source === MANUAL_ADD_SOURCE) {
      this.#snapshotGwpState(cart);
      return;
    }

    if (source === 'personalization-component-edit') {
      this.#snapshotGwpState(cart);
      return;
    }

    if (document.querySelector('.cart-items__personalization-modal[open]')) {
      this.#snapshotGwpState(cart);
      return;
    }

    const currentIds = this.#getGwpIdsInCart(cart);

    for (const gwpId of this.#lastKnownGwpIds) {
      if (currentIds.has(gwpId)) continue;

      const config = this.#config.find((entry) => entry.id === gwpId);
      if (!config) continue;

      // Offer-box promos should stay in preview mode when their gift line disappears,
      // unless an auto-added or accepted gift was removed — then reflect rejection.
      // Coupon offer-box promos follow the same rule while their code is still
      // active; non-offer-box coupon (auto-add) gifts are left untouched.
      if (this.#isGiftQualified(cart, config) && (config.trigger !== 'coupon' || config.offerBoxOnly)) {
        if (config.offerBoxOnly) {
          if (this.#confirmedOfferAcceptIds.has(gwpId) || this.#lastKnownGwpLineKeys.has(gwpId)) {
            this.#reflectOfferBoxGiftRemoval(config, cart);
          }
        } else {
          this.#dismiss(gwpId);
        }
      }
    }

    this.#snapshotGwpState(cart);
  }

  /**
   * @param {Object} cart
   * @param {Set<string>} [previouslyTrackedGwpIds]
   * @param {Map<string, Set<string>>} [previouslyTrackedGwpLineKeys]
   * @returns {{ toRemove: string[], toAdd: Array<{ variantId: number, configId: string }> }}
   */
  #planCartChanges(cart, previouslyTrackedGwpIds = this.#lastKnownGwpIds, previouslyTrackedGwpLineKeys = this.#lastKnownGwpLineKeys) {
    /** @type {Set<string>} */
    const toRemove = new Set();
    /** @type {Array<{ variantId: number, configId: string }>} */
    const toAdd = [];

    for (const config of this.#config) {
      const qualified = this.#isGiftQualified(cart, config);
      const nativeConflict = qualified && this.#hasNativeGwpConflict(cart, config);
      const shouldHaveGift =
        qualified &&
        !nativeConflict &&
        (config.trigger === 'coupon' || !this.#isDismissed(config.id));
      const trackedLineKeys = this.#mergeTrackedLineKeys(
        config.id,
        previouslyTrackedGwpLineKeys.get(config.id)
      );
      const existingLines = this.#findManagedLines(cart, config, {
        forRemoval: !shouldHaveGift,
        previouslyTracked: previouslyTrackedGwpIds.has(config.id),
        trackedLineKeys,
      });

      if (shouldHaveGift && this.#shouldAutoAddGift(config, cart)) {
        const existingVariantIds = new Set(existingLines.map((line) => line.variantId));
        for (const variantId of this.#getAutoAddVariantIdsForConfig(config)) {
          if (!existingVariantIds.has(variantId)) {
            toAdd.push({ variantId, configId: config.id });
          }
        }
      } else if (!shouldHaveGift) {
        if (
          config.trigger === 'coupon' &&
          this.#pendingCouponCodes.has(normalizeDiscountCode(config.couponCode || ''))
        ) {
          continue;
        }

        for (const line of existingLines) {
          toRemove.add(line.key);
        }

        for (const line of this.#findGwpGiftWrapLines(cart, config)) {
          toRemove.add(line.key);
        }

        if (config.trigger === 'subtotal') {
          for (const key of this.#findDomGwpLineKeys(config.id)) {
            toRemove.add(key);
          }
        }
      }
    }

    return { toRemove: [...toRemove], toAdd };
  }

  /**
   * @param {Object} cart
   */
  #updateGwpUI(cart) {
    for (const config of this.#config) {
      if (!this.#isGiftQualified(cart, config)) {
        this.#pendingOfferEdits.delete(config.id);
        this.#pendingRushOfferIds.delete(config.id);
      }
    }
    this.#setGatedCheckoutInitializing(false);
    this.#applyManagedLineSlots(cart);
    this.#renderPendingDiscountPills(cart);

    const offerContainers = document.querySelectorAll('[data-gwp-offers]');
    const progressContainers = document.querySelectorAll('[data-gwp-offers-progress]');

    if (offerContainers.length === 0 && progressContainers.length === 0) {
      this.#syncGatedAcceleratedCheckout(cart);
      return;
    }

    const offers = this.#config.filter((config) => this.#shouldSurfaceGwpOffer(config, cart));
    const progressConfigs = this.#config.filter((config) =>
      this.#shouldShowProgressForConfig(config, cart)
    );

    for (const container of progressContainers) {
      if (!(container instanceof HTMLElement)) continue;

      if (progressConfigs.length === 0) {
        container.innerHTML = '';
        container.hidden = true;
        continue;
      }

      container.hidden = false;
      container.innerHTML = `
        <ul class="gift-with-purchase-offers-progress__list">
          ${progressConfigs.map((config) => this.#renderProgressItem(config, cart)).join('')}
        </ul>
      `;
    }

    for (const container of offerContainers) {
      if (!(container instanceof HTMLElement)) continue;

      if (offers.length === 0) {
        container.innerHTML = '';
        container.hidden = true;
        container.dataset.gwpRenderedIds = '';
        continue;
      }

      const showItemHeadings = offers.length > 1;
      const blockHeadingText =
        offers.length === 1 && this.#shouldShowOfferHeading(offers[0])
          ? this.#resolveOfferHeading(offers[0])
          : '';

      const offerHeadingHtml =
        blockHeadingText && !showItemHeadings
          ? `<p class="gift-with-purchase-offers__heading">${this.#escapeHtml(blockHeadingText)}</p>`
          : '';

      container.hidden = false;

      // Render-once guard: while the same set of offers is visible, reconcile the
      // existing DOM instead of replacing innerHTML. This preserves selected product,
      // variant, personalization, checkbox, and initialized swiper state.
      const nextRenderedIds = offers
        .map((config) => `${config.id}:${this.#canOfferFreeRush(config, cart) ? 'rush' : 'no-rush'}`)
        .join('|');
      if (container.dataset.gwpRenderedIds === nextRenderedIds && container.children.length > 0) {
        for (const config of offers) {
          const item = container.querySelector(`[data-gwp-id="${CSS.escape(config.id)}"]`);
          if (item instanceof HTMLElement) {
            this.#reconcileOfferItem(item, config, cart);
            this.#syncUserConfirmedGiftAcceptToItem(item);
          }
        }
        continue;
      }

      container.innerHTML = `
        <div class="gift-with-purchase-offers__list">
          ${offers
            .map((config) => {
              return this.#renderOfferItem(config, offerHeadingHtml, cart);
            })
            .join('')}
        </div>
      `;
      container.dataset.gwpRenderedIds = nextRenderedIds;

      // Restore the persisted selection (chosen product, personalization text,
      // opt-out toggle) from the cart onto the freshly rendered offer items.
      for (const config of offers) {
        const item = container.querySelector(`[data-gwp-id="${CSS.escape(config.id)}"]`);
        if (item instanceof HTMLElement) {
          this.#reconcileOfferItem(item, config, cart);
          this.#syncUserConfirmedGiftAcceptToItem(item);
        }
      }
    }

    this.#syncGatedAcceleratedCheckout(cart);
    void this.#ensureAcceptedOfferLines(cart);
  }

  /**
   * Adds the selected gift for interactive offers that are accepted in the UI but
   * not yet represented by a cart line. Offer-box promos require an explicit form
   * interaction; sized auto-add promos can commit as soon as their form is valid.
   * @param {Object} cart
   */
  async #ensureAcceptedOfferLines(cart) {
    if (this.#syncing) return;

    let cartSnapshot = cart;

    for (const config of this.#config) {
      const requiresVariantSelection = this.#configRequiresVariantSelection(config);

      if (!this.#isInteractiveOffer(config)) continue;
      if (!this.#isGiftQualified(cartSnapshot, config)) continue;
      if (this.#isDismissed(config.id)) continue;
      if (this.#offerConflictMessages.has(config.id)) continue;
      if (this.#hasNativeGwpConflict(cartSnapshot, config)) continue;

      if (requiresVariantSelection && !this.#hasPendingVariantSelectionGift(cartSnapshot, config)) {
        continue;
      }

      if (!requiresVariantSelection && this.#hasManagedGiftLineInCart(cartSnapshot, config)) {
        continue;
      }

      const item = document.querySelector(
        `[data-gwp-offer-item][data-gwp-id="${CSS.escape(config.id)}"]`
      );
      if (!(item instanceof HTMLElement)) continue;

      const accept = item.querySelector('input[value="acceptGWPGProduct"]');
      if (!(accept instanceof HTMLInputElement) || !accept.checked) continue;
      if (config.offerBoxOnly && !this.#hasUserConfirmedGiftAccept(item)) continue;

      const context = this.#resolveOfferContext(accept);
      if (!context) continue;

      this.#syncOfferPersonalizationForSelection(
        context.item,
        context.config,
        context.selectedVariantId,
        context.selectedProductId
      );
      if (!this.#isOfferVariantSelectionComplete(context)) continue;
      if (!this.#isGwpOfferPersonalizationValid(context)) continue;

      const properties = await this.#buildOfferProperties(context);
      const updatedCart = await this.#updateGiftSelection(
        context.config,
        context.selectedVariantId,
        properties
      );

      if (updatedCart) {
        cartSnapshot = updatedCart;
      }
    }
  }

  /**
   * Updates cart-derived state on an already-rendered interactive offer without
   * destroying user input. Only forces the accepted/enabled state
   * when the gift is genuinely in the cart; otherwise leaves the user's current
   * accept/reject selection, personalization text, and toggle untouched.
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {Object} cart
   */
  #reconcileOfferItem(item, config, cart) {
    const accept = item.querySelector('input[value="acceptGWPGProduct"]');
    const acceptRush = item.querySelector(`input[value="${ACCEPT_GWP_RUSH_VALUE}"]`);
    const reject = item.querySelector('input[value="rejectGWPProduct"]');
    const freeRushListItem = acceptRush?.closest('.gift-with-purchase-offers__list-item');
    const canOfferFreeRush = this.#canOfferFreeRush(config, cart);

    const acceptChecked = accept instanceof HTMLInputElement && accept.checked;
    const rushChecked = acceptRush instanceof HTMLInputElement && acceptRush.checked;
    const rejectCheckedDom = reject instanceof HTMLInputElement && reject.checked;

    if (freeRushListItem instanceof HTMLElement) {
      freeRushListItem.hidden = !canOfferFreeRush;

      if (!canOfferFreeRush && acceptRush instanceof HTMLInputElement && acceptRush.checked) {
        acceptRush.checked = false;
      }
    }

    const selection = this.#resolveOfferSelection(cart, config);

    if (this.#pendingRushOfferIds.has(config.id)) {
      if (selection?.choice === 'rush') {
        this.#pendingRushOfferIds.delete(config.id);
      } else {
        if (acceptRush instanceof HTMLInputElement) acceptRush.checked = true;
        if (accept instanceof HTMLInputElement) accept.checked = false;
        if (reject instanceof HTMLInputElement) reject.checked = false;
        this.#setOfferChoiceDisabled(item, false);
        this.#setOfferGiftDetailsDisabled(item, true);
        this.#syncOfferChoiceVisibility(item, 'rush');
        return;
      }
    }

    if (selection?.choice === 'gift' || selection?.choice === 'rush') {
      this.#confirmedOfferAcceptIds.add(config.id);
    }

    if (this.#isOfferEditPending(item, config, selection)) {
      this.#setOfferControlsDisabled(item, false);
      this.#syncOfferChoiceVisibility(item, 'gift');
      const context = this.#buildOfferContext(item, config);
      if (context) {
        this.#syncOfferPersonalizationForSelection(
          item, config, context.selectedVariantId, context.selectedProductId
        );
      }
      return;
    }
    delete item.dataset.gwpOfferEditing;
    this.#pendingOfferEdits.delete(config.id);

    // Only while a gift cart update is in flight — not when accept is merely the HTML default.
    const pendingGiftSwitch =
      this.#syncing &&
      (acceptChecked || this.#hasUserConfirmedGiftAccept(item)) &&
      !rushChecked &&
      !rejectCheckedDom &&
      selection?.choice !== 'gift';

    if (pendingGiftSwitch) {
      this.#setOfferChoiceDisabled(item, false);
      this.#setOfferGiftDetailsDisabled(item, false);
      this.#syncOfferChoiceVisibility(item, 'gift');

      if (selection?.choice === 'gift') {
        this.#applyOfferSelectionToItem(item, config, selection);
      }
      return;
    }

    const showRejected = this.#isDismissed(config.id);
    const rejectChecked = reject instanceof HTMLInputElement && reject.checked;

    if (!selection || showRejected || rejectChecked) {
      if (showRejected) {
        if (reject instanceof HTMLInputElement && !reject.checked) reject.checked = true;
        if (accept instanceof HTMLInputElement && accept.checked) accept.checked = false;
        if (acceptRush instanceof HTMLInputElement && acceptRush.checked) acceptRush.checked = false;
        delete item.dataset.gwpOfferAccepted;
      }

      if (showRejected || rejectChecked) {
        this.#setOfferChoiceDisabled(item, false);
        this.#setOfferGiftDetailsDisabled(item, true);
      } else if (!selection) {
        if (accept instanceof HTMLInputElement && !accept.checked) accept.checked = true;
        this.#setOfferChoiceDisabled(item, false);
        this.#setOfferGiftDetailsDisabled(item, false);
      }

      this.#syncOfferChoiceVisibility(item, this.#resolveOfferChoiceFromDom(item));
      this.#restoreOfferConflictError(item, config);
      return;
    }

    if (selection.choice === 'rush') {
      if (acceptRush instanceof HTMLInputElement && !acceptRush.checked) acceptRush.checked = true;
      if (accept instanceof HTMLInputElement && accept.checked) accept.checked = false;
      if (reject instanceof HTMLInputElement && reject.checked) reject.checked = false;
      this.#setOfferChoiceDisabled(item, false);
      this.#setOfferGiftDetailsDisabled(item, true);
      this.#syncOfferChoiceVisibility(item, 'rush');
      return;
    }

    if (accept instanceof HTMLInputElement && !accept.checked) accept.checked = true;
    if (acceptRush instanceof HTMLInputElement && acceptRush.checked) acceptRush.checked = false;
    if (reject instanceof HTMLInputElement && reject.checked) reject.checked = false;

    this.#setOfferControlsDisabled(item, false);
    this.#syncOfferChoiceVisibility(item, 'gift');
    this.#applyOfferSelectionToItem(item, config, selection);
    this.#restoreOfferConflictError(item, config);
  }

  /**
   * Reflects the cart-derived gift selection (chosen product, personalization
   * text, opt-out toggle) onto an already-rendered offer item without clobbering
   * an input the user is actively editing.
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {{ choice: GwpOfferChoice, variantId: number, text: string, optedOut: boolean }} selection
   */
  #applyOfferSelectionToItem(item, config, selection) {
    if (this.#isOfferEditPending(item, config, selection)) return;

    const scopeId = config.id;
    const product = this.#getConfigProduct(config, selection.variantId);
    const cartProductId = product?.productId ?? 0;
    const domProductId = this.#resolveSelectedProductId(item, config);

    // Only mirror cart product onto the radio when UI already matches cart.
    if (cartProductId && (!domProductId || domProductId === cartProductId)) {
      const productRadio = document.querySelector(
        `input[name="gwpProduct-${CSS.escape(scopeId)}"][value="${CSS.escape(String(cartProductId))}"]`
      );
      if (
        productRadio instanceof HTMLInputElement &&
        !productRadio.checked &&
        productRadio !== document.activeElement
      ) {
        productRadio.checked = true;
      }

      if (product) {
        this.#applyOfferVariantFieldsetState(item, product, selection, false);
      }
    }

    const uiProductId = this.#resolveSelectedProductId(item, config) || cartProductId;
    if (uiProductId) {
      this.#syncOfferVariantOptionsVisibility(item, config, uiProductId);
    }

    const textInput = item.querySelector('.js-personalization-text-input[data-personalization-text-input]');
    const textToApply =
      textInput instanceof HTMLInputElement && textInput === document.activeElement
        ? textInput.value
        : selection.text;

    item.dataset.gwpSyncingPersonalization = 'true';

    try {
      this.#syncOfferPersonalizationForSelection(
        item,
        config,
        selection.variantId,
        uiProductId
      );

      const optOutToggle = item.querySelector('[data-personalize-opt-out-toggle]');
      if (optOutToggle instanceof HTMLInputElement) {
        optOutToggle.checked = selection.optedOut;
      }

      if (selection.optedOut) {
        this.#clearGwpOfferPersonalizationValidation(item);
      }

      if (textInput instanceof HTMLInputElement && textInput.value !== textToApply) {
        textInput.value = textToApply;

        const personalization = /** @type {HTMLElement & {
          validatePersonalizationInput?: () => boolean,
          _queuePreviewRefresh?: (delay?: number) => void
        } | null} */ (item.querySelector('personalization-component[data-gwp-personalization]'));
        personalization?.validatePersonalizationInput?.();
        personalization?._queuePreviewRefresh?.();
      }

      this.#applyGwpOfferPersonalizationUiState(item, config, selection);
    } finally {
      delete item.dataset.gwpSyncingPersonalization;
    }
  }

  /**
   * Reverts offer UI to the cart-derived gift selection after a failed validation
   * or cart update. No-op when the gift is not yet represented in the cart.
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {{ choice: GwpOfferChoice, variantId: number, text: string, optedOut: boolean } | null} cartSelection
   */
  #restoreOfferUiFromCartSelection(item, config, cartSelection) {
    if (cartSelection?.choice !== 'gift') return;
    this.#applyOfferSelectionToItem(item, config, cartSelection);
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {{ variantId: number, optedOut: boolean }} selection
   */
  #applyGwpOfferPersonalizationUiState(item, config, selection) {
    const personalization = item.querySelector('[data-gwp-personalization]');
    if (!(personalization instanceof HTMLElement)) return;

    const product = this.#getConfigProduct(config, selection.variantId);
    const isPersonalizable = Boolean(this.#resolveGwpPersonalizableDesign(product));
    this.#applyGwpPersonalizationRowVisibility(personalization, isPersonalizable, selection.optedOut);
  }

  /**
   * Clears personalization validation UI when the shopper has opted out.
   * @param {HTMLElement} item
   */
  #clearGwpOfferPersonalizationValidation(item) {
    const personalization = item.querySelector('personalization-component[data-gwp-personalization]');
    if (
      personalization &&
      typeof personalization.validatePersonalizationInput === 'function'
    ) {
      personalization.validatePersonalizationInput();
      return;
    }

    const textInput = item.querySelector('[data-personalization-text-input]');
    if (textInput instanceof HTMLInputElement) {
      textInput.setCustomValidity('');
      textInput.removeAttribute('aria-invalid');
    }

    const hint = item.querySelector('[data-personalization-preview-hint]');
    if (hint instanceof HTMLElement) {
      hint.textContent = '';
      hint.hidden = true;
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isGwpOfferPersonalizationOptedOut(item) {
    const optOutToggle = item.querySelector('input[data-personalize-opt-out-toggle]');
    return optOutToggle instanceof HTMLInputElement && optOutToggle.checked;
  }

  /**
   * Updates the offer box's hidden personalization metadata to match the selected gift product.
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {number} selectedVariantId
   * @param {string | number} [selectedProductId]
   */
  #syncOfferPersonalizationForSelection(item, config, selectedVariantId, selectedProductId) {
    const personalization = item.querySelector('[data-gwp-personalization]');
    if (!(personalization instanceof HTMLElement)) return;

    if (!this.#isOfferGiftAccepted(item)) return;

    const resolvedProductId =
      selectedProductId != null && String(selectedProductId).trim() !== ''
        ? String(selectedProductId).trim()
        : this.#resolveSelectedProductIdString(item, config);
    const product = this.#resolveOfferProduct(config, resolvedProductId, selectedVariantId);
    const design = this.#resolveGwpPersonalizableDesign(product);
    const isPersonalizable = Boolean(design);
    const textBlock = personalization.querySelector('[data-personalization-text-block]');
    const textInput = personalization.querySelector('[data-personalization-text-input]');
    const optOutToggle = personalization.querySelector('[data-personalize-opt-out-toggle]');
    const designInput = personalization.querySelector('[data-design-input]');
    const threadColorInput = personalization.querySelector('[data-thread-color-input]');
    const previewHint = personalization.querySelector('[data-personalization-preview-hint]');

    item.dataset.gwpSyncingPersonalization = 'true';

    try {
      personalization.hidden = !isPersonalizable;

      if (textBlock instanceof HTMLElement) {
        textBlock.dataset.personalizationHide = `${!isPersonalizable}`;
        textBlock.dataset.personalizationDisabled = `${!isPersonalizable}`;
      }

      if (textInput instanceof HTMLInputElement) {
        textInput.disabled = !isPersonalizable;
        if (!isPersonalizable) {
          textInput.value = '';
          textInput.setCustomValidity('');
          textInput.removeAttribute('aria-invalid');
        }
      }

      if (optOutToggle instanceof HTMLInputElement) {
        optOutToggle.disabled = !isPersonalizable;
        if (!isPersonalizable && optOutToggle.checked) {
          optOutToggle.checked = false;
        }
      }

      if (!isPersonalizable || !design) {
        if (designInput instanceof HTMLInputElement) {
          designInput.checked = false;
          designInput.value = '';
          this.#applyGwpAttributes(designInput, {}, GWP_DESIGN_INPUT_DATA_ATTRIBUTE_NAMES);
        }

        if (threadColorInput instanceof HTMLInputElement) {
          threadColorInput.checked = false;
          threadColorInput.value = '';
          this.#applyGwpAttributes(threadColorInput, {}, GWP_THREAD_COLOR_INPUT_ATTRIBUTE_NAMES);
        }

        if (previewHint instanceof HTMLElement) {
          previewHint.hidden = true;
          previewHint.textContent = '';
        }
        return;
      }

      const designChanged = designInput instanceof HTMLInputElement
        ? this.#applyGwpDesignInputDataAttributes(designInput, design)
        : false;

      const designGrid = personalization.querySelector('[data-personalization-designs]');
      if (designGrid instanceof HTMLElement) {
        const productCode = this.#getGwpProductCode(product);
        if (productCode) {
          designGrid.dataset.productCode = productCode;
        }
      }

      const color = this.#resolveGwpColorForProduct(product);
      if (threadColorInput instanceof HTMLInputElement) {
        if (color) {
          this.#applyGwpThreadColorInputAttributes(threadColorInput, color);
        } else {
          threadColorInput.checked = false;
          threadColorInput.value = '';
          this.#applyGwpAttributes(threadColorInput, {}, GWP_THREAD_COLOR_INPUT_ATTRIBUTE_NAMES);
        }
      }

      const personalizationComponent = /** @type {HTMLElement & {
        _syncProgrammaticDesignChange?: () => void,
        syncPersonalizationVisibility?: () => void,
        _queuePreviewRefresh?: (delay?: number) => void
      }} */ (personalization);

      if (designChanged && personalizationComponent._syncProgrammaticDesignChange) {
        personalizationComponent._syncProgrammaticDesignChange();
        return;
      }

      personalizationComponent.syncPersonalizationVisibility?.();
      personalizationComponent._queuePreviewRefresh?.();
    } finally {
      delete item.dataset.gwpSyncingPersonalization;
      const optedOut = optOutToggle instanceof HTMLInputElement && optOutToggle.checked;
      this.#applyGwpPersonalizationRowVisibility(personalization, isPersonalizable, optedOut);
      if (optedOut) {
        this.#clearGwpOfferPersonalizationValidation(item);
      }
    }
  }

  /**
   * @param {HTMLElement} personalization
   * @param {boolean} isPersonalizable
   * @param {boolean} [optedOutOverride]
   */
  #applyGwpPersonalizationRowVisibility(personalization, isPersonalizable, optedOutOverride) {
    const textRow = personalization.querySelector('.gwp-personalization__text-row');
    const optOutToggle = personalization.querySelector('[data-personalize-opt-out-toggle]');

    if (!(textRow instanceof HTMLElement)) return;

    if (!isPersonalizable) {
      textRow.dataset.personalizationTextHide = 'true';
      return;
    }

    if (typeof optedOutOverride === 'boolean') {
      textRow.dataset.personalizationTextHide = optedOutOverride ? 'true' : 'false';
      return;
    }

    if (optOutToggle instanceof HTMLInputElement) {
      textRow.dataset.personalizationTextHide = `${optOutToggle.checked}`;
    }
  }

  /**
   * @param {Event} event
   */
  #togglePersonalization = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-personalize-opt-out-toggle]')) {
      return;
    }

    const item = target.closest('[data-gwp-offer-item]');
    if (!(item instanceof HTMLElement) || item.dataset.gwpSyncingPersonalization === 'true') {
      return;
    }

    const personalization = item.querySelector('[data-gwp-personalization]');
    if (!(personalization instanceof HTMLElement) || personalization.hidden) return;

    const textBlock = personalization.querySelector('[data-personalization-text-block]');
    if (textBlock instanceof HTMLElement && textBlock.dataset.personalizationHide === 'true') {
      return;
    }

    const textRow = item.querySelector('.gwp-personalization__text-row');
    if (textRow instanceof HTMLElement) {
      textRow.dataset.personalizationTextHide = `${target.checked}`;
    }

    this.#clearGwpOfferPersonalizationValidation(item);
  };

  /**
   * Returns cart line keys for rows currently tagged with a GWP slot in the DOM.
   * @param {string} configId
   * @returns {string[]}
   */
  #findDomGwpLineKeys(configId) {
    /** @type {string[]} */
    const keys = [];

    document
      .querySelectorAll(`.cart-items__table-row[data-gwp-slot="${CSS.escape(configId)}"]`)
      .forEach((row) => {
        if (row instanceof HTMLElement && typeof row.dataset.key === 'string' && row.dataset.key.length > 0) {
          keys.push(row.dataset.key);
        }
      });

    return keys;
  }

  /**
   * Tags each cart line that belongs to a managed gift slot with `data-gwp-slot`
   * (set to the owning config id) and clears the attribute from rows that are no
   * longer managed. Cart rows are tagged server-side in cart-products.liquid when
   * possible; this pass reconciles sessionStorage tracked keys, subtotal
   * qualification, and other cases Liquid cannot resolve.
   * @param {Object} cart
   */
  #applyManagedLineSlots(cart) {
    if (!cart?.items) return;

    const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (rows.length === 0) return;

    /** @type {Map<string, string>} */
    const keyToSlot = new Map();
    for (const config of this.#config) {
      if (config.trigger === 'subtotal' && !this.#isGiftQualified(cart, config)) {
        continue;
      }

      for (const line of this.#findManagedLines(cart, config)) {
        keyToSlot.set(line.key, config.id);
      }
    }

    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;

      const key = row.dataset.key;
      const slot = key ? keyToSlot.get(key) : undefined;

      if (slot) {
        if (row.dataset.gwpSlot !== slot) row.dataset.gwpSlot = slot;
      } else if (row.dataset.gwpSlot != null) {
        delete row.dataset.gwpSlot;
      }
    });
  }

  /**
   * Returns the full cart line item objects managed by this config.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {Array<Object>}
   */
  #getManagedLineItems(cart, config) {
    const keys = new Set(this.#findManagedLines(cart, config).map((line) => line.key));
    if (keys.size === 0) return [];

    return (cart.items || []).filter((item) => keys.has(item.key));
  }

  /**
   * Derives the current offer selection from the cart so the UI can persist the
   * chosen product, personalization text, and opt-out state across re-renders.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {{ choice: GwpOfferChoice, variantId: number, text: string, optedOut: boolean } | null}
   */
  #resolveOfferSelection(cart, config) {
    const lines = this.#getManagedLineItems(cart, config);
    if (lines.length === 0) return null;

    const line = lines[0];

    if (this.#isFreeRushLineItem(line, config)) {
      return {
        choice: 'rush',
        variantId: Number(line.variant_id),
        text: '',
        optedOut: false,
      };
    }

    const product = this.#getConfigProduct(config, Number(line.variant_id));
    const design = this.#resolveGwpPersonalizableDesign(product);
    const letterType = design ? this.#getGwpDesignLetterTypeRaw(design) : '';
    const properties = line.properties || {};
    let text = this.#resolveGwpPersonalizationText(properties, letterType);

    if (!text) {
      const pid = this.#getLineItemProperty(properties, '_pid');
      if (pid && cart?.attributes) {
        const rawPayload = getPersonalizationEntryRawFromAttributesWithLegacy(cart.attributes, pid);
        if (rawPayload) {
          text = resolvePersonalizationTextFromEntryRaw(rawPayload, letterType);
        }
      }
    }

    if (!text) {
      const personalizationRecord = this.#getGwpPersonalizationRecordFromCartLine(cart, line, letterType);
      text = this.#resolveGwpPersonalizationText(personalizationRecord, letterType);
    }

    return {
      choice: 'gift',
      variantId: Number(line.variant_id),
      text,
      optedOut: this.#resolveGwpOfferOptedOut(cart, line, product),
    };
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {boolean}
   */
  #shouldAutoAddGift(config, cart) {
    if (this.#hasNativeGwpConflict(cart, config)) return false;
    if (this.#isInteractiveCouponOffer(config)) return false;

    if (config.trigger !== 'coupon' && this.#isDismissed(config.id)) return false;
    if (this.#offerConflictMessages.has(config.id)) return false;

    if (config.trigger === 'subtotal' && this.#getAutoAddVariantIdsForConfig(config).length === 0) {
      return false;
    }

    return true;
  }

  /**
   * @param {Object} cart
   * @returns {GwpConfig | null}
   */
  #getPendingSubtotalGwpOffer(cart) {
    return (
      this.#config.find((config) => {
        if (config.trigger !== 'subtotal') return false;
        if (!this.#isGiftQualified(cart, config)) return false;
        if (this.#isDismissed(config.id)) return false;
        if (this.#hasPendingVariantSelectionGift(cart, config)) return true;
        if (this.#findManagedLine(cart, config)) return false;
        if (config.offerBoxOnly) return true;

        return this.#shouldShowOfferBox(config);
      }) ?? null
    );
  }

  /**
   * @param {Object} cart
   * @returns {boolean}
   */
  #hasDeclinedQualifiedSubtotalGwp(cart) {
    return this.#config.some(
      (config) =>
        config.trigger === 'subtotal' &&
        this.#isGiftQualified(cart, config) &&
        this.#isDismissed(config.id)
    );
  }

  /**
   * @param {Object} cart
   * @returns {boolean}
   */
  #hasActiveSubtotalGwp(cart) {
    if (this.#getPendingSubtotalGwpOffer(cart)) return true;

    return this.#config.some(
      (config) => config.trigger === 'subtotal' && Boolean(this.#findManagedLine(cart, config))
    );
  }

  /**
   * @param {Object} cart
   * @returns {boolean}
   */
  #hasActiveCouponGwp(cart) {
    return this.#config.some(
      (config) => config.trigger === 'coupon' && Boolean(this.#findManagedLine(cart, config))
    );
  }

  /**
   * Whether a GWP offer box should appear alongside other configured gifts.
   * Offer-box subtotal promos stay visible at threshold (and when showing a
   * conflict) even if a coupon gift is already in the cart.
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {boolean}
   */
  #shouldSurfaceGwpOffer(config, cart) {
    if (!this.#shouldShowOfferForConfig(config, cart)) return false;

    if (config.trigger === 'subtotal') {
      if (this.#isDismissed(config.id)) return true;
      if (this.#offerConflictMessages.has(config.id)) return true;
      if (config.offerBoxOnly && this.#isGiftQualified(cart, config)) return true;
      if (this.#hasActiveCouponGwp(cart)) return false;

      return true;
    }

    return true;
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {boolean}
   */
  #shouldShowOfferForConfig(config, cart) {
    const qualified = this.#isGiftQualified(cart, config);

    if (!qualified) return false;
    if (!this.#shouldShowOfferBox(config)) return false;

    if (this.#isInteractiveOffer(config)) {
      return true;
    }

    if (this.#isDismissed(config.id) || this.#offerConflictMessages.has(config.id)) return true;

    if (this.#findManagedLine(cart, config)) return false;

    return false;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {number}
   */
  #getThresholdGapCents(cart, config) {
    const threshold = config.subtotalThresholdCents ?? 0;
    if (threshold <= 0) return Infinity;

    const subtotal = this.#getMerchandiseSubtotalCents(cart);
    return Math.max(0, threshold - subtotal);
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {boolean}
   */
  #shouldShowProgressForConfig(config, cart) {
    if (config.trigger !== 'subtotal') return false;
    if (this.#isGiftQualified(cart, config)) return false;

    return this.#getThresholdGapCents(cart, config) <= PROGRESS_MAX_GAP_CENTS;
  }

  /**
   * @param {number} cents
   * @param {Object} cart
   * @returns {string}
   */
  #formatMoney(cents, cart) {
    const currency = cart?.currency || 'USD';
    return formatMoney(cents, '{{amount}}', currency);
  }

  /**
   * Resolves the merchant-authored message shown when a recognized coupon GWP
   * has not reached its configured merchandise subtotal.
   * @param {GwpConfig} config
   * @param {Object} cart
   * @param {number} merchandiseSubtotalCents
   * @returns {string}
   */
  #resolveCouponThresholdErrorMessage(config, cart, merchandiseSubtotalCents) {
    const thresholdCents = config.couponThresholdCents ?? 0;
    const remainingCents = Math.max(0, thresholdCents - merchandiseSubtotalCents);
    const template =
      config.couponThresholdErrorMessage?.trim() ||
      'Add [amount] more in qualifying merchandise to use this offer. Minimum spend: [threshold].';
    const amount = `${this.currencySymbol}${this.#formatMoney(remainingCents, cart)}`;
    const threshold = `${this.currencySymbol}${this.#formatMoney(thresholdCents, cart)}`;

    return template
      .split('[amount]').join(amount)
      .split('[threshold]').join(threshold);
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {string}
   */
  #resolveProgressMessage(config, cart) {
    const { progressHeading } = config || null;
    const gapCents = this.#getThresholdGapCents(cart, config);
    const amount = this.#formatMoney(gapCents, cart);
    const template =
      progressHeading
      || Theme.translations?.gift_with_purchase_progress
      || 'Add just [amount] more to qualify for a free gift!';

    return template.replace('[amount]', `${this.currencySymbol}${amount}`);
  }

  /**
   * @param {GwpConfig} config
   * @param {Object} cart
   * @returns {string}
   */
  #renderProgressItem(config, cart) {
    const product = this.#getPrimaryProduct(config) || {};
    const message = this.#escapeHtml(this.#resolveProgressMessage(config, cart));
    const threshold = config.subtotalThresholdCents ?? 0;
    const formattedThreshold = this.#formatMoney(threshold, cart);
    const cartSubtotal = this.#getMerchandiseSubtotalCents(cart);
    const progressPercent = (cartSubtotal / threshold) * 100;
    const imageUrl = product.imageUrl || '';
    const media = imageUrl
      ? `<div class="gift-with-purchase-offers-progress__media">
          <img class="gift-with-purchase-offers-progress__image" src="${this.#escapeHtml(imageUrl)}" alt="" width="56" height="56" loading="lazy">
        </div>`
      : '<span class="gift-with-purchase-offers-progress__media" aria-hidden="true"></span>';

    return `
      <li class="gift-with-purchase-offers-progress__item">
        <p class="gift-with-purchase-offers-progress__message">${message}</p>
        <div class="gift-with-purchase-offers-progress__bar">
          ${media}
          <div class="gift-with-purchase-offers-progress__linear-bar">
            <div
              class="gift-with-purchase-offers-progress__linear-progress"
              style="width: ${progressPercent}%"
            ></div>
          </div>
          <p class="gift-with-purchase-offers-progress__threshold">${this.currencySymbol}${formattedThreshold}</p>
        </div>
      </li>
    `;
  }

  /**
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #shouldShowOfferBox(config) {
    if (config.trigger === 'coupon') return config.offerBoxOnly === true;
    if (config.offerBoxOnly) return true;

    return config.showOfferBox !== false;
  }

  /**
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #shouldShowOfferHeading(config) {
    return this.#shouldShowOfferBox(config);
  }

  /**
   * @param {GwpConfig} config
   * @returns {string}
   */
  #resolveOfferHeading(config) {
    if (config.offerHeading) {
      return config.offerHeading;
    }

    return (
      Theme.translations?.gift_with_purchase_offer_heading || 'You still qualify for a free gift'
    );
  }

  /**
   * @param {GwpProduct} [product]
   * @returns {string}
   */
  #resolveGwpVariantPlaceholder(product) {
    const optionName = (product?.optionName || '').trim();
    const genericFallback =
      Theme.translations?.gift_with_purchase_select_option || 'Select an option';

    if (!optionName) return genericFallback;

    const namedTemplate =
      Theme.translations?.gift_with_purchase_select_option_named ||
      'Select from [option] available';

    return namedTemplate.replace('[option]', this.#pluralizeGwpOptionName(optionName));
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpProduct} product
   * @param {number} selectedVariantId
   */
  #updateVariantOptionLegend(item, product, selectedVariantId) {
    const fieldset = item.querySelector(
      `[data-gwp-variant-fieldset][data-gwp-product-id="${CSS.escape(String(product.productId))}"]`
    );
    if (!(fieldset instanceof HTMLElement)) return;

    const selectedText = fieldset.querySelector('[data-gwp-variant-selected]');
    if (!(selectedText instanceof HTMLElement)) return;

    const variantOptions = this.#getProductVariantOptions(product);
    const selectedVariant = variantOptions.find((variant) => variant.variantId === selectedVariantId);
    const variantRadio = fieldset.querySelector('input[data-gwp-variant-input]:checked');

    if (selectedVariant && variantRadio instanceof HTMLInputElement) {
      selectedText.textContent = selectedVariant.optionValue || String(selectedVariant.variantId);
      return;
    }

    if (!variantRadio) {
      selectedText.textContent = this.#resolveGwpVariantPlaceholder(product);
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpProduct} product
   * @param {{ variantId?: number } | null} selection
   * @param {boolean} [disabled]
   */
  #applyOfferVariantFieldsetState(item, product, selection, disabled = false) {
    const fieldset = item.querySelector(
      `[data-gwp-variant-fieldset][data-gwp-product-id="${CSS.escape(String(product.productId))}"]`
    );
    if (!(fieldset instanceof HTMLElement)) return;

    const selectedVariantId = selection?.variantId ?? null;

    fieldset.querySelectorAll('input[data-gwp-variant-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const shouldCheck =
        Number.isFinite(selectedVariantId) &&
        selectedVariantId > 0 &&
        Number(input.value) === selectedVariantId &&
        input !== document.activeElement;

      if (shouldCheck) {
        input.checked = true;
        input.dataset.currentChecked = 'true';
      } else if (input !== document.activeElement) {
        input.checked = false;
        input.dataset.currentChecked = 'false';
      }

      input.disabled = disabled;
    });

    if (Number.isFinite(selectedVariantId) && selectedVariantId > 0) {
      this.#updateVariantOptionLegend(item, product, selectedVariantId);
    } else {
      this.#updateVariantOptionLegend(item, product, 0);
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {GwpConfig} config
   * @param {string | number} productId
   */
  #syncOfferVariantOptionsVisibility(item, config, productId) {
    const container = item.querySelector('[data-gwp-variant-options]');
    const selectedProductId = String(productId);
    const showVariantOptions = this.#isOfferGiftAccepted(item);

    item.querySelectorAll('[data-gwp-variant-fieldset]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLElement)) return;

      const fieldsetProductId = String(fieldset.dataset.gwpProductId || '');
      fieldset.hidden = !showVariantOptions || fieldsetProductId !== selectedProductId;
    });

    if (!(container instanceof HTMLElement)) return;

    if (!showVariantOptions) {
      container.hidden = true;
      item.querySelectorAll('[data-gwp-variant-options-item]').forEach((wrapper) => {
        if (wrapper instanceof HTMLElement) wrapper.hidden = true;
      });
      return;
    }

    let hasVisibleFieldset = false;

    container.querySelectorAll('[data-gwp-variant-options-item]').forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;

      const wrapperProductId = String(wrapper.dataset.gwpProductId || '');
      const matchesProduct = wrapperProductId === selectedProductId;
      const product = this.#getConfigProductById(config, wrapperProductId);
      const showFieldset = matchesProduct && Boolean(product?.variantFieldsetHtml);
      wrapper.hidden = !showFieldset;

      if (showFieldset && product) {
        hasVisibleFieldset = true;
        this.#applyOfferVariantFieldsetState(
          item,
          product,
          { variantId: this.#getCheckedOfferVariantId(item, config, product.productId) },
          false
        );
      }
    });

    container.hidden = !hasVisibleFieldset;
  }

  /**
   * @param {GwpConfig} config
   * @param {GwpProduct[]} products
   * @param {{ variantId: number } | null} selection
   * @returns {string}
   */
  #renderOfferVariantOptions(config, products, selection, hideGiftDetails = false) {
    const fieldsets = products
      .filter((product) => product.variantFieldsetHtml && this.#productHasVariantOptions(product))
      .map((product) => {
        const productId = product.productId;
        const isActiveProduct =
          !hideGiftDetails &&
          (selection
            ? this.#getProductVariantOptions(product).some(
                (variant) => variant.variantId === selection.variantId
              )
            : product === products[0]);

        return `
          <div
            class="gift-with-purchase-offers__variant-options-item"
            data-gwp-variant-options-item
            data-gwp-product-id="${productId}"
            ${isActiveProduct ? '' : 'hidden'}
          >
            ${product.variantFieldsetHtml}
          </div>
        `;
      });

    if (fieldsets.length === 0) return '';

    const initiallyVisible = !hideGiftDetails && fieldsets.some((fieldset) => !fieldset.includes('hidden'));

    return `
      <div
        class="gift-with-purchase-offers__variant-options"
        data-gwp-variant-options
        ${initiallyVisible ? '' : 'hidden'}
      >
        ${fieldsets.join('')}
      </div>
    `;
  }

  /**
   * @param {GwpConfig} config
   * @param {string} offerHeading
   * @param {Object} cart
   * @returns {string}
   */
  #renderOfferItem(config, offerHeading, cart) {
    const products = this.#getConfigProducts(config);
    const scopeId = this.#escapeHtml(config.id);
    const selection = this.#resolveOfferSelection(cart, config);
    const showRejected = this.#isDismissed(config.id);
    const conflictMessage = this.#offerConflictMessages.get(config.id) || '';
    const isRushSelected = selection?.choice === 'rush';
    const showGiftDetails = !showRejected && !isRushSelected;
    const hasCartGiftChoice = selection?.choice === 'gift' || selection?.choice === 'rush';
    const hasConfirmedAccept = hasCartGiftChoice || this.#confirmedOfferAcceptIds.has(config.id);
    const productControlsDisabled = showRejected ? 'disabled' : '';
    const selectedProductFromCart = selection
      ? this.#getConfigProduct(config, selection.variantId)
      : null;
    const conflictErrorHtml = conflictMessage
      ? `<p class="gift-with-purchase-offers__conflict-error" data-gwp-conflict-error role="alert">${this.#escapeHtml(conflictMessage)}</p>`
      : `<p class="gift-with-purchase-offers__conflict-error" data-gwp-conflict-error role="alert" hidden></p>`;

    const productsOptions = products.map((product, idx) => {
      const productId = product.productId;
      const isSelected = selectedProductFromCart
        ? productId === selectedProductFromCart.productId
        : idx === 0;
      const inputId = `gwpProduct-${scopeId}-${productId}`;
      const title = this.#escapeHtml(product.productTitle || 'Free gift');
      const imageUrl = product.imageUrl || '';
      const media = imageUrl
        ? `<img class="gift-with-purchase-offers__image" src="${this.#escapeHtml(imageUrl)}" alt="${title}" width="160" height="160" loading="lazy">`
        : '<span class="gift-with-purchase-offers__media" aria-hidden="true"></span>';

      return `
        <div class="gift-with-purchase-offers__list-image-item" role="listitem">
          <input
            class="sr-only"
            id="${inputId}"
            value="${productId}"
            type="radio"
            name="gwpProduct-${scopeId}"
            ${isSelected ? 'checked' : ''}
            ${productControlsDisabled}
          />
          <label for="${inputId}">
            ${media}
          </label>
        </div>
      `;
    });

    const productsOptionsHTML = productsOptions.join('');
    const selectedProduct = selectedProductFromCart || products[0] || null;
    const selectedProductDesign = this.#resolveGwpPersonalizableDesign(selectedProduct);
    const monogramProduct =
      selectedProduct && selectedProductDesign
        ? selectedProduct
        : products.find((entry) => this.#resolveGwpPersonalizableDesign(entry)) || null;
    const monogramFieldHtml = monogramProduct
      ? this.#renderOfferMonogramField(config, monogramProduct, {
          hidden: !selectedProductDesign,
          initialText: selection?.text || '',
          optedOut: selection?.optedOut === true,
        })
      : '';
    const variantOptionsHtml = this.#renderOfferVariantOptions(
      config,
      products,
      selection,
      !showGiftDetails
    );

    const freeRushHtml = this.#canOfferFreeRush(config, cart)
      ? `
          <div class="gift-with-purchase-offers__list-item">
            <input
              type="radio"
              id="${ACCEPT_GWP_RUSH_VALUE}-${scopeId}"
              name="addGWPProduct-${scopeId}"
              value="${ACCEPT_GWP_RUSH_VALUE}"
              ${isRushSelected ? 'checked' : ''}
            />
            <label for="${ACCEPT_GWP_RUSH_VALUE}-${scopeId}">${this.#escapeHtml(this.#freeRushOrder?.label || 'RUSH my order for FREE:')}</label>
          </div>
        `
      : '';
    const giftLabel = config.discountType === 'percentage' ? 'Discounted gift' : 'FREE gift';
    const acceptGiftLabel = `${giftLabel}${products.length === 1 ? ':' : ' of your choice:'}`;

    return `
      <div
        class="gift-with-purchase-offers__item"
        data-gwp-offer-item
        data-gwp-id="${scopeId}"
        data-gwp-show-gift-details="${showGiftDetails ? 'true' : 'false'}"
        ${hasConfirmedAccept ? 'data-gwp-offer-accepted="true"' : ''}
      >
        ${offerHeading}
        <fieldset class="gift-with-purchase-offers__add-gwp">
          <legend class="visually-hidden">Select an option:</legend>
          <div class="gift-with-purchase-offers__list-item">
            <input
              type="radio"
              id="rejectGWPProduct-${scopeId}"
              name="addGWPProduct-${scopeId}"
              value="rejectGWPProduct"
              ${showRejected ? 'checked' : ''}
            />
            <label for="rejectGWPProduct-${scopeId}">I don't want a ${config.discountType === 'percentage' ? 'discounted' : 'free'} gift.</label>
          </div>
          <div class="gift-with-purchase-offers__list-item">
            <input
              type="radio"
              id="acceptGWPGProduct-${scopeId}"
              name="addGWPProduct-${scopeId}"
              value="acceptGWPGProduct"
              ${showGiftDetails ? 'checked' : ''}
            />
            <label for="acceptGWPGProduct-${scopeId}">${this.#escapeHtml(acceptGiftLabel)}</label>
          </div>
          ${freeRushHtml}
        </fieldset>
        ${conflictErrorHtml}
        <gift-with-purchase-options class="gift-with-purchase-offers__chooser">
          <div
            class="gift-with-purchase-offers__add-gwp-options"
            data-gwp-options-list
            role="list"
            aria-label="Select a gift:"
            ${showGiftDetails ? '' : 'hidden'}
          >
            ${productsOptionsHTML || ''}
          </div>
          <div class="gift-with-purchase-offers__navigation" data-gwp-options-controls hidden>
            <button type="button" class="button button-unstyled gift-with-purchase-offers__arrow" data-gwp-options-previous aria-label="Previous gifts" disabled>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="m10 3-5 5 5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button type="button" class="button button-unstyled gift-with-purchase-offers__arrow" data-gwp-options-next aria-label="Next gifts">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="m6 3 5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </gift-with-purchase-options>
        ${variantOptionsHtml}
        ${monogramFieldHtml}
      </div>
    `;
  }

  /**
   * @param {unknown} value
   * @returns {unknown | null}
   */
  #parseGwpJsonValue(value) {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  /**
   * Resolves a single design object from product MTO design data.
   * Accepts a design object, an array of designs, or an MTO-style `{ designs: [] }` payload.
   * @param {unknown} monogram
   * @returns {Record<string, unknown> | null}
   */
  #resolveGwpDesign(monogram) {
    if (!monogram || typeof monogram !== 'object') return null;

    if (Array.isArray(monogram)) {
      return this.#findGwpOption(monogram);
    }

    const payload = /** @type {Record<string, unknown>} */ (monogram);

    if (Array.isArray(payload.designs)) {
      return this.#findGwpOption(payload.designs);
    }

    if (this.#hasGwpOptionValue(payload, 'letter_type') || this.#hasGwpOptionValue(payload, 'id')) {
      return payload;
    }

    return null;
  }

  /**
   * @param {string} regex
   * @returns {{ min: number | null, max: number | null }}
   */
  #getValidationMinMaxFromRegex(regex) {
    if (regex.includes('{1}') || regex.includes('{1,1}')) return { min: 1, max: 1 };
    if (regex.includes('{2}') || regex.includes('{2,2}')) return { min: 2, max: 2 };
    if (regex.includes('{3}') || regex.includes('{3,3}')) return { min: 3, max: 3 };
    if (regex.includes('{1,3}')) return { min: 1, max: 3 };
    if (regex.includes('{2,3}')) return { min: 2, max: 3 };
    if (regex.includes('{1,11}')) return { min: 1, max: 11 };

    return { min: null, max: null };
  }

  /**
   * @param {string} regex
   * @returns {string}
   */
  #getValidationSpecialCharacters(regex) {
    if (regex.includes('#.:;')) return "#.:;'_,!? -";
    if (regex.includes('#.:,')) return '#.:,!? -';
    if (regex.includes('.:,!? ')) return '.:,!? ';
    if (regex.includes('.,? ')) return '.,? -';
    if (regex.includes('.:,! ')) return '.:,! -';
    if (regex.includes('.:!? ')) return '.:!? -';
    if (regex.includes('.:,#!? ')) return '.:,#!? -';

    return '';
  }

  /**
   * Builds data attributes for a hidden design input, mirroring personalization-options.liquid.
   * @param {Record<string, unknown>} design
   * @returns {Record<string, string>}
   */
  #getGwpDesignInputDataAttributes(design) {
    const regex = this.#toGwpString(design.char_validation_regex);
    const { min, max } = this.#getValidationMinMaxFromRegex(regex);
    /** @type {Record<string, string>} */
    const attrs = {
      'data-design-id': this.#toGwpString(design.id),
      'data-design-name': this.#toGwpString(design.name),
      'data-design-font': this.#toGwpString(design.font),
      'data-design-letter-type': this.#getGwpDesignLetterType(design),
      'data-design-letter-type-raw': this.#toGwpString(design.letter_type),
      'data-design-thumbnail': this.#toGwpString(design.svg_url || design.thumbnail),
      'data-validation-regex': regex,
      'data-validation-allow-numbers': regex.includes('0-9') ? 'true' : 'false',
      'data-validation-allow-uppercase': !regex || regex.includes('A-Z') ? 'true' : 'false',
      'data-validation-allow-lowercase':
        !regex || regex.includes('a-z') || regex.includes('A-Za-z') ? 'true' : 'false',
    };

    if (min !== null) attrs['data-validation-min'] = String(min);
    if (max !== null) attrs['data-validation-max'] = String(max);

    const specialCharacters = this.#getValidationSpecialCharacters(regex);
    if (specialCharacters) {
      attrs['data-validation-special-characters'] = specialCharacters;
    }

    return attrs;
  }

  /**
   * @param {Record<string, string>} attrs
   * @returns {string}
   */
  #buildGwpAttributeString(attrs) {
    return Object.entries(attrs)
      .map(([name, value]) => `${name}="${this.#escapeHtml(value)}"`)
      .join(' ');
  }

  /**
   * @param {HTMLElement} element
   * @param {Record<string, string>} attrs
   * @param {string[]} attributeNames
   * @returns {boolean}
   */
  #applyGwpAttributes(element, attrs, attributeNames) {
    let changed = false;

    for (const name of attributeNames) {
      if (Object.prototype.hasOwnProperty.call(attrs, name)) {
        const value = attrs[name];
        if (element.getAttribute(name) !== value) {
          element.setAttribute(name, value);
          changed = true;
        }
      } else if (element.hasAttribute(name)) {
        element.removeAttribute(name);
        changed = true;
      }
    }

    return changed;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {Record<string, unknown>} design
   * @returns {boolean}
   */
  #applyGwpDesignInputDataAttributes(input, design) {
    const value = this.#toGwpString(design.id);
    const changed = input.value !== value || !input.checked;

    input.value = value;
    input.checked = true;

    return this.#applyGwpAttributes(
      input,
      this.#getGwpDesignInputDataAttributes(design),
      GWP_DESIGN_INPUT_DATA_ATTRIBUTE_NAMES
    ) || changed;
  }

  /**
   * @param {Record<string, unknown>} color
   * @returns {Record<string, string>}
   */
  #getGwpThreadColorInputAttributes(color) {
    const colorName = this.#toGwpString(color.name).trim();
    const colorId = this.#toGwpString(color.id).trim();

    return {
      value: colorName,
      'data-thread-color-key': colorId,
      'data-thread-color-label': colorName,
      'data-thread-color-id': colorId,
      'data-thread-color-hex': this.#toGwpString(color.hex).trim(),
    };
  }

  /**
   * @param {HTMLInputElement} input
   * @param {Record<string, unknown>} color
   */
  #applyGwpThreadColorInputAttributes(input, color) {
    input.value = this.#toGwpString(color.name).trim();
    input.checked = true;
    this.#applyGwpAttributes(
      input,
      this.#getGwpThreadColorInputAttributes(color),
      GWP_THREAD_COLOR_INPUT_ATTRIBUTE_NAMES
    );
  }

  /**
   * @param {GwpConfig} config
   * @param {GwpProduct} product
   * @param {{ hidden?: boolean, initialText?: string, optedOut?: boolean }} [options]
   * @returns {string}
   */
  #renderOfferMonogramField(config, product, options = {}) {
    const design = this.#resolveGwpPersonalizableDesign(product);
    if (!design) return '';

    const color = this.#resolveGwpColorForProduct(product);
    const scopeId = this.#escapeHtml(config.id);
    const letterType = this.#escapeHtml(this.#getGwpDesignLetterType(design));
    const productCode = this.#escapeHtml(this.#getGwpProductCode(product));
    const designInputAttrs = this.#buildGwpAttributeString(this.#getGwpDesignInputDataAttributes(design));
    const threadColorInputAttrs = color
      ? this.#buildGwpAttributeString(this.#getGwpThreadColorInputAttributes(color))
      : '';
    const isHidden = options.hidden === true;
    const hiddenAttribute = isHidden ? 'hidden' : '';
    const disabledAttribute = isHidden ? 'disabled' : '';
    const initialText = this.#escapeHtml((options.initialText || '').trim());
    const optedOut = options.optedOut === true;
    const optOutCheckedAttribute = optedOut ? 'checked' : '';
    const textHiddenAttribute = isHidden || optedOut ? 'true' : 'false';
    const previewEnterText = this.#escapeHtml(
      Theme.translations?.personalization_preview_enter_text ||
        'Enter your personalization text to see a preview.'
    );
    const previewCompleteFields = this.#escapeHtml(
      Theme.translations?.personalization_cart_preview_complete_fields ||
        'Complete the text to see a preview.'
    );
    const previewMinChars = this.#escapeHtml(
      Theme.translations?.personalization_preview_min_chars ||
        'Enter at least [count] characters for this style to see a preview.'
    );
    const previewMaxChars = this.#escapeHtml(
      Theme.translations?.personalization_preview_max_chars ||
        'Use at most [count] characters for this style.'
    );
    const previewPlaceholderText = this.#escapeHtml(
      Theme.translations?.personalization_preview_placeholder_text ||
        'Enter your initials'
    );
    const previewPersonalizationCheckbox = Theme.translations?.preview_personalization_checkbox
      || "I don't want to personalize";

    return `
      <personalization-component
        class="personalization gift-with-purchase-offers__personalization"
        data-gwp-personalization
        data-i18n-preview-enter-text="${previewEnterText}"
        data-i18n-preview-complete-fields="${previewCompleteFields}"
        data-i18n-preview-min-chars="${previewMinChars}"
        data-i18n-preview-max-chars="${previewMaxChars}"
        data-i18n-preview-placeholder-text="${previewPlaceholderText}"
        ${hiddenAttribute}
      >
        <div
          class="visually-hidden"
          data-personalization-designs
          data-product-code="${productCode}"
        ></div>
        <input
          type="radio"
          class="sr-only"
          id="personalization-design-${scopeId}"
          name="personalization-design-${scopeId}"
          value="${this.#escapeHtml(this.#toGwpString(design.id))}"
          data-design-input
          checked
          ${designInputAttrs}
        >
        <input
          type="radio"
          class="sr-only"
          id="personalization-thread-color-${scopeId}"
          name="personalization-thread-color-${scopeId}"
          data-thread-color-input
          ${color ? 'checked' : ''}
          ${threadColorInputAttrs}
        >
        <div
          id="personalization-step-text-${scopeId}"
          class="gwp-personalization__text-block personalization__text-block"
          data-personalization-text-block
          data-personalization-step="text"
          data-personalization-step-index="2"
          data-personalization-disabled="${isHidden ? 'true' : 'false'}"
          data-personalization-hide="${isHidden ? 'true' : 'false'}"
        >
          <div
            class="gwp-personalization__text-row personalization__text-row"
            data-personalization-text-hide="${textHiddenAttribute}"
          >
            <div class="personalization__text-input-wrap">
              <label
                class="visually-hidden"
                for="personalization-text-${scopeId}"
                data-personalization-letter-type-label
              >
                ${letterType}
              </label>

              <input
                id="personalization-text-${scopeId}"
                class="personalization__text-input js-personalization-text-input"
                type="text"
                value="${initialText}"
                placeholder="${previewPlaceholderText}"
                autocomplete="off"
                maxlength="20"
                data-personalization-text-input
                ${disabledAttribute}
              >
            </div>

            <div
              class="personalization__text-preview js-personalization-text-preview"
              aria-live="polite"
            >
              <p data-personalization-text-preview></p>
            </div>
          </div>

          <p
            class="personalization__preview-hint"
            data-personalization-preview-hint
            aria-live="polite"
            hidden
          ></p>
          <div class="gift-with-purchase-offers__personalization-toggle">
            <label
              class="gwp-personalization__label personalization__label product-form-addon js-cart-personalization-toggle"
              for="enable-cart-personalization-${scopeId}"
            >
              <input
                class="gwp-personalization__checkbox personalization__checkbox"
                id="enable-cart-personalization-${scopeId}"
                type="checkbox"
                value="Yes"
                data-personalize-opt-out-toggle=""
                ${optOutCheckedAttribute}
                ${disabledAttribute}
              />
              ${previewPersonalizationCheckbox}
            </label>
          </div>
        </div>
      </personalization-component>
    `;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #toGwpString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }

    return '';
  }

  /**
   * @param {Record<string, unknown>} option
   * @param {string} key
   * @returns {boolean}
   */
  #hasGwpOptionValue(option, key) {
    return this.#toGwpString(option[key]).trim().length > 0;
  }

  /**
   * @param {unknown} entries
   * @returns {Record<string, unknown> | null}
   */
  #findGwpOption(entries) {
    if (!Array.isArray(entries)) return null;

    for (const entry of entries) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      return /** @type {Record<string, unknown>} */ (entry);
    }

    return null;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return this.#toGwpString(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  /**
   * @returns {PendingCouponOffer | null}
   */
  #getPendingCouponOffer() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(PENDING_COUPON_STORAGE_KEY) || 'null');
      if (!parsed || typeof parsed !== 'object') return null;

      const code = typeof parsed.code === 'string' ? parsed.code.trim() : '';
      const configIds = Array.isArray(parsed.configIds)
        ? parsed.configIds.filter((id) => typeof id === 'string' && id.length > 0)
        : [];
      const validatedRushConfigIds = Array.isArray(parsed.validatedRushConfigIds)
        ? parsed.validatedRushConfigIds.filter(
            (id) => typeof id === 'string' && configIds.includes(id)
          )
        : [];
      const cartToken = typeof parsed.cartToken === 'string' ? parsed.cartToken : '';
      const createdAt = Number(parsed.createdAt);

      if (!code || configIds.length === 0 || !cartToken || !Number.isFinite(createdAt)) return null;

      this.#pendingCouponCodes.add(normalizeDiscountCode(code));
      return { code, configIds, validatedRushConfigIds, cartToken, createdAt };
    } catch {
      return null;
    }
  }

  /**
   * @param {string} discountCode
   * @param {GwpConfig[]} configs
   * @param {Object} cart
   * @param {{ validatedRushConfigIds?: string[] }} [options]
   * @returns {boolean}
   */
  #setPendingCouponOffer(discountCode, configs, cart, options = {}) {
    const normalized = normalizeDiscountCode(discountCode);
    const cartToken = typeof cart?.token === 'string' ? cart.token : '';
    if (!normalized || configs.length === 0 || !cartToken) return false;

    const existing = this.#getPendingCouponOffer();
    if (existing && normalizeDiscountCode(existing.code) !== normalized) {
      this.#pendingCouponCodes.delete(normalizeDiscountCode(existing.code));
      this.#pendingDiscountApplySnapshots.delete(normalizeDiscountCode(existing.code));
    }

    /** @type {PendingCouponOffer} */
    const pending = {
      code: discountCode.trim(),
      configIds: [...new Set(configs.map((config) => config.id))],
      validatedRushConfigIds: [
        ...new Set(
          (options.validatedRushConfigIds || []).filter((configId) =>
            configs.some((config) => config.id === configId)
          )
        ),
      ],
      cartToken,
      createdAt: Date.now(),
    };

    try {
      sessionStorage.setItem(PENDING_COUPON_STORAGE_KEY, JSON.stringify(pending));
      this.#pendingCouponCodes.add(normalized);
      return true;
    } catch (error) {
      console.error('Gift with purchase: failed to save pending discount code', error);
      return false;
    }
  }

  /**
   * Keeps a successfully validated coupon pending while its physical validation
   * gift is replaced by free rush. Shopify cannot keep the coupon applicable
   * once the eligible physical gift is removed, so this state preserves the
   * shopper's code activation and its cart pill for the complimentary rush line.
   * @param {PendingCouponOffer} pending
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #markPendingCouponRushValidated(pending, config) {
    if (!pending.configIds.includes(config.id)) return false;

    const validatedRushConfigIds = [
      ...new Set([...(pending.validatedRushConfigIds || []), config.id]),
    ];

    try {
      sessionStorage.setItem(
        PENDING_COUPON_STORAGE_KEY,
        JSON.stringify({ ...pending, validatedRushConfigIds })
      );
      this.#pendingCouponCodes.add(normalizeDiscountCode(pending.code));
      return true;
    } catch (error) {
      console.error('Gift with purchase: failed to preserve validated free rush code', error);
      return false;
    }
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isValidatedCouponRushOffer(cart, config) {
    const pending = this.#getPendingCouponOffer();
    if (!pending?.validatedRushConfigIds?.includes(config.id)) return false;

    return this.#isPendingCouponOfferQualified(cart, config);
  }

  /**
   * Removes complimentary rush lines owned by a validated pending coupon offer.
   * @param {Object} cart
   * @param {PendingCouponOffer} pending
   * @returns {Promise<Object>}
   */
  async #removeValidatedCouponRushLines(cart, pending) {
    const lineKeys = new Set();

    for (const configId of pending.validatedRushConfigIds || []) {
      const config = this.#config.find((entry) => entry.id === configId);
      if (!config) continue;

      for (const item of cart.items || []) {
        if (this.#isFreeRushLineItem(item, config) && item.key) {
          lineKeys.add(item.key);
        }
      }
    }

    if (lineKeys.size === 0) return cart;

    const cartResponse = await this.#removeCartLines([...lineKeys], cart);
    return this.#refreshCartAfterLineRemovals(cartResponse);
  }

  /**
   * @param {string} [discountCode]
   * @returns {boolean}
   */
  #clearPendingCouponOffer(discountCode = '') {
    const pending = this.#getPendingCouponOffer();
    if (!pending) {
      document.querySelectorAll('[data-gwp-pending-discount]').forEach((pill) => pill.remove());
      return false;
    }

    const normalized = normalizeDiscountCode(pending.code);
    if (discountCode && normalizeDiscountCode(discountCode) !== normalized) return false;

    try {
      sessionStorage.removeItem(PENDING_COUPON_STORAGE_KEY);
    } catch (error) {
      console.error('Gift with purchase: failed to clear pending discount code', error);
    }
    this.#pendingCouponCodes.delete(normalized);
    this.#pendingDiscountApplySnapshots.delete(normalized);
    pending.configIds.forEach((configId) => this.#confirmedOfferAcceptIds.delete(configId));
    document.querySelectorAll('[data-gwp-pending-discount]').forEach((pill) => pill.remove());
    return true;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isPendingCouponOfferQualified(cart, config) {
    if (!this.#isInteractiveCouponOffer(config)) return false;

    const pending = this.#getPendingCouponOffer();
    if (!pending || !pending.configIds.includes(config.id)) return false;
    if (normalizeDiscountCode(pending.code) !== normalizeDiscountCode(config.couponCode || '')) {
      return false;
    }

    const cartToken = typeof cart?.token === 'string' ? cart.token : '';
    if (!cartToken || pending.cartToken !== cartToken) return false;

    const threshold = config.couponThresholdCents ?? 0;
    return this.#getCouponOfferMerchandiseSubtotalCents(cart) >= threshold;
  }

  /**
   * Clears pending state that no longer belongs to this cart or no longer meets
   * the merchant-authored threshold. Shopify applicability remains authoritative.
   * @param {Object} cart
   */
  #reconcilePendingCouponOffer(cart) {
    const pending = this.#getPendingCouponOffer();
    if (!pending) return;

    if (
      isDiscountStrictlyApplicable(cart, pending.code) &&
      (pending.validatedRushConfigIds || []).length === 0
    ) {
      this.#clearPendingCouponOffer(pending.code);
      return;
    }

    const cartToken = typeof cart?.token === 'string' ? cart.token : '';
    const cartChanged = !cartToken || pending.cartToken !== cartToken;
    const hasQualifiedConfig = pending.configIds.some((configId) => {
      const config = this.#config.find((entry) => entry.id === configId);
      return config ? this.#isPendingCouponOfferQualified(cart, config) : false;
    });

    if (cartChanged || !hasQualifiedConfig) {
      this.#clearPendingCouponOffer(pending.code);
    }
  }

  /**
   * Mirrors a deferred discount code in the existing cart discount pill list.
   * The marker keeps it out of the real Shopify discount-code collection.
   * @param {Object} cart
   */
  #renderPendingDiscountPills(cart) {
    this.#reconcilePendingCouponOffer(cart);
    document.querySelectorAll('[data-gwp-pending-discount]').forEach((pill) => pill.remove());

    const pending = this.#getPendingCouponOffer();
    if (!pending) return;

    const normalized = normalizeDiscountCode(pending.code);
    for (const list of document.querySelectorAll('.cart-discount__codes')) {
      if (!(list instanceof HTMLUListElement)) continue;

      const alreadyRendered = Array.from(list.querySelectorAll('.cart-discount__pill')).some(
        (pill) =>
          pill instanceof HTMLLIElement &&
          !pill.hasAttribute('data-gwp-pending-discount') &&
          normalizeDiscountCode(pill.dataset.discountCode || '') === normalized
      );
      if (alreadyRendered) continue;

      const pill = document.createElement('li');
      pill.className = 'cart-discount__pill';
      pill.dataset.discountCode = pending.code;
      pill.dataset.gwpPendingDiscount = 'true';
      const hasValidatedRush = (pending.validatedRushConfigIds || []).length > 0;
      pill.setAttribute(
        'aria-label',
        hasValidatedRush
          ? `${pending.code} applied to free rush offer`
          : `${pending.code} pending gift selection`
      );
      pill.innerHTML = `
        <div class="cart-discount__pill-row">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="20" height="20" fill="currentColor" aria-hidden="true">
            <g><path d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"></path></g>
          </svg>
          <p class="cart-discount__pill-code">${this.#escapeHtml(pending.code)}</p>
        </div>
        <button type="button" class="cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled" aria-label="Remove ${this.#escapeHtml(pending.code)}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M4.28 3.22a.75.75 0 0 0-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 1 0 1.06 1.06L10 11.06l5.72 5.72a.75.75 0 1 0 1.06-1.06L11.06 10l5.72-5.72a.75.75 0 0 0-1.06-1.06L10 8.94 4.28 3.22Z"></path></svg>
        </button>
      `;

      pill.querySelector('button')?.addEventListener('click', () => {
        void this.clearPendingDiscountCode(pending.code);
      });
      list.append(pill);
    }
  }

  /**
   * @param {string} discountCode
   * @returns {Promise<void>}
   */
  clearPendingDiscountCode = async (discountCode) => {
    while (this.#preparingDiscountCode) {
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
    await this.#waitUntilNotSyncing();

    const pending = this.#getPendingCouponOffer();
    if (
      !pending ||
      normalizeDiscountCode(pending.code) !== normalizeDiscountCode(discountCode)
    ) {
      return;
    }

    try {
      let cart = await this.#fetchCart();
      cart = await this.#removeValidatedCouponRushLines(cart, pending);
      this.#clearPendingCouponOffer(discountCode);
      this.#snapshotGwpState(cart);

      document.dispatchEvent(
        new CartUpdateEvent(cart, SYNC_SOURCE, {
          source: SYNC_SOURCE,
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );

      this.#updateGwpUI(cart);
      this.#scheduleOffersUIRefresh(cart);
    } catch (error) {
      console.error('Gift with purchase: failed to clear pending discount code', error);
    }
  };

  /**
   * @returns {Set<string>}
   */
  #getDismissedIds() {
    try {
      const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
      if (!raw) return new Set();

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();

      return new Set(parsed.filter((id) => typeof id === 'string'));
    } catch {
      return new Set();
    }
  }

  /**
   * @param {string} gwpId
   * @returns {boolean}
   */
  #isDismissed(gwpId) {
    return this.#getDismissedIds().has(gwpId);
  }

  /**
   * @param {string} gwpId
   */
  #dismiss(gwpId) {
    this.#pendingRushOfferIds.delete(gwpId);
    this.#pendingOfferEdits.delete(gwpId);
    document
      .querySelectorAll(`[data-gwp-offer-item][data-gwp-id="${CSS.escape(gwpId)}"]`)
      .forEach((item) => {
        if (item instanceof HTMLElement) delete item.dataset.gwpOfferEditing;
      });
    const dismissed = this.#getDismissedIds();
    dismissed.add(gwpId);
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
  }

  /**
   * @param {string} gwpId
   */
  #clearDismissal(gwpId) {
    const dismissed = this.#getDismissedIds();
    if (!dismissed.delete(gwpId)) return;

    if (dismissed.size === 0) {
      sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
  }

  /**
   * @param {string} title
   * @returns {string}
   */
  #normalizeDiscountTitle(title) {
    return (title || '').trim().toLowerCase();
  }

  /**
   * @param {Object} item
   * @param {string} discountTitle
   * @returns {boolean}
   */
  #lineHasDiscountTitle(item, discountTitle) {
    const normalized = this.#normalizeDiscountTitle(discountTitle);
    if (!normalized) return false;

    const allocations = item.line_level_discount_allocations || item.discounts || [];

    return allocations.some((/** @type {{ discount_application?: { title?: string }, title?: string }} */ allocation) => {
      const application = allocation.discount_application || allocation;
      const title = this.#normalizeDiscountTitle(application?.title);
      return (
        title === normalized ||
        (title.length > 0 && normalized.length > 0 && (title.includes(normalized) || normalized.includes(title)))
      );
    });
  }

  /**
   * Coupon-triggered gifts use the discount code as the line allocation title.
   * @param {Object} item
   * @param {string} couponCode
   * @returns {boolean}
   */
  #lineHasCouponCode(item, couponCode) {
    const normalized = couponCode.trim().toLowerCase();
    if (!normalized) return false;

    const allocations = item.line_level_discount_allocations || item.discounts || [];

    return allocations.some(
      (/** @type {{ discount_application?: { type?: string, title?: string }, type?: string, title?: string }} */ allocation) => {
        const application = allocation.discount_application || allocation;
        if (application?.type !== 'discount_code') return false;
        return application.title?.trim().toLowerCase() === normalized;
      }
    );
  }

  /**
   * @param {Object} item
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #lineMatchesGwpIdentity(item, config) {
    const slotId = this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY);
    if (slotId) {
      return slotId === config.id;
    }

    if (config.trigger === 'coupon') {
      return this.#lineHasCouponCode(item, config.couponCode || '');
    }

    return this.#lineHasDiscountTitle(item, config.discountTitle || '');
  }

  /**
   * @param {Object} item
   * @returns {boolean}
   */
  #lineHasAnyConfiguredGiftIdentity(item) {
    return this.#config.some((config) => this.#lineMatchesGwpIdentity(item, config));
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #isGiftQualified(cart, config) {
    if (config.trigger === 'coupon') {
      const threshold = config.couponThresholdCents ?? 0;
      if (
        (config.appDiscountId || this.#isInteractiveCouponOffer(config)) &&
        threshold > 0 &&
        this.#getCouponOfferMerchandiseSubtotalCents(cart) < threshold
      ) {
        return false;
      }

      return (
        this.#isCouponActive(cart, config.couponCode || '') ||
        this.#isPendingCouponOfferQualified(cart, config)
      );
    }

    const threshold = config.subtotalThresholdCents ?? 0;
    if (threshold < 0 || (threshold === 0 && !config.appDiscountId)) return false;

    return this.#getMerchandiseSubtotalCents(cart) >= threshold;
  }

  /**
   * @param {Object} cart
   * @param {string} couponCode
   * @returns {boolean}
   */
  #isCouponActive(cart, couponCode) {
    return isDiscountStrictlyApplicable(cart, couponCode);
  }

  /**
   * @param {Object} cart
   * @returns {number}
   */
  #getMerchandiseSubtotalCents(cart) {
    let subtotal = cart.items_subtotal_price ?? 0;
    const excludedKeys = this.#getSubtotalExcludedLineKeys(cart);
    const tagExcludedKeys = this.#getTagExcludedLineKeys();

    for (const item of cart.items || []) {
      const isGiftLine = excludedKeys.has(item.key);
      const isTagExcluded = tagExcludedKeys.has(item.key);
      if (!isGiftLine && !isTagExcluded) continue;
      subtotal -= item.final_line_price ?? item.line_price ?? 0;
    }

    return Math.max(0, subtotal);
  }

  /**
   * Coupon offer thresholds exclude coupon gift lines without changing the
   * established subtotal-trigger calculation used by automatic gifts.
   * @param {Object} cart
   * @returns {number}
   */
  #getCouponOfferMerchandiseSubtotalCents(cart) {
    let subtotal = this.#getMerchandiseSubtotalCents(cart);
    const subtotalExcludedKeys = this.#getSubtotalExcludedLineKeys(cart);

    for (const config of this.#config) {
      if (config.trigger !== 'coupon') continue;

      for (const item of this.#getConfigGiftLineItems(cart, config)) {
        if (!item.key || subtotalExcludedKeys.has(item.key)) continue;
        subtotal -= item.final_line_price ?? item.line_price ?? 0;
      }
    }

    return Math.max(0, subtotal);
  }

  /**
   * Cart line keys whose product is tagged out of free-gift qualification
   * @returns {Set<string>}
   */
  #getTagExcludedLineKeys() {
    /** @type {Set<string>} */
    const keys = new Set();

    document.querySelectorAll('[data-gwp-excluded-line-keys]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      (element.dataset.gwpExcludedLineKeys || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean)
        .forEach((key) => keys.add(key));
    });

    return keys;
  }

  /**
   * @param {Object} cart
   * @returns {Set<string>}
   */
  #getSubtotalExcludedLineKeys(cart) {
    /** @type {Set<string>} */
    const keys = new Set();

    for (const config of this.#config) {
      if (config.trigger !== 'subtotal') continue;

      for (const line of this.#findSubtotalGiftLines(cart, config, this.#mergeTrackedLineKeys(config.id))) {
        keys.add(line.key);
      }
    }

    return keys;
  }

  /**
   * @param {Object} item
   * @param {GwpConfig} config
   * @returns {boolean}
   */
  #lineLooksLikeSubtotalGift(item, config) {
    const variantIds = this.#getAllConfigVariantIds(config);
    if (!variantIds.includes(item.variant_id)) return false;
    if (this.#getLineItemProperty(item.properties, GWP_SLOT_LINE_PROPERTY) === config.id) {
      return true;
    }
    if (this.#lineMatchesGwpIdentity(item, config)) return true;
    if (this.#isFreeRushLineItem(item, config)) return true;

    return false;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {Set<string>} [trackedLineKeys]
   * @returns {Array<{ variantId: number, key: string }>}
   */
  #findSubtotalGiftLines(cart, config, trackedLineKeys = this.#mergeTrackedLineKeys(config.id)) {
    const variantIds = new Set(this.#getAllConfigVariantIds(config));
    if (variantIds.size === 0) return [];

    /** @type {Map<string, { variantId: number, key: string }>} */
    const linesByKey = new Map();

    for (const item of cart.items || []) {
      if (!variantIds.has(item.variant_id)) continue;
      if (!this.#lineLooksLikeSubtotalGift(item, config)) continue;
      linesByKey.set(item.key, { variantId: item.variant_id, key: item.key });
    }

    for (const key of trackedLineKeys) {
      const item = (cart.items || []).find((entry) => entry.key === key);
      if (!item || !variantIds.has(item.variant_id)) continue;
      if (!this.#lineLooksLikeSubtotalGift(item, config)) continue;
      linesByKey.set(key, { variantId: item.variant_id, key });
    }

    if (this.#canOfferFreeRush(config, cart)) {
      for (const item of cart.items || []) {
        if (!this.#isFreeRushLineItem(item, config)) continue;
        linesByKey.set(item.key, { variantId: item.variant_id, key: item.key });
      }
    }

    return [...linesByKey.values()];
  }

  /**
   * @param {Map<string, Set<string>>} lineKeysMap
   * @returns {Map<string, Set<string>>}
   */
  #cloneTrackedLineKeysMap(lineKeysMap) {
    /** @type {Map<string, Set<string>>} */
    const clone = new Map();

    for (const [configId, keys] of lineKeysMap.entries()) {
      clone.set(configId, new Set(keys));
    }

    for (const [configId, keys] of this.#getStoredGwpLineKeys().entries()) {
      clone.set(configId, new Set([...(clone.get(configId) || []), ...keys]));
    }

    return clone;
  }

  /**
   * @param {string} configId
   * @param {Set<string>} [memoryKeys]
   * @returns {Set<string>}
   */
  #mergeTrackedLineKeys(configId, memoryKeys = this.#lastKnownGwpLineKeys.get(configId)) {
    const merged = new Set(memoryKeys || []);
    const stored = this.#getStoredGwpLineKeys().get(configId);

    if (stored) {
      for (const key of stored) {
        merged.add(key);
      }
    }

    return merged;
  }

  /**
   * @returns {Map<string, Set<string>>}
   */
  #getStoredGwpLineKeys() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(GWP_LINE_KEYS_STORAGE_KEY) || '{}');
      if (!parsed || typeof parsed !== 'object') return new Map();

      /** @type {Map<string, Set<string>>} */
      const map = new Map();

      for (const [configId, keys] of Object.entries(parsed)) {
        if (!Array.isArray(keys)) continue;
        map.set(
          configId,
          new Set(keys.filter((key) => typeof key === 'string' && key.length > 0))
        );
      }

      return map;
    } catch {
      return new Map();
    }
  }

  #persistGwpLineKeys() {
    /** @type {Record<string, string[]>} */
    const payload = {};

    for (const [configId, keys] of this.#lastKnownGwpLineKeys.entries()) {
      if (keys.size > 0) {
        payload[configId] = [...keys];
      }
    }

    if (Object.keys(payload).length === 0) {
      sessionStorage.removeItem(GWP_LINE_KEYS_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(GWP_LINE_KEYS_STORAGE_KEY, JSON.stringify(payload));
  }

  /**
   * @param {Object} cart
   */
  #snapshotGwpState(cart) {
    /** @type {Set<string>} */
    const ids = new Set();
    /** @type {Map<string, Set<string>>} */
    const lineKeys = new Map();

    for (const config of this.#config) {
      const lines =
        config.trigger === 'subtotal'
          ? this.#findSubtotalGiftLines(cart, config, this.#mergeTrackedLineKeys(config.id))
          : this.#findManagedLines(cart, config);

      if (lines.length === 0) {
        lineKeys.delete(config.id);
        continue;
      }

      ids.add(config.id);
      lineKeys.set(config.id, new Set(lines.map((line) => line.key)));
    }

    this.#lastKnownGwpIds = ids;
    this.#lastKnownGwpLineKeys = lineKeys;
    this.#persistGwpLineKeys();
  }

  /**
   * @param {Object} cart
   * @returns {Set<string>}
   */
  #getGwpIdsInCart(cart) {
    /** @type {Set<string>} */
    const ids = new Set();

    for (const config of this.#config) {
      const lines =
        config.trigger === 'subtotal'
          ? this.#findSubtotalGiftLines(cart, config, this.#mergeTrackedLineKeys(config.id))
          : this.#findManagedLines(cart, config);

      if (lines.length > 0) {
        ids.add(config.id);
      }
    }

    return ids;
  }

  /**
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {{ key: string } | null}
   */
  #findManagedLine(cart, config) {
    const lines = this.#findManagedLines(cart, config);
    return lines.length > 0 ? { key: lines[0].key } : null;
  }

  /**
   * Finds every cart line managed by this config, one per configured gift product variant.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @param {{ forRemoval?: boolean, previouslyTracked?: boolean, trackedLineKeys?: Set<string> }} [options]
   * @returns {Array<{ variantId: number, key: string }>}
   */
  #findManagedLines(cart, config, options = {}) {
    const {
      forRemoval = false,
      trackedLineKeys = this.#mergeTrackedLineKeys(config.id),
    } = options;

    if (config.trigger === 'subtotal') {
      return this.#findSubtotalGiftLines(cart, config, trackedLineKeys);
    }

    const hasIdentity = (config.couponCode || '').trim().length > 0;
    if (!hasIdentity) return [];

    const variantIds = new Set(this.#getAllConfigVariantIds(config));
    if (variantIds.size === 0) return [];

    /** @type {Array<{ variantId: number, key: string }>} */
    const lines = [];

    for (const item of cart.items || []) {
      if (this.#isFreeRushLineItem(item, config)) {
        lines.push({ variantId: item.variant_id, key: item.key });
        continue;
      }
      if (!variantIds.has(item.variant_id)) continue;
      if (this.#lineMatchesGwpIdentity(item, config)) {
        lines.push({ variantId: item.variant_id, key: item.key });
      }
    }

    if (!forRemoval && !this.#isGiftQualified(cart, config)) {
      return lines;
    }

    for (const key of trackedLineKeys) {
      const item = (cart.items || []).find((entry) => entry.key === key);
      if (!item) continue;
      if (this.#isFreeRushLineItem(item, config)) {
        if (!lines.some((line) => line.key === key)) {
          lines.push({ variantId: item.variant_id, key });
        }
        continue;
      }
      if (!variantIds.has(item.variant_id)) continue;
      if (!this.#lineMatchesGwpIdentity(item, config)) continue;
      if (lines.some((line) => line.key === key)) continue;
      lines.push({ variantId: item.variant_id, key });
    }

    return lines;
  }

  /**
   * Finds gift-wrap add-ons associated with a GWP slot, including nested
   * gift-wrap lines attached to the GWP parent.
   * @param {Object} cart
   * @param {GwpConfig} config
   * @returns {Array<{ variantId: number, key: string }>}
   */
  #findGwpGiftWrapLines(cart, config) {
    return (cart.items || [])
      .filter(
        (item) =>
          this.#getLineItemProperty(item.properties, GIFT_WRAP_ADDON_PROPERTY) === 'true' &&
          this.#getLineItemProperty(item.properties, GWP_GIFT_WRAP_SLOT_PROPERTY) === config.id
      )
      .map((item) => ({ variantId: item.variant_id, key: item.key }));
  }

  /**
   * @param {Object} item
   * @returns {boolean}
   */
  #isManagedLineItem(item) {
    return this.#lineHasAnyConfiguredGiftIdentity(item);
  }

  /**
   * @param {boolean} loading
   */
  #setPromotionsLoading(loading) {
    document.querySelectorAll('[data-gwp-offers-mount]').forEach((mount) => {
      if (!(mount instanceof HTMLElement)) return;

      const inCartDrawer = Boolean(mount.closest('cart-drawer-component'));
      const showMountOverlay = loading && !inCartDrawer;

      const overlay = mount.querySelector('[data-gwp-offers-loading]');
      if (overlay instanceof HTMLElement) {
        overlay.classList.toggle('hidden', !showMountOverlay);
        overlay.setAttribute('aria-hidden', String(!showMountOverlay));
      }

      mount.dataset.gwpOffersLoading = loading ? 'true' : 'false';
    });

    if (!loading) {
      this.#restoreAllOfferControlsEnabledState();
    }

    const drawer = document.querySelector('cart-drawer-component');
    if (!(drawer instanceof HTMLElement)) return;

    if (loading) {
      const dialog = drawer.querySelector('dialog');
      if (!(dialog instanceof HTMLDialogElement && dialog.open)) return;
    }

    drawer.dispatchEvent(new CartDrawerPromotionsLoadingEvent(loading, SYNC_SOURCE));
  }

  /**
   * @param {boolean} loading
   */
  setPromotionsLoading(loading) {
    this.#setPromotionsLoading(loading);
    if (!loading) {
      this.#syncing = false;
    }
  }

  /**
   * @returns {Promise<Object>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * @returns {string[]}
   */
  #getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    });

    return [...new Set(sectionIds)];
  }

  /**
   * @param {string[]} lineKeys
   * @param {Object} currentCart
   * @returns {Promise<Object>}
   */
  async #removeCartLines(lineKeys, currentCart) {
    if (lineKeys.length === 0) return currentCart;

    const sections = this.#getCartSectionIds();
    /** @type {Record<string, number>} */
    const updates = {};

    for (const lineKey of lineKeys) {
      updates[lineKey] = 0;
    }

    const body = JSON.stringify({
      updates,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to remove cart lines: ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed');
    }

    return { ...currentCart, ...data };
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   * @param {Object} currentCart
   * @returns {Promise<Object>}
   */
  async #changeLineQuantity(lineKey, quantity, currentCart) {
    const sections = this.#getCartSectionIds();
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to update cart line: ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart change failed');
    }

    return { ...currentCart, ...data };
  }

  /**
   * Ensures `/cart/add.js` line items are present on the cart object used for sync
   * planning when a follow-up `/cart.js` fetch races the add mutation.
   * @param {Object} cart
   * @param {Array<Object>} [addedItems]
   * @returns {Object}
   */
  #mergeAddedItemsIntoCart(cart, addedItems) {
    if (!Array.isArray(addedItems) || addedItems.length === 0) return cart;

    const items = [...(cart.items || [])];
    const keys = new Set(items.map((item) => item.key));

    for (const addedItem of addedItems) {
      if (!addedItem?.key || keys.has(addedItem.key)) continue;
      items.push(addedItem);
      keys.add(addedItem.key);
    }

    if (items.length === (cart.items || []).length) return cart;

    return {
      ...cart,
      items,
      item_count: items.reduce((total, item) => total + (item.quantity || 0), 0),
    };
  }

  /**
   * @param {Array<{ variantId: number, configId: string }>} linesToAdd
   * @returns {Promise<Object>}
   */
  async #addGiftLines(linesToAdd) {
    const sections = this.#getCartSectionIds();
    const items = linesToAdd.map(({ variantId, configId }) => ({
      id: variantId,
      quantity: 1,
      properties: this.#withGwpSlotProperty({}, configId),
    }));

    const body = JSON.stringify({
      items,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error(`Failed to add gift line: ${response.status}`);
    }

    const data = await response.json();
    if (data.status) {
      throw new Error(data.message || 'Failed to add gift with purchase');
    }

    // /cart/add.js returns only the added line(s) in `items`, unlike /cart/change.js.
    let cart = await this.#fetchCart();
    cart = this.#mergeAddedItemsIntoCart(cart, data.items);

    if (data.sections) {
      cart.sections = data.sections;
    }

    return cart;
  }
}

const controller = new GiftWithPurchaseController();

/** @type {Window & { GiftWithPurchase?: { prepareForCheckout: () => Promise<boolean>, prepareForDiscountCode: (code: string) => Promise<boolean | { deferred: true, code: string }>, afterDiscountCodeApplied: (code: string) => Promise<void>, clearPendingDiscountCode: (code: string) => Promise<void>, consumeDiscountConflictMessage: () => string | null, consumeDiscountConflictCart: () => Object | null, refreshOffersUI: () => Promise<Object | null>, setPromotionsLoading: (loading: boolean) => void } }} */
const gwpWindow = window;

gwpWindow.GiftWithPurchase = {
  prepareForCheckout: () => controller.prepareForCheckout(),
  prepareForDiscountCode: (code) => controller.prepareForDiscountCode(code),
  afterDiscountCodeApplied: (code) => controller.afterDiscountCodeApplied(code),
  clearPendingDiscountCode: (code) => controller.clearPendingDiscountCode(code),
  consumeDiscountConflictMessage: () => controller.consumeDiscountConflictMessage(),
  consumeDiscountConflictCart: () => controller.consumeDiscountConflictCart(),
  refreshOffersUI: () => controller.refreshOffersUI(),
  setPromotionsLoading: (loading) => controller.setPromotionsLoading(loading),
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => controller.init(), { once: true });
} else {
  controller.init();
}
